import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search, ShoppingCart, Trash2, Plus, Minus, Zap, LogOut,
  Wifi, WifiOff, RefreshCw, Package, CheckCircle2, AlertTriangle,
  X, ScanLine, Keyboard, Percent, Clock, Timer, CreditCard, Receipt, Banknote,
  Smartphone, BookMarked, TrendingUp, RotateCcw, Mic,
  User, Minimize2, ShieldCheck, PlusCircle, Users, Eye, EyeOff, Sparkles, MoreHorizontal, MoreVertical, Menu, Home, Monitor,
  BarChart3, Activity, Flame, Target, ChevronRight, ChevronUp, ChevronDown, ChevronLeft,   Megaphone, PackagePlus, Settings, Edit, DoorOpen,
  DollarSign, ShoppingBag, Award, ArrowUpRight, ArrowDownRight, Camera, PauseCircle, Barcode, Tag, FileText, Rocket, Bell, CircleDollarSign, Loader2
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { SoundManager } from '../utils/SoundManager';
import { LunaVoiceModel, LunaFace, type LunaVoiceModelHandle } from './LunaVoiceModel';
import BillingLockGate from './billing/BillingLockGate';
import StoreSettingsFAB from './billing/StoreSettingsFAB';
import { CameraScanner } from './CameraScanner';
import { PricePicker, type PriceOption } from './PricePicker';
import LunaMarketingBrain from './LunaMarketingBrain';
import MarketingCenter from './MarketingCenter';
import BulkInventoryModal from './BulkInventoryModal';
import VoiceProductHologram from './VoiceProductHologram';
import EditProductModal from './EditProductModal';
import { useProductCache } from '../hooks/useProductCache';
import VoiceCheckoutModal from './VoiceCheckoutModal';
import NetworkMonitor from './NetworkMonitor';
import MapPicker from './MapPicker';
import { FastAverageColor } from 'fast-average-color';
import { compressProductImage } from '../utils/imageCompressor';
// 🛡️ Phase 1 — Resilience & Compliance
import { ConfirmModalHost, confirmAction } from './pos/ConfirmModal';
import DayEndReport from './pos/DayEndReport';
import VoidModal from './pos/VoidModal';
import ReturnModal from './pos/ReturnModal';
import { OfflineQueue, OfflineStatus } from '../utils/OfflineQueue';
import { SettingsView } from './pos/SettingsView';
import { CartSidebar } from './pos/CartSidebar';
import { SaveEstimateModal } from './pos/SaveEstimateModal';
import { EstimatesView } from './pos/EstimatesView';
import { HoldManager } from './pos/HoldManager';
// ⚡ Phase 2 — Speed & Power-User UX
import { ScanBurstHost, triggerScanBurst } from './pos/ScanBurst';
import { NumpadHost, openNumpad } from './pos/Numpad';
import { ShortcutsModal } from './pos/ShortcutsModal';
import SplitPaymentModal from './pos/SplitPaymentModal';
// 💳 Phase 10 — Expanded payment methods
import PaymentMethodPicker, { type PaymentConfig, type PaymentMethod } from './pos/PaymentMethodPicker';
import { SwipeableRow } from './pos/SwipeableRow';
import { Haptics } from '../utils/haptics';
import { printReceiptImage, isAgentConfigured, checkAgentStatus, autoPair, setAgentConfig, syncFromCloud, getAgentConfig, getStoreAuthHeaders, type ReceiptData } from '../utils/thermalPrint';
import PrinterSetupModal from './PrinterSetupModal';
import { ReceiptTemplateRenderer } from './pos/ReceiptTemplateRenderer';
// ReceiptBarcode removed — TODO: Secure Backend Print
import { SplitSquareHorizontal } from 'lucide-react';
// 🎯 Phase 3 — CRM & Customer Loyalty
import CustomerPicker, { type CustomerLite } from './pos/CustomerPicker';
import CustomerDirectory from './pos/CustomerDirectory';
import { UserCircle2, UserMinus } from 'lucide-react';
// 🛡️ Phase 4 — RBAC & Multi-Cashier
import CashierAuth, { type CashierSession } from './pos/CashierAuth';
import CashierManager from './pos/CashierManager';
import { SupplierManager } from './pos/SupplierManager';
// 🕐 Phase 7 — Workforce & Shift Management
import ShiftManager from './pos/ShiftManager';
// 👥 Phase 2 — Employee Management
import EmployeeManagement from './pos/EmployeeManagement';
import { Shield, SwitchCamera, Layers, Building2, Calendar, Grid3X3, ClipboardList, Car, Wrench, AlertCircle, Wallet } from 'lucide-react';
// 🛡️ Phase 5 — Declarative RBAC UI gating
import { Can } from './pos/Can';
import MasterDashboard from './MasterDashboard';
import LifetimeAnalytics from './LifetimeAnalytics';
import AdvancedAnalytics from './AdvancedAnalytics';
import InventorySheetScannerModal from './pos/InventorySheetScannerModal';
import { BrainCircuit } from 'lucide-react';

interface Product {
  id: number;
  name: string;
  category: string;
  categoryId?: number | null;
  price: number;
  costPrice?: number;
  sellingPrice?: number;
  stockCount: number;
  unitType?: 'Pieces' | 'Kg' | 'Liters' | 'Grams' | 'ml';
  emoji?: string;
  color?: string;
  coreCharge?: number;
  barcode?: string | null;
  productImage?: string;
  warrantyPeriod?: string | null;
  returnPolicy?: string | null;
  baseModel?: string | null;
  lunaVisibility?: string;
  minPrice?: number | null;
  maxPrice?: number | null;
  expiryDate?: string | null;
}

interface CartItem {
  product: Product;
  qty: number;
  itemDiscount?: number;
  itemMarkup?: number;
  damageFlag?: boolean;
  adjustedPrice?: number;
  warrantyPeriod?: string;
  returnPolicy?: string;
  originalPrice?: number;
  priceLabel?: string;
}

interface HeldBill {
  id: string;
  label: string;
  items: CartItem[];
  time: Date;
}

interface SaleStat {
  id: string;
  customerId?: number | null;
  total: number;
  subtotal: number;
  discountPct: number;
  discountAmt: number;
  items: number;
  method: string;
  time: Date;
  cashierName: string;
  cart: CartItem[];
  upsellShown: boolean;
  upsellAccepted: boolean;
  upsellItem?: string;
  creditAmount?: number;
  creditPaidAmount?: number;
  creditPromiseDate?: string | null;
  creditStatus?: string | null;
  creditPaymentHistory?: any[] | null;
  customer?: any | null;
  brokerName?: string | null;
  brokerCommission?: number;
  brokerStatus?: string | null;
  brokerPaymentHistory?: any | null;
  splitDetails?: any[] | null;
  storeCreditUsed?: number;
}

interface POSDashboardProps {
  storeApiKey: string;
  storeName: string;
  cashierName?: string;
  onExit?: () => void;
  onSwitchShop?: () => void;
  cashierMode?: boolean; // simplified cashier-only view
}

const formatRs = (amount: number) =>
  'Rs. ' + amount.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getCategoryEmoji = (category: string): string => {
  const map: Record<string, string> = {
    'Grocery':    '🌾', 'Dairy':      '🥛', 'Beverages':  '🥤',
    'Auto Parts': '⚙️', 'Hardware':   '🛠️', 'Pharmacy':   '💊',
    'Meat':       '🥩', 'Household':  '🧴', 'Electronics':'💻',
    'Clothing':   '👕', 'Bakery':     '🍞', 'Frozen':     '🧊',
    'Stationery': '✏️', 'Toys':       '🧸', 'Beauty':     '💄',
    'Produce':    '🥬',
  };
  if (map[category]) return map[category];
  // Keyword-based fuzzy fallback for dynamic categories
  const lc = category.toLowerCase();
  if (lc.includes('electron') || lc.includes('laptop') || lc.includes('computer') || lc.includes('tech')) return '💻';
  if (lc.includes('auto') || lc.includes('vehicle') || lc.includes('car')) return '⚙️';
  if (lc.includes('pharm') || lc.includes('medic') || lc.includes('health')) return '💊';
  if (lc.includes('beverage') || lc.includes('drink')) return '🥤';
  if (lc.includes('cloth') || lc.includes('fashion') || lc.includes('wear')) return '👕';
  if (lc.includes('meat') || lc.includes('fish') || lc.includes('seafood')) return '🥩';
  if (lc.includes('dairy') || lc.includes('milk')) return '🥛';
  if (lc.includes('hardware') || lc.includes('tool')) return '🛠️';
  if (lc.includes('bake') || lc.includes('bread')) return '🍞';
  if (lc.includes('toy') || lc.includes('game')) return '🧸';
  if (lc.includes('beauty') || lc.includes('cosmetic')) return '💄';
  if (lc.includes('station') || lc.includes('office') || lc.includes('book')) return '✏️';
  if (lc.includes('house') || lc.includes('clean') || lc.includes('home')) return '🧴';
  if (lc.includes('frozen') || lc.includes('ice')) return '🧊';
  return '📦';
};

const getCategoryColor = (category: string): string => {
  const map: Record<string, string> = {
    'Grocery':    'from-amber-500/20 to-yellow-500/10 border-amber-500/30',
    'Dairy':      'from-sky-500/20 to-blue-500/10 border-sky-500/30',
    'Beverages':  'from-red-500/20 to-rose-500/10 border-red-500/30',
    'Auto Parts': 'from-slate-500/20 to-zinc-500/10 border-slate-500/30',
    'Hardware':   'from-stone-500/20 to-neutral-500/10 border-stone-500/30',
    'Pharmacy':   'from-pink-500/20 to-rose-500/10 border-pink-500/30',
    'Meat':       'from-orange-600/20 to-red-500/10 border-orange-600/30',
    'Household':  'from-lime-500/20 to-green-500/10 border-lime-500/30',
    'Electronics':'from-cyan-500/20 to-teal-500/10 border-cyan-500/30',
    'Bakery':     'from-yellow-500/20 to-amber-500/10 border-yellow-500/30',
    'Frozen':     'from-indigo-500/20 to-blue-500/10 border-indigo-500/30',
    'Clothing':   'from-violet-500/20 to-purple-500/10 border-violet-500/30',
    'Stationery': 'from-emerald-500/20 to-green-500/10 border-emerald-500/30',
    'Toys':       'from-fuchsia-500/20 to-pink-500/10 border-fuchsia-500/30',
    'Beauty':     'from-rose-500/20 to-pink-500/10 border-rose-500/30',
    'Produce':    'from-green-500/20 to-lime-500/10 border-green-500/30',
  };
  if (map[category]) return map[category];
  const lc = category.toLowerCase();
  if (lc.includes('electron') || lc.includes('tech')) return map['Electronics'];
  if (lc.includes('auto') || lc.includes('vehicle')) return map['Auto Parts'];
  if (lc.includes('pharm') || lc.includes('medic')) return map['Pharmacy'];
  if (lc.includes('beverage') || lc.includes('drink')) return map['Beverages'];
  if (lc.includes('cloth') || lc.includes('fashion')) return map['Clothing'];
  return 'from-purple-500/20 to-violet-500/10 border-purple-500/30';
};

const mapProducts = (raw: any[]): Product[] => raw.map((p: any) => ({
  ...p,
  price:      parseFloat(p.price)      || 0,
  stockCount: parseFloat(p.stockCount) || 0,
  unitType:   p.unitType || 'Pieces',
  minPrice:   p.minPrice != null && p.minPrice !== '' ? parseFloat(p.minPrice) : null,
  maxPrice:   p.maxPrice != null && p.maxPrice !== '' ? parseFloat(p.maxPrice) : null,
  emoji:      p.emoji || getCategoryEmoji(p.category),
  color:      p.color || getCategoryColor(p.category),
}));

// Sri Lankan dummy fallback inventory
const DUMMY_INVENTORY: Product[] = [
  { id: 1,  name: 'Samba Rice 1kg',              category: 'Grocery',    price: 260,   stockCount: 200, emoji: '🌾', color: 'from-amber-500/20 to-yellow-500/10 border-amber-500/30' },
  { id: 2,  name: 'Munchee Super Cream Cracker', category: 'Grocery',    price: 500,   stockCount: 80,  emoji: '🍘', color: 'from-orange-500/20 to-amber-500/10 border-orange-500/30' },
  { id: 3,  name: 'Anchor Full Cream Milk 400g', category: 'Dairy',      price: 1350,  stockCount: 40,  emoji: '🥛', color: 'from-sky-500/20 to-blue-500/10 border-sky-500/30' },
  { id: 4,  name: 'Coca-Cola 1.5L',              category: 'Beverages',  price: 395,   stockCount: 60,  emoji: '🥤', color: 'from-red-500/20 to-rose-500/10 border-red-500/30' },
  { id: 5,  name: 'Ceylon Tea 200g',             category: 'Grocery',    price: 790,   stockCount: 55,  emoji: '🍵', color: 'from-emerald-500/20 to-green-500/10 border-emerald-500/30' },
  { id: 6,  name: 'Elephant Ginger Beer 330ml',  category: 'Beverages',  price: 180,   stockCount: 120, emoji: '🍺', color: 'from-yellow-500/20 to-lime-500/10 border-yellow-500/30' },
  { id: 7,  name: 'Toyota Aqua Brake Pads',      category: 'Auto Parts', price: 4500,  stockCount: 12,  emoji: '🔧', color: 'from-slate-500/20 to-zinc-500/10 border-slate-500/30' },
  { id: 8,  name: 'NGK Spark Plug (BKR5E)',      category: 'Auto Parts', price: 1200,  stockCount: 30,  emoji: '⚡', color: 'from-cyan-500/20 to-teal-500/10 border-cyan-500/30' },
  { id: 9,  name: 'Shell Helix 10W-40 1L',       category: 'Auto Parts', price: 2800,  stockCount: 25,  unitType: 'Liters', emoji: '🛢️', color: 'from-purple-500/20 to-violet-500/10 border-purple-500/30' },
  { id: 10, name: 'Sunlight Soap Bar 90g',        category: 'Household',  price: 115,   stockCount: 150, emoji: '🧼', color: 'from-lime-500/20 to-green-500/10 border-lime-500/30' },
  { id: 11, name: 'Panadol 500mg (10 tabs)',      category: 'Pharmacy',   price: 85,    stockCount: 200, emoji: '💊', color: 'from-pink-500/20 to-rose-500/10 border-pink-500/30' },
  { id: 12, name: 'Fonterra Yoghurt 80g',         category: 'Dairy',      price: 120,   stockCount: 70,  emoji: '🍦', color: 'from-indigo-500/20 to-blue-500/10 border-indigo-500/30' },
  { id: 13, name: 'Lanka Tiles Floor Tile 1pc',   category: 'Hardware',   price: 380,   stockCount: 500, emoji: '🏗️', color: 'from-stone-500/20 to-neutral-500/10 border-stone-500/30' },
  { id: 14, name: 'Cargills Chicken',              category: 'Meat',       price: 1550,  stockCount: 20,  unitType: 'Kg', emoji: '🍗', color: 'from-orange-600/20 to-red-500/10 border-orange-600/30' },
  { id: 15, name: 'Dencol Toothpaste 175g',       category: 'Household',  price: 265,   stockCount: 90,  emoji: '🦷', color: 'from-teal-500/20 to-cyan-500/10 border-teal-500/30' },
];

const CATEGORY_COLORS: Record<string, string> = {
  'All':        'bg-cyan-500/20 border-cyan-500/40 text-cyan-300',
  'Grocery':    'bg-amber-500/15 border-amber-500/30 text-amber-300',
  'Dairy':      'bg-sky-500/15 border-sky-500/30 text-sky-300',
  'Beverages':  'bg-red-500/15 border-red-500/30 text-red-300',
  'Auto Parts': 'bg-slate-500/15 border-slate-500/30 text-slate-300',
  'Household':  'bg-lime-500/15 border-lime-500/30 text-lime-300',
  'Pharmacy':   'bg-pink-500/15 border-pink-500/30 text-pink-300',
  'Meat':       'bg-orange-600/15 border-orange-600/30 text-orange-300',
  'Hardware':   'bg-stone-500/15 border-stone-500/30 text-stone-300',
};

const BLANK_PRODUCT = { name: '', costPrice: '', sellingPrice: '', minPrice: '', maxPrice: '', category: 'Grocery', categoryId: '', stockCount: '', unitType: 'Pieces' as string, imageUrl: '', coreCharge: '', barcode: '', warrantyPeriod: '', returnPolicy: '', productImage: '', baseModel: '', lunaVisibility: 'PUBLIC', expiryDate: '' };

// 🎨 Dynamic Logo-Based SaaS Theming Helpers
const hexToRgbString = (hex: string): string => {
  // Remove # if present
  const clean = hex.replace('#', '');
  // Parse hex
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  // Return space-separated RGB for Tailwind opacity modifiers
  return `${r} ${g} ${b}`;
};

// Calculate relative luminance per WCAG 2.1
// https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
const getLuminance = (r: number, g: number, b: number): number => {
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

// Determine if text should be black or white based on background luminance
// Returns 'white' or 'black'
const getContrastTextColor = (r: number, g: number, b: number): 'white' | 'black' => {
  const luminance = getLuminance(r, g, b);
  // WCAG recommends white text for dark backgrounds (luminance < 0.5)
  return luminance > 0.5 ? 'black' : 'white';
};

// 🎨 Multi-Color Brand Palette Type (from Gemini Vision)
interface BrandPalette {
  primaryBrand: string;      // "R G B"
  secondaryAccent: string;   // "R G B"
  surfaceBackground: string; // "R G B"
  textOnPrimary: string;     // "R G B"
}

const applyThemeColor = (palette: BrandPalette | null, enabled: boolean) => {
  const root = document.documentElement;
  if (!enabled || !palette) {
    // Reset to defaults (Sky Blue)
    root.style.setProperty('--themeBrand-rgb', '14 165 233');
    root.style.setProperty('--brand-primary-rgb', '14 165 233');
    root.style.setProperty('--brand-secondary-rgb', '99 102 241');
    root.style.setProperty('--brand-surface-rgb', '240 249 255');
    root.style.setProperty('--brand-text-on-primary-rgb', '255 255 255');
    return;
  }
  try {
    // Apply full brand palette to CSS variables
    root.style.setProperty('--themeBrand-rgb', palette.primaryBrand);
    root.style.setProperty('--brand-primary-rgb', palette.primaryBrand);
    root.style.setProperty('--brand-secondary-rgb', palette.secondaryAccent);
    root.style.setProperty('--brand-surface-rgb', palette.surfaceBackground);
    root.style.setProperty('--brand-text-on-primary-rgb', palette.textOnPrimary);
  } catch (e) {
    console.error('Failed to apply theme palette:', e);
    // Reset to defaults on error
    root.style.setProperty('--themeBrand-rgb', '14 165 233');
    root.style.setProperty('--brand-primary-rgb', '14 165 233');
    root.style.setProperty('--brand-secondary-rgb', '99 102 241');
    root.style.setProperty('--brand-surface-rgb', '240 249 255');
    root.style.setProperty('--brand-text-on-primary-rgb', '255 255 255');
  }
};

// 🎨 Extract brand palette using Gemini Vision API
const extractBrandPalette = async (imageBase64: string): Promise<BrandPalette | null> => {
  try {
    const res = await fetch('/api/extract-brand-palette', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageBase64 })
    });
    if (!res.ok) {
      console.error('Brand palette extraction failed:', res.status);
      return null;
    }
    const data = await res.json();
    if (data.success && data.palette) {
      return data.palette as BrandPalette;
    }
    return null;
  } catch (e) {
    console.error('Brand palette extraction error:', e);
    return null;
  }
};

const extractColorFromImage = async (imageUrl: string, fac: FastAverageColor): Promise<string | null> => {
  try {
    const result = await fac.getColorAsync(imageUrl, { algorithm: 'dominant' });
    // result.hex contains the hex color
    return result.hex;
  } catch (e) {
    console.error('Color extraction failed:', e);
    return null;
  }
};

// ═══ STANDALONE ADD PRODUCT MODAL ═══════════════════════════════════
// Extracted as a top-level component so parent re-renders CANNOT unmount it.
// All form state + AI debounce lives here — completely isolated.
const AddProductModal = React.memo(function AddProductModal({ isOpen, onClose, storeApiKey, onProductAdded, autoPartsModuleEnabled = false, prefillBarcode = '', inventory = [], onSelectExistingProduct }: {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  onProductAdded: (product: Product) => void;
  autoPartsModuleEnabled?: boolean;
  prefillBarcode?: string;
  inventory?: Product[];
  onSelectExistingProduct?: (product: Product) => void;
}) {
  const [form, setForm] = useState(BLANK_PRODUCT);
  const [aiCategoryMatch, setAiCategoryMatch] = useState(false);
  const [aiFailed, setAiFailed] = useState(false);
  const [aiEmoji, setAiEmoji] = useState('📦');
  const [suppliers, setSuppliers] = useState<{ id: number; name: string }[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [categories, setCategories] = useState<{ id: number; name: string; warrantyPeriod?: string | null; returnPolicy?: string }[]>([]);
  const [addCameraOpen, setAddCameraOpen] = useState(false);
  const [stockItemsExpanded, setStockItemsExpanded] = useState(false);
  const [individualStockItems, setIndividualStockItems] = useState<{ expiryDate: string }[]>([]);
  // Multiple prices
  const [priceList, setPriceList] = useState<Array<{ label: string; price: string }>>([]);

  // 🔍 Real-time Barcode Collision Detection & Existing Product Cross-Reference
  const assignedProduct = useMemo(() => {
    const code = form.barcode?.trim();
    if (!code || code.length < 2 || !Array.isArray(inventory) || inventory.length === 0) return null;
    return inventory.find(p => p.barcode && p.barcode.trim() === code) || null;
  }, [form.barcode, inventory]);

  // ── Hardware barcode scanner fast-type listener (inside Add Product)
  const addBarcodeBufferRef = useRef('');
  const addLastKeyTimeRef = useRef(0);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // Skip if user is focused in a text input/textarea (let them type normally)
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
      // Skip if camera scanner is open
      if (addCameraOpen) return;
      const now = Date.now();
      if (now - addLastKeyTimeRef.current > 120) addBarcodeBufferRef.current = '';
      addLastKeyTimeRef.current = now;
      if (e.key === 'Enter' && addBarcodeBufferRef.current.length > 2) {
        // ✅ Detected hardware scanner — auto-fill barcode field
        setForm(p => ({ ...p, barcode: addBarcodeBufferRef.current }));
        addBarcodeBufferRef.current = '';
        e.preventDefault();
        // Focus back on form
      } else if (e.key.length === 1) {
        addBarcodeBufferRef.current += e.key;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      addBarcodeBufferRef.current = '';
    };
  }, [isOpen, addCameraOpen]);

  useEffect(() => {
    fetch('/api/categories?storeId=16', { headers: { 'x-store-api-key': storeApiKey } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [storeApiKey]);

  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/store/suppliers', { headers: { 'x-store-api-key': storeApiKey } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setSuppliers(Array.isArray(data) ? data : []))
      .catch(() => setSuppliers([]));
  }, [isOpen, storeApiKey]);

  // Reset form when modal opens
  const prevOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      const bc = prefillBarcode || '';
      setForm(p => ({ ...BLANK_PRODUCT, barcode: bc }));
      setAiCategoryMatch(false);
      setAiFailed(false);
      setAiEmoji('📦');
      setSelectedSupplierId('');
      setStockItemsExpanded(false);
      setIndividualStockItems([]);
    }
    prevOpen.current = isOpen;
  }, [isOpen, prefillBarcode]);

  // Valid dropdown categories — used for safety net validation
  const VALID_CATEGORIES = ['Grocery','Dairy','Beverages','Auto Parts','Hardware','Pharmacy','Meat','Household','Produce','Electronics','Bakery','Frozen','Clothing','Stationery','Toys','Beauty'];

  // AI auto-categorize: debounced 1500ms after user stops typing product name
  useEffect(() => {
    const name = form.name.trim();
    // Reset stale state when input is too short or cleared
    if (name.length < 3 || !isOpen) {
      setAiFailed(false);
      setAiCategoryMatch(false);
      if (name.length < 1) setAiEmoji('📦');
      return;
    }
    // Immediately show loading indicator — proves UI is alive while debounce waits
    setAiEmoji('⏳');
    setAiCategoryMatch(false);
    setAiFailed(false);

    const timer = setTimeout(async () => {
      try {
        const res = await fetch('/api/ai/categorize', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productName: name }),
        });
        const data = await res.json();
        // Always set emoji — never leave it stale
        const newEmoji = data.emoji || '📦';
        setAiEmoji(newEmoji);

        // Graceful category coercion
        if (data.category) {
          const finalCategory = VALID_CATEGORIES.includes(data.category) ? data.category : 'Toys';
          setForm(p => {
            const match = categories.find(c => c.name === finalCategory);
            return { ...p, category: finalCategory, categoryId: match ? String(match.id) : p.categoryId };
          });
          setAiCategoryMatch(true);
          setAiFailed(false);
          setTimeout(() => setAiCategoryMatch(false), 3000);
        } else {
          setAiFailed(true);
          setTimeout(() => setAiFailed(false), 4000);
        }
      } catch {
        setAiEmoji('📦');
        setAiFailed(true);
        setTimeout(() => setAiFailed(false), 4000);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [form.name, isOpen]);

  const [saveError, setSaveError] = useState('');

  const handleSave = async () => {
    if (!form.name.trim() || !form.sellingPrice) return;
    setSaveError('');
    try {
      const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch('/api/add-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {})
        },
        body: JSON.stringify({
          apiKey: storeApiKey,
          name: form.name.trim(),
          category: form.category,
          categoryId: form.categoryId ? parseInt(form.categoryId) : null,
          costPrice: form.costPrice ? parseFloat(form.costPrice) : null,
          sellingPrice: parseFloat(form.sellingPrice),
          stockCount: parseFloat(form.stockCount || '0'),
          unitType: form.unitType,
          coreCharge: 0,
          barcode: form.barcode || null,
          warrantyPeriod: form.warrantyPeriod || null,
          returnPolicy: form.returnPolicy || null,
          baseModel: form.baseModel || null,
          lunaVisibility: form.lunaVisibility || 'PUBLIC',
          minPrice: form.minPrice ? parseFloat(form.minPrice) : null,
          maxPrice: form.maxPrice ? parseFloat(form.maxPrice) : null,
          supplierId: selectedSupplierId || null,
          productImage: form.productImage || null,
          expiryDate: stockItemsExpanded ? null : (form.expiryDate || null),
          stockItems: stockItemsExpanded ? individualStockItems : null,
          priceList: priceList.filter(p => p.label.trim() && p.price && parseFloat(p.price) > 0).map(p => ({ label: p.label.trim(), price: parseFloat(p.price) })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setSaveError(data.error || data.message || 'Failed to add product');
        return;
      }
      const p: Product = {
        ...data.product,
        emoji: getCategoryEmoji(data.product.category),
        color: getCategoryColor(data.product.category),
      };
      onProductAdded(p);
      setForm(BLANK_PRODUCT);
      setStockItemsExpanded(false);
      setIndividualStockItems([]);
      setPriceList([]);
      setSaveError('');
      onClose();
    } catch (err: any) {
      setSaveError(err?.message || 'Network error — failed to add product');
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9996] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
        className="w-full max-w-md bg-[#080b18] border border-white/10 rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.07]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <PlusCircle size={13} className="text-emerald-400" />
            </div>
            <p className="font-black text-[13px] text-white">Add Product</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/8 text-slate-600 hover:text-white transition-colors"><X size={13} /></button>
        </div>

        <div className="p-5 space-y-3">
          {saveError && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs font-medium">
              <AlertTriangle size={14} className="shrink-0 text-rose-400" />
              <span>{saveError}</span>
            </div>
          )}

          {/* Name */}
          <div className="space-y-1">
            <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Product Name *</label>
            <input value={form.name} onChange={e => { setForm(p => ({ ...p, name: e.target.value })); setAiCategoryMatch(false); }}
              placeholder="e.g. Munchee Milk Toffee 100g" autoFocus
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40 font-mono" />
          </div>

          {/* 📱 Barcode / QR Code */}
          <div className="space-y-1">
            <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Barcode / QR Code (Optional)</label>
            <div className="flex gap-2">
              <input value={form.barcode} onChange={e => setForm(p => ({ ...p, barcode: e.target.value }))}
                placeholder="Scan or enter barcode…"
                className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-cyan-500/40 font-mono" />
              <button type="button" onClick={() => setAddCameraOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 text-[10px] font-bold hover:bg-cyan-500/25 transition-all whitespace-nowrap">
                <Camera size={11} /> Scan
              </button>
            </div>

            {/* 🔍 Barcode Collision Alert & Assigned Product Card */}
            {assignedProduct && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs space-y-2.5 shadow-[0_0_25px_rgba(245,158,11,0.15)] backdrop-blur-md"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 font-bold text-amber-300 text-[11px]">
                    <AlertTriangle size={13} className="text-amber-400 shrink-0" />
                    <span>Barcode Already Assigned!</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    EXISTS IN INVENTORY
                  </span>
                </div>

                <div className="flex items-center justify-between p-2.5 rounded-xl bg-black/40 border border-white/5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-base shrink-0">{assignedProduct.emoji || '📦'}</span>
                    <div className="min-w-0">
                      <p className="font-bold text-white text-[12px] truncate">{assignedProduct.name}</p>
                      <p className="text-[10px] text-slate-400">
                        {assignedProduct.category} • Current Stock: <strong className="text-amber-300 font-mono">{assignedProduct.stockCount} {assignedProduct.unitType || 'Pcs'}</strong>
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-mono font-black text-emerald-400">Rs. {assignedProduct.price || assignedProduct.sellingPrice}</p>
                    {assignedProduct.costPrice && (
                      <p className="text-[9px] text-slate-500 font-mono">Cost: Rs. {assignedProduct.costPrice}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-0.5">
                  <span className="text-[10px] text-amber-400/80">
                    ⚠️ Product already exists with this barcode.
                  </span>
                  {onSelectExistingProduct && (
                    <button
                      type="button"
                      onClick={() => onSelectExistingProduct(assignedProduct)}
                      className="px-2.5 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 active:scale-95 transition-all shadow-sm shadow-amber-500/30"
                    >
                      <Edit size={10} /> Edit / Restock This Item
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {!assignedProduct && form.barcode && form.barcode.trim().length >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 text-[11px] font-medium"
              >
                <CheckCircle2 size={13} className="text-emerald-400 shrink-0" />
                <span>✨ <strong>New Barcode Available:</strong> Not assigned to any item yet. Ready for new product!</span>
              </motion.div>
            )}
          </div>

          {/* Camera Scanner (inside AddProductModal) */}
          <AnimatePresence>
            {addCameraOpen && (
              <CameraScanner
                onDetected={(code) => { setForm(p => ({ ...p, barcode: code })); setAddCameraOpen(false); }}
                onClose={() => setAddCameraOpen(false)}
              />
            )}
          </AnimatePresence>

          {/* Cost Price + Selling Price */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Cost Price (Rs.)</label>
              <input value={form.costPrice} onChange={e => setForm(p => ({ ...p, costPrice: e.target.value }))}
                type="number" min="0" step="0.01" placeholder="Buying price"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-orange-500/40 font-mono" />
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Selling Price (Rs.) *</label>
              <input value={form.sellingPrice} onChange={e => setForm(p => ({ ...p, sellingPrice: e.target.value }))}
                type="number" min="0" step="0.01" placeholder="Selling price"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40 font-mono" />
            </div>
          </div>

          {/* ↔️ Between Price Range (Allowed Price Range for Cashiers) */}
          <div className="p-2.5 rounded-2xl bg-indigo-500/5 border border-indigo-500/15 space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[9px] text-indigo-300 font-bold uppercase tracking-wider font-mono flex items-center gap-1">
                ↔️ Between Price Range (අතරමැදි මිල ගණන්)
              </label>
              <span className="text-[8px] text-slate-400 font-mono">Optional</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input value={form.minPrice} onChange={e => setForm(p => ({ ...p, minPrice: e.target.value }))}
                  type="number" min="0" step="0.01" placeholder="Min Price (e.g. 2000)"
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-2.5 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-400/50 font-mono" />
              </div>
              <div>
                <input value={form.maxPrice} onChange={e => setForm(p => ({ ...p, maxPrice: e.target.value }))}
                  type="number" min="0" step="0.01" placeholder="Max Price (e.g. 3000)"
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-2.5 py-2 text-[12px] text-white placeholder:text-slate-600 outline-none focus:border-indigo-400/50 font-mono" />
              </div>
            </div>
          </div>

          {/* Live Profit Calculator */}
          {form.costPrice && form.sellingPrice && (() => {
            const cost = parseFloat(form.costPrice);
            const sell = parseFloat(form.sellingPrice);
            const profit = sell - cost;
            const margin = cost > 0 ? ((profit / cost) * 100) : 0;
            const isPositive = profit > 0;
            return (
              <div className={`flex items-center justify-between px-3 py-2 rounded-xl border ${isPositive ? 'bg-emerald-500/8 border-emerald-500/20' : 'bg-red-500/8 border-red-500/20'}`}>
                <div className="flex items-center gap-2">
                  <TrendingUp size={12} className={isPositive ? 'text-emerald-400' : 'text-red-400'} />
                  <span className={`text-[10px] font-bold ${isPositive ? 'text-emerald-300' : 'text-red-300'}`}>
                    Profit: {formatRs(profit)}
                  </span>
                </div>
                <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black ${isPositive ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
                  {margin >= 0 ? '+' : ''}{margin.toFixed(1)}% margin
                </span>
              </div>
            );
          })()}

          {/* ── Multiple Prices ── */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">🏷️ Additional Prices</label>
              <button
                type="button"
                onClick={() => setPriceList(prev => [...prev, { label: '', price: '' }])}
                className="text-[10px] font-bold text-emerald-400 hover:text-emerald-300 transition-all"
              >+ Add Price</button>
            </div>
            {priceList.length > 0 && (
              <div className="space-y-1.5">
                {priceList.map((p, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <input
                      type="text"
                      placeholder="Label (e.g. Wholesale)"
                      value={p.label}
                      onChange={e => {
                        const newList = [...priceList];
                        newList[i] = { ...newList[i], label: e.target.value };
                        setPriceList(newList);
                      }}
                      className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2.5 py-2 text-[12px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40"
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price"
                      value={p.price}
                      onChange={e => {
                        const newList = [...priceList];
                        newList[i] = { ...newList[i], price: e.target.value };
                        setPriceList(newList);
                      }}
                      className="w-24 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2.5 py-2 text-[12px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setPriceList(prev => prev.filter((_, j) => j !== i))}
                      className="p-1.5 text-red-400 hover:text-red-300 transition-all"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unit Type — Segmented Control (1-click) */}
          <div className="space-y-1.5">
            <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Sold By</label>
            <div className="flex gap-1.5">
              {([
                { value: 'Pieces', label: 'Pcs', icon: '📦' },
                { value: 'Kg',     label: 'Kg',  icon: '⚖️' },
                { value: 'Grams',  label: 'g',   icon: '⚖️' },
                { value: 'Liters', label: 'L',   icon: '💧' },
                { value: 'ml',     label: 'ml',  icon: '💧' },
              ] as const).map(u => {
                const active = form.unitType === u.value;
                return (
                  <button key={u.value} type="button"
                    onClick={() => setForm(p => ({ ...p, unitType: u.value }))}
                    className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl text-[10px] font-bold font-mono transition-all duration-150 border
                      ${active
                        ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.25)]'
                        : 'bg-white/[0.03] border-white/[0.08] text-slate-600 hover:border-white/20 hover:text-slate-400'
                      }`}>
                    <span className="text-sm leading-none">{u.icon}</span>
                    <span>{u.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stock Qty */}
          <div className="space-y-1">
            <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">
              Stock {form.unitType === 'Pieces' ? 'Qty' : `(${form.unitType})`}
            </label>
            <input value={form.stockCount} onChange={e => setForm(p => ({ ...p, stockCount: e.target.value }))}
              type="number" min="0" step={form.unitType === 'Pieces' ? '1' : '0.001'}
              placeholder={form.unitType === 'Pieces' ? '0' : '0.000'}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40 font-mono" />
          </div>

          {/* 📅 Expiry Date — Expandable Individual or Apply-to-All */}
          {parseFloat(form.stockCount) > 0 && form.unitType === 'Pieces' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">
                  Expiry Dates <span className="text-slate-500">(Optional)</span>
                </label>
                <button type="button" onClick={() => {
                  if (!stockItemsExpanded) {
                    // Expand: create individual items with shared expiry date
                    const qty = Math.min(parseInt(form.stockCount) || 1, 100);
                    const items = Array.from({ length: qty }, () => ({ expiryDate: form.expiryDate || '' }));
                    setIndividualStockItems(items);
                  }
                  setStockItemsExpanded(!stockItemsExpanded);
                }} className="flex items-center gap-1 text-[9px] font-bold text-amber-400 hover:text-amber-300 transition-colors">
                  {stockItemsExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                  {stockItemsExpanded ? 'Collapse' : `Expand ${form.stockCount} items`}
                </button>
              </div>

              {/* Collapsed: Single date → applies to ALL */}
              {!stockItemsExpanded && (
                <div className="space-y-1">
                  <input value={form.expiryDate} onChange={e => setForm(p => ({ ...p, expiryDate: e.target.value }))}
                    type="date"
                    className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-amber-500/40 font-mono" />
                  {form.expiryDate && (
                    <p className="text-[8px] text-amber-400/70 font-mono">⚡ Same date for all {form.stockCount} items — auto-removed on scan</p>
                  )}
                </div>
              )}

              {/* Expanded: Individual rows */}
              {stockItemsExpanded && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => {
                      // Apply first item's date to all
                      const firstDate = individualStockItems[0]?.expiryDate || '';
                      setIndividualStockItems(prev => prev.map(() => ({ expiryDate: firstDate })));
                    }} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-bold hover:bg-amber-500/25 transition-all">
                      <Clock size={10} /> Apply First Date to All
                    </button>
                    <span className="text-[9px] text-slate-600 font-mono">{individualStockItems.length} items</span>
                  </div>
                  <div className="max-h-[240px] overflow-y-auto space-y-1 pr-1 rounded-xl bg-white/[0.02] border border-white/[0.06] p-2">
                    {individualStockItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 group">
                        <span className="w-6 h-6 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-[9px] text-slate-500 font-mono shrink-0">
                          {idx + 1}
                        </span>
                        <input
                          type="date"
                          value={item.expiryDate}
                          onChange={e => {
                            const newItems = [...individualStockItems];
                            newItems[idx] = { expiryDate: e.target.value };
                            setIndividualStockItems(newItems);
                          }}
                          className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-[11px] text-white placeholder:text-slate-700 outline-none focus:border-amber-500/40 font-mono"
                        />
                        <button type="button" onClick={() => {
                          const newItems = [...individualStockItems];
                          newItems[idx] = { expiryDate: '' };
                          setIndividualStockItems(newItems);
                        }} className="w-6 h-6 rounded-lg flex items-center justify-center text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100">
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 🚗 Core Charge — Auto Parts only */}
          {autoPartsModuleEnabled && (
            <div className="space-y-1">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">🚗 Core Charge (Rs.) — Refundable</label>
              <input value={form.coreCharge} onChange={e => setForm(p => ({ ...p, coreCharge: e.target.value }))}
                type="number" min="0" step="0.01" placeholder="e.g. 500 for battery"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-orange-500/40 font-mono" />
              <p className="text-[8px] text-slate-500 italic">Charged when item is sold, refunded when old part is returned</p>
            </div>
          )}

          {/* 🚗 Vehicle Fitment Tags — Auto Parts only */}
          {autoPartsModuleEnabled && (
            <div className="space-y-1.5 p-2.5 rounded-xl bg-orange-500/8 border border-orange-500/20">
              <label className="text-[9px] text-orange-300 uppercase tracking-widest font-mono font-bold">🚗 Vehicle Fitment (Optional)</label>
              <p className="text-[8px] text-slate-500">Tag compatible vehicle makes/models for search. Example: Toyota Prius 2009-2015</p>
              <p className="text-[8px] text-slate-400 italic">Note: Fitment data can be added after product creation via product details</p>
            </div>
          )}

          {/* Warranty & Return Policy */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Return Policy</label>
              <input value={form.returnPolicy} onChange={e => setForm(p => ({ ...p, returnPolicy: e.target.value }))}
                list="add-return-policies"
                maxLength={100} placeholder="e.g. 14 Days, No Return, Standard"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40 font-mono" />
              <datalist id="add-return-policies">
                <option value="14 Days" /><option value="7 Days" /><option value="3 Days" /><option value="No Return" /><option value="Standard" />
              </datalist>
            </div>
            <div className="space-y-1">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Warranty Period</label>
              <input value={form.warrantyPeriod} onChange={e => setForm(p => ({ ...p, warrantyPeriod: e.target.value }))}
                list="add-warranty-periods"
                maxLength={100} placeholder="e.g. 3 Months"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40 font-mono" />
              <datalist id="add-warranty-periods">
                <option value="6 Months" /><option value="1 Year" /><option value="2 Years" /><option value="Lifetime" /><option value="No Warranty" />
              </datalist>
            </div>
          </div>

          {/* Base Model */}
          <div className="space-y-1">
            <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Base Model (Parent Model)</label>
            <input value={form.baseModel} onChange={e => setForm(p => ({ ...p, baseModel: e.target.value }))}
              list="add-base-models"
              maxLength={100} placeholder="e.g. Toyota Prius, iPhone 15, Honda Dio"
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none focus:border-emerald-500/40 font-mono" />
            <datalist id="add-base-models">
              <option value="Toyota Prius" /><option value="Honda Civic" /><option value="Suzuki Alto" /><option value="Nissan Leaf" />
              <option value="iPhone 15" /><option value="Samsung Galaxy" /><option value="Honda Dio" /><option value="Yamaha FZ" />
            </datalist>
          </div>

          {/* Luna AI Visibility */}
          <div className="space-y-1">
            <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Luna AI Visibility</label>
            <select value={form.lunaVisibility} onChange={e => setForm(p => ({ ...p, lunaVisibility: e.target.value }))}
              className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white outline-none focus:border-emerald-500/40 font-mono">
              <option value="PUBLIC" className="bg-[#080b18]">🌐 Public — visible with price</option>
              <option value="HIDDEN" className="bg-[#080b18]">🚫 Hidden — excluded from Luna</option>
              <option value="PRICE_HIDDEN" className="bg-[#080b18]">🔒 Hide Price — visible, no price shown</option>
            </select>
          </div>

          {/* Category + AI indicator */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Category</label>
              <AnimatePresence mode="wait">
                {aiCategoryMatch && (
                  <motion.span key="matched" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                    className="flex items-center gap-1 text-[9px] font-bold text-violet-300">
                    <Sparkles size={10} className="text-violet-400" /> AI Matched
                  </motion.span>
                )}
                {!aiCategoryMatch && aiFailed && (
                  <motion.span key="failed" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
                    className="text-[9px] text-slate-600 font-mono">
                    Auto-match unavailable
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
            <input
              value={form.category}
              onChange={e => {
                const val = e.target.value;
                const match = categories.find(c => c.name.toLowerCase() === val.toLowerCase());
                setForm(p => ({ ...p, category: val, categoryId: match ? String(match.id) : '' }));
                if (match) { setAiEmoji(getCategoryEmoji(match.name)); }
                else { setAiEmoji(getCategoryEmoji(val)); }
                setAiCategoryMatch(false);
              }}
              list="add-categories"
              placeholder="Type or select a category"
              className={`w-full bg-white/[0.04] border rounded-xl px-3 py-2.5 text-[13px] text-white placeholder:text-slate-700 outline-none font-mono transition-all ${
                aiCategoryMatch ? 'border-violet-500/40 shadow-[0_0_12px_rgba(139,92,246,0.15)]' : 'border-white/[0.08] focus:border-emerald-500/40'
              }`} />
            <datalist id="add-categories">
              {categories.map(c => <option key={c.id} value={c.name} />)}
            </datalist>

            {/* 🤖 Auto Categorize button — sends product name to Luna AI */}
            {form.name.trim() && (
              <button
                type="button"
                onClick={async () => {
                  if (!form.name.trim()) return;
                  try {
                    const res = await fetch('/api/auto-categorize', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
                      body: JSON.stringify({
                        productNames: [form.name.trim()],
                        existingCategories: categories.map(c => c.name),
                      }),
                    });
                    const data = await res.json();
                    if (data.assignments && data.assignments[0]) {
                      const cat = data.assignments[0].category;
                      const match = categories.find(c => c.name.toLowerCase() === cat.toLowerCase());
                      setForm(p => ({ ...p, category: match ? match.name : cat, categoryId: match ? String(match.id) : '' }));
                      setAiCategoryMatch(true);
                      setAiEmoji(getCategoryEmoji(match ? match.name : cat));
                    }
                  } catch { /* ignore */ }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-400 text-[9px] font-bold hover:bg-violet-500/20 transition-all"
              >
                <Sparkles size={10} /> Auto Categorize
              </button>
            )}
          </div>

          {/* Supplier dropdown */}
          {suppliers.length > 0 && (
            <div className="space-y-1">
              <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Supplier (Optional)</label>
              <select value={selectedSupplierId} onChange={e => setSelectedSupplierId(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-[13px] text-white outline-none focus:border-emerald-500/40 font-mono">
                <option value="" className="bg-[#080b18]">— No Supplier —</option>
                {suppliers.map(s => (
                  <option key={s.id} value={String(s.id)} className="bg-[#080b18]">{s.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Product Image Upload */}
          <div className="space-y-1.5">
            <label className="text-[9px] text-slate-600 uppercase tracking-widest font-mono">Product Image (Optional)</label>
            <div className="flex items-center gap-3">
              {form.productImage ? (
                <div className="relative group">
                  <img src={form.productImage} alt="Product" className="w-16 h-16 object-cover rounded-lg border border-white/10" />
                  <button
                    onClick={() => setForm(p => ({ ...p, productImage: '' }))}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-white/10 flex items-center justify-center bg-white/[0.02]">
                  <Package size={16} className="text-slate-600" />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const compressed = await compressProductImage(file, 512, 512, 0.85);
                        setForm(p => ({ ...p, productImage: compressed }));
                      } catch {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setForm(p => ({ ...p, productImage: ev.target?.result as string }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }
                  }}
                  className="block w-full text-[10px] text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-cyan-500/10 file:text-cyan-400 file:text-[10px] file:font-semibold hover:file:bg-cyan-500/20 file:cursor-pointer"
                />
                <p className="text-[8px] text-cyan-400/80 font-mono mt-1">✨ Auto-compressed to 512×512px for maximum speed & disk savings</p>
              </div>
            </div>
          </div>

          {/* Emoji preview */}
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.025] border border-white/[0.06]">
            <span className="text-3xl">{aiEmoji}</span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-white truncate">{form.name || 'Product name'}</p>
              <p className="text-[9px] text-slate-600 font-mono">{form.category} · {form.sellingPrice ? formatRs(parseFloat(form.sellingPrice)) : 'Rs. 0.00'}{form.unitType !== 'Pieces' ? ` per ${form.unitType}` : ''}</p>
            </div>
          </div>

          <motion.button whileTap={{ scale: 0.96 }} onClick={handleSave}
            disabled={!form.name.trim() || !form.sellingPrice}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest bg-gradient-to-r from-emerald-500 to-cyan-500 text-black shadow-[0_0_20px_rgba(16,185,129,0.25)] hover:shadow-[0_0_32px_rgba(16,185,129,0.4)] disabled:opacity-40 disabled:cursor-not-allowed transition-shadow">
            <PlusCircle size={13} /> Save Product
          </motion.button>
        </div>

        {/* Floating Scan Button — always accessible while filling form */}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => setAddCameraOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-[0_0_24px_rgba(6,182,212,0.5)] flex items-center justify-center hover:shadow-[0_0_36px_rgba(6,182,212,0.7)] transition-shadow"
        >
          <Camera size={22} />
        </motion.button>
        <p className="fixed bottom-1 right-6 z-[9999] text-[8px] text-cyan-400/60 font-mono text-right">Scan QR/Barcode</p>
      </motion.div>
    </motion.div>
  );
});

// 📋 Estimates summary card for the Analytics tab
const EstimatesAnalyticsCard: React.FC<{ storeApiKey: string; formatRs: (n: number) => string }> = ({ storeApiKey, formatRs }) => {
  const [estData, setEstData] = useState<{ total: number; pending: number; converted: number; pendingValue: number; convertedValue: number } | null>(null);
  const [estLoading, setEstLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEstLoading(true);
    fetch(`/api/estimates?apiKey=${encodeURIComponent(storeApiKey)}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.estimates) {
          const all = d.estimates;
          const pending = all.filter((e: any) => e.status === 'pending');
          const converted = all.filter((e: any) => e.status === 'converted');
          setEstData({
            total: all.length,
            pending: pending.length,
            converted: converted.length,
            pendingValue: pending.reduce((sum: number, e: any) => sum + (parseFloat(e.grandTotal) || 0), 0),
            convertedValue: converted.reduce((sum: number, e: any) => sum + (parseFloat(e.grandTotal) || 0), 0),
          });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setEstLoading(false); });
    return () => { cancelled = true; };
  }, [storeApiKey]);

  return (
    <div className="rounded-xl sm:rounded-2xl border border-sky-100 bg-white p-3 sm:p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
          <FileText size={14} className="text-sky-600" />
        </div>
        <div>
          <h3 className="text-[12px] sm:text-[13px] font-bold text-slate-800">Estimates Summary</h3>
          <p className="text-[9px] sm:text-[10px] text-slate-500">Pending, converted & conversion rate</p>
        </div>
      </div>
      {estLoading ? (
        <div className="flex items-center justify-center py-6 text-slate-400 text-[11px]">
          <RefreshCw size={14} className="animate-spin mr-2" /> Loading…
        </div>
      ) : estData ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-sky-50 border border-sky-100">
            <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
              <FileText size={14} className="text-sky-600" />
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Pending</p>
              <p className="text-[15px] font-black text-slate-800 font-mono">{estData.pending}</p>
              <p className="text-[10px] text-slate-500 font-mono">{formatRs(estData.pendingValue)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle2 size={14} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Converted</p>
              <p className="text-[15px] font-black text-slate-800 font-mono">{estData.converted}</p>
              <p className="text-[10px] text-emerald-600 font-mono">
                {estData.total > 0
                  ? `${Math.round((estData.converted / estData.total) * 100)}% rate`
                  : '—'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <FileText size={14} className="text-slate-500" />
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Total</p>
              <p className="text-[15px] font-black text-slate-800 font-mono">{estData.total}</p>
              <p className="text-[10px] text-slate-500 font-mono">all time</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <FileText size={14} className="text-amber-600" />
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Converted Value</p>
              <p className="text-[15px] font-black text-slate-800 font-mono">{formatRs(estData.convertedValue)}</p>
              <p className="text-[10px] text-amber-600 font-mono">total revenue</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 text-slate-400 text-[10px] font-mono border border-dashed border-slate-200 rounded-xl">
          <FileText size={20} className="mx-auto mb-1 text-slate-300" />
          Could not load estimates
        </div>
      )}
    </div>
  );
};

export default function POSDashboard({ storeApiKey, storeName, cashierName = 'Cashier', onExit, onSwitchShop, cashierMode = false }: POSDashboardProps) {
  // ── Eager cache: secure, storeId-isolated product catalog (see src/hooks/useProductCache.ts) ──
  const {
    inventory,
    setInventory,
    commitStoreId,
    writeCache,
    readCache,
    clearSessionCache,
  } = useProductCache(storeApiKey, mapProducts);

  const [categoriesCache, setCategoriesCache] = useState<{ id: number; name: string; warrantyPeriod?: string | null; returnPolicy?: string }[]>([]);

  const handleExit = useCallback(() => {
    clearSessionCache();
    onExit?.();
  }, [clearSessionCache, onExit]);

  const handleSwitchShop = useCallback(() => {
    clearSessionCache();
    onSwitchShop?.();
  }, [clearSessionCache, onSwitchShop]);
  // SECURITY: role is hard-locked to the prop from route-based auth (/pos/admin vs /pos/cashier).
  // No internal toggle is permitted — the UI must never allow escalation from cashier to manager.
  const isCashierMode = cashierMode;
  // 🏪 Shop Status — Open/Closed toggle (persisted in localStorage)
  const [shopOpen, setShopOpen] = useState<boolean>(() => localStorage.getItem('kangavena:shopOpen') !== 'false');
  const [shopSyncing, setShopSyncing] = useState(false);
  const sendDayEndReport = useCallback(async () => {
    setShopSyncing(true);
    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch('/api/reports/day-end/send-whatsapp', {
        method: 'POST',
        headers: { 'x-store-api-key': storeApiKey, ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      });
      const data = await res.json();
      if (res.ok) {
        showToast('Day-end report sent via WhatsApp', 'success');
      } else {
        showToast(data.error || 'Failed to send report', 'error');
      }
    } catch {
      showToast('Cannot reach server for day-end report', 'error');
    } finally {
      setShopSyncing(false);
    }
  }, [storeApiKey]);
  const toggleShop = useCallback(() => {
    Haptics.fire('tap');
    setShopOpen(prev => {
      const next = !prev;
      localStorage.setItem('kangavena:shopOpen', String(next));
      if (!next) {
        // Shop closing — auto-fire day-end report after brief delay
        setTimeout(() => { void sendDayEndReport(); }, 500);
      }
      return next;
    });
  }, [sendDayEndReport]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkLunaVisibility, setBulkLunaVisibility] = useState<string>('PUBLIC');
  const [bulkVisLoading, setBulkVisLoading] = useState(false);
  const [activeWeightCardId, setActiveWeightCardId] = useState<number | null>(null);
  const [weightInputValue, setWeightInputValue] = useState('');
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [addProductBarcodePrefill, setAddProductBarcodePrefill] = useState('');
  const [bulkInventoryOpen, setBulkInventoryOpen] = useState(false);
  const [bulkInitialProducts, setBulkInitialProducts] = useState<any[]>([]);
  const [voiceProductOpen, setVoiceProductOpen] = useState(false);
  const [expressCheckoutOpen, setExpressCheckoutOpen] = useState(false);
  // 📱 Phase 8 — Mobile Bottom Action Sheet
  const [mobileActionSheetOpen, setMobileActionSheetOpen] = useState(false);
  const [mobileActionSheetReducedMotion, setMobileActionSheetReducedMotion] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  // Cashier management (admin only)
  const [cashiersOpen, setCashiersOpen] = useState(false);
  const [cashiersList, setCashiersList] = useState<{ id: number; name: string; username: string; createdAt?: string }[]>([]);
  const [selectedCashier, setSelectedCashier] = useState<{ id: number; name: string } | null>(null);
  const [cashiersLoading, setCashiersLoading] = useState(false);
  const [cashiersError, setCashiersError] = useState('');
  const [newCashier, setNewCashier] = useState({ name: '', username: '', password: '' });
  const [newCashierAdding, setNewCashierAdding] = useState(false);
  const [showCashierPw, setShowCashierPw] = useState(false);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  // 🧾 Digital Receipt — holds last completed transaction for receipt view
  const [lastTransaction, setLastTransaction] = useState<any>(null);
  // Fetch categories for warranty/return policy inheritance
  useEffect(() => {
    if (!storeApiKey) return;
    fetch('/api/categories', { headers: { 'x-store-api-key': storeApiKey } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setCategoriesCache(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [storeApiKey]);

  // 🏪 Dynamic Store Branding
  const [storeBranding, setStoreBranding] = useState<any>(null);
  // 🎨 Dynamic Logo-Based SaaS Theming (Multi-Color Brand Palette)
  const [logoThemeEnabled, setLogoThemeEnabled] = useState<boolean>(false);
  const [themePalette, setThemePalette] = useState<BrandPalette | null>(null);
  const facRef = useRef<FastAverageColor | null>(null);
  // ✨ Silent Whisperer — background AI upsell engine (no voice, no avatar)
  const [aiSuggestion, setAiSuggestion] = useState<{ item: string; reason: string } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());
  const [enableLunaSuggestions, setEnableLunaSuggestions] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem('kangavena:enableLunaSuggestions');
    return saved !== null ? saved === 'true' : true;
  });

  const handleToggleEnableLunaSuggestions = () => {
    setEnableLunaSuggestions(prev => {
      const next = !prev;
      localStorage.setItem('kangavena:enableLunaSuggestions', String(next));
      return next;
    });
  };
  // Upsell telemetry for this bill (reset on sale/clear)
  const upsellRef = useRef<{ shown: boolean; accepted: boolean; item: string | null }>({ shown: false, accepted: false, item: null });
  // 🔮 Manager Command Center state
  const [analyticsTab, setAnalyticsTab] = useState<'pulse' | 'analytics' | 'advanced' | 'lifetime' | 'transactions' | 'inventory' | 'payments'>('pulse');
  const [weeklyTrendsData, setWeeklyTrendsData] = useState<{ bestDay: string | null; worstDay: string | null; dayTotals: Record<string, number> } | null>(null);
  // 💳 Phase 10 — Payment reconciliation state
  const [paymentRecon, setPaymentRecon] = useState<any>(null);
  const [paymentReconLoading, setPaymentReconLoading] = useState(false);
  const [forensicTx, setForensicTx] = useState<SaleStat | null>(null);
  const [marketingBrainOpen, setMarketingBrainOpen] = useState(false);
  const [marketingCenterOpen, setMarketingCenterOpen] = useState(false);
  const [inventorySheetScannerOpen, setInventorySheetScannerOpen] = useState(false);
  const [storeId, setStoreId] = useState<string | null>(() => {
    // Hydrate from session on mount so storeId-gated features (profile edit, void auth, etc.)
    // work immediately without waiting for the /api/stores effect to complete.
    // The /api/stores effect will overwrite this with the server-confirmed value when online.
    try {
      const cashier = JSON.parse(localStorage.getItem('pos_cashier_session') || 'null');
      if (cashier?.store?.id) return String(cashier.store.id);
    } catch { /* malformed */ }
    try {
      const admin = JSON.parse(localStorage.getItem('pos_admin_session') || 'null');
      if (admin?.storeId) return String(admin.storeId);
    } catch { /* malformed */ }
    return null;
  });
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', managerWhatsapp: '', address: '', latitude: '', longitude: '', website: '', email: '' });
  const [storeLogo, setStoreLogo] = useState<string | null>(null);
  const [paMode, setPaMode] = useState(false);
  const [paModeSaving, setPaModeSaving] = useState(false);
  const [isLunaIndexed, setIsLunaIndexed] = useState(true);
  const [showLogoOnBill, setShowLogoOnBill] = useState(false);
  const [hidePoweredByOnBill, setHidePoweredByOnBill] = useState<boolean>(() => localStorage.getItem('kangavena:hidePoweredByOnBill') === 'true');
  const [receiptTemplate, setReceiptTemplate] = useState<string>(() => localStorage.getItem('kangavena:receiptTemplate') || 'modern_left_brand');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');

  // 🧭 HASH-BASED VIEW ROUTER — replaces fragmented modal states
  // Views: #pos (default), #bill, #analytics, #settings, #cashiers, #inventory
  const [currentView, setCurrentView] = useState<string>('pos');

  // Listen to hash changes for view routing
  useEffect(() => {
    const updateViewFromHash = () => {
      const hash = window.location.hash.replace('#', '') || 'pos';
      const validViews = ['pos', 'bill', 'analytics', 'settings', 'cashiers', 'inventory', 'dayend', 'customers', 'shifts', 'estimates', 'suppliers', 'employees', 'garage_jobs', 'vin_lookup'];
      setCurrentView(validViews.includes(hash) ? hash : 'pos');
    };
    updateViewFromHash();
    window.addEventListener('hashchange', updateViewFromHash);
    return () => window.removeEventListener('hashchange', updateViewFromHash);
  }, []);

  // 📱 Phase 8 — Accessibility: detect reduced-motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setMobileActionSheetReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setMobileActionSheetReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // 📱 Phase 8 — Accessibility: ESC closes Mobile Bottom Action Sheet
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileActionSheetOpen) {
        setMobileActionSheetOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileActionSheetOpen]);

  // Helper to navigate to a view
  const navigateTo = useCallback((view: string) => {
    if (view === 'pos') {
      history.pushState(null, '', window.location.pathname);
    } else {
      history.pushState(null, '', `#${view}`);
    }
    setCurrentView(view);
  }, []);

  const goBack = useCallback(() => {
    if (window.history.length > 1 && currentView !== 'pos') {
      history.back();
    } else {
      navigateTo('pos');
    }
  }, [currentView, navigateTo]);
  const [sessionStart] = useState<Date>(() => new Date());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // 🛡️ Phase 1 — Offline queue tri-state status (online | offline | syncing)
  const [offlineStatus, setOfflineStatus] = useState<OfflineStatus>(navigator.onLine ? 'online' : 'offline');
  const [pendingTxCount, setPendingTxCount] = useState<number>(0);
  const [failedTxCount, setFailedTxCount] = useState<number>(0);
  // 🔲 Barcode scanner connected status (auto-detected via fast typing pattern)
  const [scannerConnected, setScannerConnected] = useState(false);
  const scannerLastScanRef = useRef<number>(0);
  // 🖨️ Printer connection status (auto-detected via WebUSB)
  const [printerConnected, setPrinterConnected] = useState(false);
  // TODO: Implement Secure Backend Print Architecture
  // printerAgentLanIp removed — print agent integration torn down
  const [printerSetupOpen, setPrinterSetupOpen] = useState(false);
  const [autoPrintBill, setAutoPrintBill] = useState<boolean>(() => localStorage.getItem('kangavena:autoPrint') === 'true');
  const [agentPrinters, setAgentPrinters] = useState<string[]>([]);

  // 🔗 Resilient Zero-Touch pairing — auto-pairs when agent comes online
  useEffect(() => {
    const authHeaders = getStoreAuthHeaders(storeApiKey);
    if (Object.keys(authHeaders).length === 0) return;

    // Sync cloud credentials once
    syncFromCloud().then(cfg => {
      if (cfg.ip && cfg.secret) setAgentConfig(cfg.ip, cfg.secret, cfg.defaultPrinter);
    }).catch(() => {});

    // Poll agent status via server API (works from any device) + LAN fallback
    let isConnected = false;
    const poll = async () => {
      try {
        // Method 1: Server API — check /api/printer/status first (fast telemetry) or /api/printer/setup
        const activeHeaders = getStoreAuthHeaders(storeApiKey);
        let res = await fetch('/api/printer/status', {
          headers: activeHeaders,
          signal: AbortSignal.timeout(4000)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.agentConnected) {
            const printerNames = (data.discoveredPrinters || []).map((p: any) => p.name || p.address).filter(Boolean);
            if (printerNames.length > 0) setAgentPrinters(printerNames);
            setPrinterConnected(true);
            isConnected = true;
            return;
          }
        }

        // Fallback to /api/printer/setup
        res = await fetch('/api/printer/setup', {
          headers: activeHeaders,
          signal: AbortSignal.timeout(5000)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.agentConnected) {
            const printerNames = (data.printers || []).map((p: any) => p.name || p.connectionAddress).filter(Boolean);
            if (printerNames.length > 0) setAgentPrinters(printerNames);
            setPrinterConnected(true);
            isConnected = true;
            return;
          }
        }

        // Method 2: Direct LAN (same PC only — try 127.0.0.1 first)
        let lanRes: Response | null = null;
        for (const host of ['127.0.0.1', 'localhost']) {
          try {
            lanRes = await fetch(`http://${host}:9123/status`, { signal: AbortSignal.timeout(1000) });
            if (lanRes && lanRes.ok) break;
          } catch {}
        }
        if (lanRes && lanRes.ok) {
          const lanData = await lanRes.json();
          if (lanData.status === 'online' || lanData.success) {
            setPrinterConnected(true);
            isConnected = true;
            if (lanData.printers && lanData.printers.length > 0) {
              setAgentPrinters(lanData.printers);
            }
            if (lanData.paired === false) {
              const savedPrinter = getAgentConfig().defaultPrinter || '';
              autoPair(storeApiKey || '', savedPrinter).then(result => {
                if (result.success && result.printers) setAgentPrinters(result.printers);
              }).catch(() => {});
            }
            return;
          }
        }
        setPrinterConnected(false);
        isConnected = false;
      } catch {
        setPrinterConnected(false);
        isConnected = false;
      }
    };

    poll(); // Immediate check on mount
    let pollTimer: NodeJS.Timeout;
    const schedulePoll = () => {
      pollTimer = setTimeout(async () => {
        await poll();
        schedulePoll();
      }, isConnected ? 15000 : 8000);
    };
    schedulePoll();

    return () => clearTimeout(pollTimer);
  }, [storeApiKey]);
  // 🛡️ Phase 1 — Void modal state
  const [voidTarget, setVoidTarget] = useState<{ txRef: string; total: number } | null>(null);
  const [voidedTxRefs, setVoidedTxRefs] = useState<Set<string>>(new Set());
  // 🔄 Return modal state
  const [returnTarget, setReturnTarget] = useState<{
    txRef: string;
    items: Array<{ product: { id: number; name: string; price: number; emoji?: string }; qty: number }>;
    customerId?: number | null;
  } | null>(null);
  const [returnedTxRefs, setReturnedTxRefs] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'undo'; onUndo?: () => void } | null>(null);
  const undoTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [enterConfirmStep, setEnterConfirmStep] = useState<0 | 1>(0); // 0=waiting, 1=first Enter pressed
  const confirmPaymentRef = useRef<() => void>(() => {});
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [activeBaseModel, setActiveBaseModel] = useState<string>('All');
  const [discount, setDiscount] = useState(0);
  const [flatDiscount, setFlatDiscount] = useState(0);
  const [showDiscount, setShowDiscount] = useState(false);
  const [now, setNow] = useState(new Date());
  const [lunaOpen, setLunaOpen] = useState(false);
  const lunaRef = useRef<LunaVoiceModelHandle | null>(null);
  const [showBill, setShowBill] = useState(false);
  const [drawerPayMode, setDrawerPayMode] = useState(false);
  // Hash-based bill drawer: push #bill to history so hardware back button closes it
  const openBill = useCallback(() => {
    setShowBill(true);
    if (window.location.hash !== '#bill') history.pushState(null, '', '#bill');
  }, []);
  const closeBill = useCallback(() => {
    setShowBill(false);
    setDrawerPayMode(false);
    if (window.location.hash === '#bill') history.back();
  }, []);
  // Camera scanner
  const [cameraOpen, setCameraOpen] = useState(false);
  // Price picker for multiple-price products
  const [pricePickerOpen, setPricePickerOpen] = useState(false);
  const [pricePickerProduct, setPricePickerProduct] = useState<any>(null);
  const [pricePickerResolve, setPricePickerResolve] = useState<((value: { price: number; label: string } | null) => void) | null>(null);
  // Payment inline mode (desktop sidebar & mobile drawer)
  const [sidebarPayMode, setSidebarPayMode] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'mobile'>('cash');
  const [cashAmount, setCashAmount] = useState('');
  const [mobileProvider, setMobileProvider] = useState('');
  // ⚡ Phase 2.1 — Split payment
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  // 📋 Print Estimate modal state
  const [estimateModalOpen, setEstimateModalOpen] = useState(false);
  const [estimateSaveModalOpen, setEstimateSaveModalOpen] = useState(false);
  // 📋 Estimate print data — holds the last-saved estimate for standalone printing
  const [estimatePrintData, setEstimatePrintData] = useState<{
    id: number;
    customerName?: string;
    customerPhone?: string;
    vehicleMake?: string;
    vehicleModel?: string;
    vehicleYear?: string;
    licensePlate?: string;
    serviceType?: string;
    orderType?: string;
    deliveryDate?: string;
    cart: any[];
    subtotal: number;
    discountPct: number;
    grandTotal: number;
    notes?: string;
    validUntil: string;
    createdAt?: string;
  } | null>(null);
  const [shouldPrintEstimate, setShouldPrintEstimate] = useState(false);

  // 🖨️ Reactive Estimate Print Trigger — ensures DOM is 100% committed before spooling
  useEffect(() => {
    if (shouldPrintEstimate && estimatePrintData) {
      const timer = setTimeout(() => {
        window.print();
        setShouldPrintEstimate(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [shouldPrintEstimate, estimatePrintData]);
  // 🎯 Phase 3 — CRM / Loyalty state
  const [customer, setCustomer] = useState<CustomerLite | null>(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [pointsToRedeem, setPointsToRedeem] = useState<number>(0);
  const [storeCreditToUse, setStoreCreditToUse] = useState<number>(0);
  const [loyaltyConfig, setLoyaltyConfig] = useState<{ enabled: boolean; pointsPerRupee: number; rupeePerPoint: number }>({
    enabled: true, pointsPerRupee: 0.01, rupeePerPoint: 0.5,
  });
  // 🎯 Loyalty Settings form state (editable copy for the settings card)
  const [loyaltyForm, setLoyaltyForm] = useState({ enabled: true, pointsPerRupee: '0.01', rupeePerPoint: '0.50' });
  const [loyaltySaving, setLoyaltySaving] = useState(false);
  // 💾 Save loyalty settings
  const saveLoyaltySettings = async () => {
    setLoyaltySaving(true);
    try {
      const token = localStorage.getItem('kangavena:cashierToken');
      const res = await fetch('/api/store/loyalty', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey, Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          loyaltyEnabled: loyaltyForm.enabled,
          pointsPerRupee: parseFloat(loyaltyForm.pointsPerRupee) || 0,
          rupeePerPoint: parseFloat(loyaltyForm.rupeePerPoint) || 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Failed to save loyalty settings', 'error'); return; }
      // Sync runtime config + form with server response
      const lc = { enabled: data.loyalty.loyaltyEnabled, pointsPerRupee: data.loyalty.pointsPerRupee, rupeePerPoint: data.loyalty.rupeePerPoint };
      setLoyaltyConfig(lc);
      setLoyaltyForm({ enabled: lc.enabled, pointsPerRupee: String(lc.pointsPerRupee), rupeePerPoint: String(lc.rupeePerPoint) });
      showToast('Loyalty settings saved!', 'success');
    } catch { showToast('Cannot reach server', 'error'); }
    finally { setLoyaltySaving(false); }
  };
  const [customFieldLabel, setCustomFieldLabel] = useState('Vehicle Number');
  const [customFieldPlaceholder, setCustomFieldPlaceholder] = useState('Enter Vehicle No');
  const [customFieldRequired, setCustomFieldRequired] = useState(false);
  // 🚗 Store type context — determines estimate form fields
  const [shopType, setShopType] = useState<string>('RETAIL');
  const isAutoParts = shopType === 'RENTAL' || shopType === 'OTHER' || shopType === 'AUTO_PARTS';
  // 🟠 Auto Parts: Apply orange brand theme
  useEffect(() => {
    if (shopType === 'AUTO_PARTS') {
      const root = document.documentElement;
      root.style.setProperty('--themeBrand-rgb', '249 115 22');
      root.style.setProperty('--brand-primary-rgb', '249 115 22');
      root.style.setProperty('--brand-secondary-rgb', '251 191 36');
      root.style.setProperty('--brand-surface-rgb', '255 247 237');
      root.style.setProperty('--brand-text-on-primary-rgb', '255 255 255');
    }
  }, [shopType]);
  // 🛡️ Phase 4 — RBAC state
  const [cashier, setCashier] = useState<CashierSession | null>(null);
  const [cashierAuthOpen, setCashierAuthOpen] = useState(false);
  const [sessionExpiring, setSessionExpiring] = useState(false);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  // 🔐 Phase 6 — Manager login-approval queue
  const [pendingLogins, setPendingLogins] = useState<any[]>([]);
  const [showApprovalPanel, setShowApprovalPanel] = useState(false);
  const pendingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours
  // 🕐 Phase 7 — Active shift state
  const [currentShift, setCurrentShift] = useState<{ id: number; startedAt: string; status: string } | null>(null);
  const [shiftLoading, setShiftLoading] = useState(false);
  // Hold bills
  const [heldBills, setHeldBills] = useState<HeldBill[]>([]);
  const [heldBillsOpen, setHeldBillsOpen] = useState(false);
  // Reorder Sheet State
  const [reorderSheet, setReorderSheet] = useState<string | null>(null);
  // Daily stats
  const [todaySales, setTodaySales] = useState<SaleStat[]>([]);
  // 📅 Analytics date scope — drives Live Pulse / Analytics / Transactions tabs (defaults to today)
  const [analyticsDate, setAnalyticsDate] = useState<string>(() => {
    const now = new Date();
    const lk = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Colombo' }));
    const y = lk.getFullYear(), m = String(lk.getMonth() + 1).padStart(2, '0'), d = String(lk.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  // 📋 Historical Transactions Audit States
  const [historicalTxs, setHistoricalTxs] = useState<any[]>([]);
  const [historicalCount, setHistoricalCount] = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage] = useState(0);
  const [txQuery, setTxQuery] = useState('');
  const [txDate, setTxDate] = useState('');
  // 🧾 Receipt barcode scanner — lookup scanned receipt transactions
  const [receiptScanOpen, setReceiptScanOpen] = useState(false);
  const [scannedTx, setScannedTx] = useState<any>(null);
  const [scannedTxLoading, setScannedTxLoading] = useState(false);
  const [scannedTxError, setScannedTxError] = useState('');
  // 🔍 Accessibility: Global UI Scale (for elderly/poor eyesight users)
  const [uiScale, setUiScale] = useState(100);
  const [uiScaleOpen, setUiScaleOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isGridExpanded, setIsGridExpanded] = useState(false);
  const [showMobileHeader, setShowMobileHeader] = useState(true);
  const [forceDesktop, setForceDesktop] = useState(() => localStorage.getItem('kangavena_force_desktop') === '1');
  const lastScrollY = useRef(0);
  const productScrollRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Race condition guard: holds raw products when /api/inventory resolves before
  // /api/stores has committed storeId to Layer 1 (only possible on first-ever session)
  const pendingInventoryRef = useRef<any[] | null>(null);

  const onProductScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const y = e.currentTarget.scrollTop;
    if (y > lastScrollY.current + 8) setShowMobileHeader(false);
    else if (y < lastScrollY.current - 8) setShowMobileHeader(true);
    lastScrollY.current = y;
  }, []);

  const showToast = (msg: string, type: 'success' | 'error' | 'undo' = 'success', onUndo?: () => void) => {
    // Clear any existing undo timeout
    if (undoTimeoutRef.current) {
      clearTimeout(undoTimeoutRef.current);
      undoTimeoutRef.current = null;
    }
    setToast({ msg, type, onUndo });
    // Auto-dismiss after 5 seconds for undo toasts, 3.2s for others
    const duration = type === 'undo' ? 5000 : 3200;
    undoTimeoutRef.current = setTimeout(() => setToast(null), duration);
  };

  // 🎯 Fuzzy Search Algorithm — character sequence matching
  const fuzzyMatch = (query: string, target: string): number => {
    const q = (query || '').toLowerCase().replace(/\s/g, '');
    const t = (target || '').toLowerCase().replace(/\s/g, '');
    if (q.length === 0) return 1;
    if (t.length === 0) return 0;
    
    let qIdx = 0, tIdx = 0, matches = 0;
    while (qIdx < q.length && tIdx < t.length) {
      if (q[qIdx] === t[tIdx]) {
        matches++;
        qIdx++;
      }
      tIdx++;
    }
    // Score: matches / query length (1.0 = perfect match)
    return matches / q.length;
  };

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Persist forceDesktop preference + toggle viewport for true desktop layout
  useEffect(() => {
    localStorage.setItem('kangavena_force_desktop', forceDesktop ? '1' : '0');
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      if (forceDesktop) {
        viewport.setAttribute('content', 'width=1280, initial-scale=0.35, minimum-scale=0.35, maximum-scale=3');
      } else {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5');
      }
    }
    return () => {
      const vp = document.querySelector('meta[name="viewport"]');
      if (vp) vp.setAttribute('content', 'width=device-width, initial-scale=1.0');
    };
  }, [forceDesktop]);

  // 🔍 Accessibility: Load saved UI scale on mount
  useEffect(() => {
    const saved = localStorage.getItem('kangavena_ui_scale');
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= 80 && parsed <= 150) {
        setUiScale(parsed);
      }
    }
  }, []);

  // 🔍 Accessibility: Apply UI scale whenever it changes
  useEffect(() => {
    // This scales ALL Tailwind classes using 'rem' (text-sm, p-4, w-10, etc.)
    document.documentElement.style.fontSize = `${uiScale}%`;
    localStorage.setItem('kangavena_ui_scale', uiScale.toString());

    // Debounce DB save (skip offline — localStorage is the source of truth)
    const timer = setTimeout(() => {
      if (!navigator.onLine) return;
      fetch('/api/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(storeApiKey ? { 'x-store-api-key': storeApiKey } : {}),
        },
        body: JSON.stringify({ uiScale })
      }).catch(() => { /* silent fail - localStorage is backup */ });
    }, 1000);

    return () => clearTimeout(timer);
  }, [uiScale]);

  const fetchInventory = useCallback(async (retryCount = 0) => {
    // Hard offline guard: bypass ALL network I/O and go straight to the storeId-isolated local cache.
    if (!navigator.onLine) {
      readCache();
      return;
    }
    if (!storeApiKey) {
      readCache();
      return;
    }
    try {
      console.log('[fetchInventory] storeApiKey:', storeApiKey ? 'present' : 'MISSING');
      const res = await fetch(`/api/inventory?apiKey=${encodeURIComponent(storeApiKey)}`, {
        headers: { 'x-store-api-key': storeApiKey }
      });
      if (!res.ok) {
        console.error(`[fetchInventory] HTTP ${res.status}: ${res.statusText}`);
        readCache();
        // If server is restarting (503 Service Unavailable) or temporary error, auto-retry up to 3 times
        if ((res.status === 503 || res.status === 502 || res.status === 504) && retryCount < 4) {
          setTimeout(() => {
            fetchInventory(retryCount + 1);
          }, 1500);
        }
        return;
      }
      const data = await res.json();
      const products: any[] = data.inventory || [];
      setInventory(mapProducts(products));
      // Eager write-through under storeId-isolated key
      if (!writeCache(products)) {
        // storeId not yet committed (first-ever session) — defer write until it arrives
        pendingInventoryRef.current = products;
      }
    } catch (err) {
      // Network failure — hydrate from storeId-isolated local cache and retry if temporary network blip
      readCache();
      if (retryCount < 4) {
        setTimeout(() => {
          fetchInventory(retryCount + 1);
        }, 1500);
      }
    }
  }, [storeApiKey, writeCache, readCache]); // mapProducts is module-level stable, setInventory is a stable setter

  // 🎨 Initialize FastAverageColor for logo theming
  useEffect(() => {
    facRef.current = new FastAverageColor();
    return () => {
      facRef.current = null;
    };
  }, []);

  useEffect(() => {
    // Skip entirely when offline — storeId is resolved from session cache by resolveStoreId()
    // in useProductCache.  When connectivity returns the 'online' re-fetch below will
    // repopulate inventory and the stores effect will re-run if storeApiKey becomes available.
    if (!navigator.onLine) return;
    if (!storeApiKey) { console.log('[fetchStores] No storeApiKey, skipping'); return; }
    // CRITICAL FIX: Use /api/my-stores (requires x-store-api-key) instead of /api/stores (admin-only)
    fetch('/api/my-stores', { headers: { 'x-store-api-key': storeApiKey } })
      .then(r => {
        if (!r.ok) {
          console.error(`[fetchStores] HTTP ${r.status}: ${r.statusText}`);
          return null;
        }
        return r.json();
      })
      .then((stores: any[] | null) => {
        if (!stores) return;
        const s = stores.find((st: any) => st.apiKey === storeApiKey);
        if (!s) return;
        setStoreId(String(s.id));
        commitStoreId(String(s.id));
        if (s.shopType) setShopType(s.shopType);
        if (s.customFieldLabel) setCustomFieldLabel(s.customFieldLabel);
        if (s.customFieldPlaceholder) setCustomFieldPlaceholder(s.customFieldPlaceholder);
        setCustomFieldRequired(!!s.customFieldRequired);
        // Flush deferred inventory write (guards first-ever-session /api/stores race)
        if (pendingInventoryRef.current) {
          writeCache(pendingInventoryRef.current);
          pendingInventoryRef.current = null;
        }
        setProfileForm({
          name: s.name || storeName,
          phone: s.phone || '',
          managerWhatsapp: s.managerWhatsapp || '',
          address: s.address || '',
          latitude: s.latitude != null ? String(s.latitude) : '',
          longitude: s.longitude != null ? String(s.longitude) : '',
          website: s.website || '',
          email: s.email || '',
        });
        setPaMode(s.isPersonalAssistantMode === true);
        setIsLunaIndexed(s.isLunaIndexed !== false);
        setShowLogoOnBill(!!s.showLogoOnBill);
        if (s.hidePoweredByOnBill !== undefined) {
          setHidePoweredByOnBill(!!s.hidePoweredByOnBill);
          localStorage.setItem('kangavena:hidePoweredByOnBill', String(!!s.hidePoweredByOnBill));
        }
        if (s.receiptTemplate) {
          setReceiptTemplate(s.receiptTemplate);
          localStorage.setItem('kangavena:receiptTemplate', s.receiptTemplate);
        }
        // 🎨 Load dynamic theming data (Multi-Color Brand Palette)
        setStoreLogo(s.storeLogo || null);
        setLogoThemeEnabled(s.logoThemeEnabled || false);
        const loadedPalette = s.themePalette || null;
        setThemePalette(loadedPalette);
        // Apply theme immediately
        applyThemeColor(loadedPalette, s.logoThemeEnabled);
        // 🟠 Auto Parts: Override to orange theme regardless of logo palette
        if (s.shopType === 'AUTO_PARTS') {
          const root = document.documentElement;
          root.style.setProperty('--themeBrand-rgb', '249 115 22');
          root.style.setProperty('--brand-primary-rgb', '249 115 22');
          root.style.setProperty('--brand-secondary-rgb', '251 191 36');
          root.style.setProperty('--brand-surface-rgb', '255 247 237');
          root.style.setProperty('--brand-text-on-primary-rgb', '255 255 255');
        }
        // 🎯 Phase 3 — Loyalty config (defaults if backend hasn't synced yet)
        const lc = {
          enabled: s.loyaltyEnabled !== false,
          pointsPerRupee: s.pointsPerRupee != null ? parseFloat(s.pointsPerRupee) : 0.01,
          rupeePerPoint: s.rupeePerPoint != null ? parseFloat(s.rupeePerPoint) : 0.5,
        };
        setLoyaltyConfig(lc);
        setLoyaltyForm({ enabled: lc.enabled, pointsPerRupee: String(lc.pointsPerRupee), rupeePerPoint: String(lc.rupeePerPoint) });
      })
      .catch(() => setProfileForm(f => ({ ...f, name: storeName })));
  }, [storeApiKey, storeName]);

  const handleTogglePaMode = async () => {
    if (!storeId) return;
    const newValue = !paMode;
    setPaModeSaving(true);
    setPaMode(newValue);
    try {
      const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch(`/api/stores/${storeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {}),
        },
        body: JSON.stringify({ isPersonalAssistantMode: newValue }),
      });
      if (!res.ok) setPaMode(!newValue);
    } catch {
      setPaMode(!newValue);
    } finally {
      setPaModeSaving(false);
    }
  };

  const handleToggleLunaIndexed = async () => {
    if (!storeId) return;
    const newValue = !isLunaIndexed;
    setIsLunaIndexed(newValue);
    try {
      const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch(`/api/stores/${storeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {}),
        },
        body: JSON.stringify({ isLunaIndexed: newValue }),
      });
      if (!res.ok) setIsLunaIndexed(!newValue);
      else showToast(newValue ? 'Store visible on Luna AI' : 'Store hidden from Luna AI', 'success');
    } catch {
      setIsLunaIndexed(!newValue);
    }
  };


  const updateStoreProfile = async () => {
    if (!storeId) { setProfileError('Store ID not loaded yet — please wait.'); return; }
    setProfileSaving(true); setProfileError('');
    try {
      const body: any = {
        name: profileForm.name.trim() || undefined,
        phone: profileForm.phone.trim() || null,
        // 🛡️ NEVER send null for managerWhatsapp — that would clear the number!
        // If empty, omit from body entirely so backend doesn't touch it.
        managerWhatsapp: (profileForm as any).managerWhatsapp?.trim() || undefined,
        address: profileForm.address.trim() || null,
        latitude: profileForm.latitude !== '' ? parseFloat(profileForm.latitude) : null,
        longitude: profileForm.longitude !== '' ? parseFloat(profileForm.longitude) : null,
        website: profileForm.website.trim() || null,
        email: profileForm.email.trim() || null,
        // 🎨 Dynamic theming fields (Multi-Color Brand Palette)
        storeLogo: storeLogo || null,
        logoThemeEnabled,
        themePalette: themePalette || null,
        showLogoOnBill,
        receiptTemplate,
      };
      const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch(`/api/stores/${storeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setProfileError(data.error || 'Failed to save'); return; }
      navigateTo('pos');
      showToast('Store profile updated!', 'success');
    } catch { setProfileError('Cannot reach server'); }
    finally { setProfileSaving(false); }
  };

  const handleSaveReceiptTemplate = async (templateId: string) => {
    setReceiptTemplate(templateId);
    localStorage.setItem('kangavena:receiptTemplate', templateId);
    if (storeId) {
      const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
      try {
        await fetch(`/api/stores/${storeId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-store-api-key': storeApiKey,
            ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {})
          },
          body: JSON.stringify({ receiptTemplate: templateId }),
        });
      } catch {}
    }
  };

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    if (storeApiKey) {
      fetchCashiers();
    }
  }, [storeApiKey]);

  // Sync hardware back button → close bill drawer
  useEffect(() => {
    const onPop = () => {
      if (window.location.hash !== '#bill') setShowBill(false);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Re-sync inventory silently when connectivity is restored.
  // fetchInventory already writes the fresh catalog to the storeId-isolated cache
  // so subsequent offline boots also get the latest data.
  useEffect(() => {
    window.addEventListener('online', fetchInventory);
    return () => window.removeEventListener('online', fetchInventory);
  }, [fetchInventory]);

  // 🛡️ Phase 1 — Subscribe to OfflineQueue for tri-state pill + pending count
  useEffect(() => {
    const unsub = OfflineQueue.subscribe((status, pending, failed) => {
      setOfflineStatus(status);
      setPendingTxCount(pending);
      setFailedTxCount(failed);
    });
    return () => { unsub(); };
  }, []);

  // Manual sync trigger — called when user clicks the Pending badge
  const handleManualSync = async () => {
    if (offlineStatus === 'syncing' || !navigator.onLine) return;
    const result = await OfflineQueue.flush();
    if (result.synced > 0 || result.purged > 0) {
      const parts: string[] = [];
      if (result.synced > 0) parts.push(`${result.synced} synced`);
      if (result.purged > 0) parts.push(`${result.purged} cleared`);
      setToast({ msg: `✓ ${parts.join(', ')}`, type: 'success' });
    } else if (result.failed > 0) {
      setToast({ msg: `Sync failed — ${result.failed} transaction${result.failed !== 1 ? 's' : ''} pending retry`, type: 'error' });
    }
  };

  // 🛡️ Phase 4/5 — Restore cashier session from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) return;

    // 🛡️ Phase 5 fix — On the admin path, a stale cashier-role token from a prior
    // /pos/cashier session would silently downgrade the owner to 'cashier' and hide
    // manager UI. Detect this and purge before the API call so the owner sees the
    // permissive null-session (all buttons visible) until the manager JWT is in place.
    if (!isCashierMode) {
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload?.role && payload.role !== 'manager') {
            // Stale non-manager token on the admin path — evict it.
            localStorage.removeItem('kangavena:cashierToken');
            return;
          }
        }
      } catch { /* malformed token — let the API call reject it */ }
    }

    // CRITICAL FIX: On the admin route, ALWAYS synthesize the manager cashier
    // immediately so the "SIGN IN" button never flashes. Then validate async.
    if (!isCashierMode) {
      try {
        const raw = localStorage.getItem('pos_admin_session');
        const s = raw ? JSON.parse(raw) : {};
        setCashier({
          id: 0,
          storeId: Number(s?.storeId ?? 0),
          username: cashierName,
          name: cashierName,
          role: 'manager',
          roleLabel: 'Manager',
        });
        setLastActivity(Date.now());
      } catch { /* malformed — manager badge acceptable fallback */ }
    }

    // Offline guard: trust the stored token without a network round-trip.
    // Firing /api/cashier/me while offline causes two separate failure modes:
    //   (a) Full offline (ERR_INTERNET_DISCONNECTED) → .catch() fires, token kept — safe.
    //   (b) Partial connectivity: local server reachable but returns 500 (DB down) or any
    //       non-{cashier:...} JSON → the old else-branch cleared the token even though
    //       the token is valid.  That is the root cause of the offline session wipe.
    if (!navigator.onLine) {
      if (isCashierMode) {
        // Cashier path: pos_cashier_session.cashier has the exact same shape
        // as the /api/cashier/me response body — use it directly.
        try {
          const raw = localStorage.getItem('pos_cashier_session');
          if (raw) {
            const s = JSON.parse(raw);
            if (s?.cashier) setCashier(s.cashier);
          }
        } catch { /* malformed session — leave cashier null, Sign In button acceptable */ }
      }
      return;
    }

    // Validate token with backend. For admin route we already synthesized above;
    // this async call only upgrades or evicts (401) the cashier state.
    fetch('/api/cashier/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        // Evict ONLY on an explicit 401 — that is the server's unambiguous signal
        // that the token is cryptographically invalid or expired.
        // A 500 (DB restart), 502 (proxy down), or any other error must NOT log
        // the cashier out — it is a transient infrastructure failure, not an auth failure.
        if (r.status === 401) {
          localStorage.removeItem('kangavena:cashierToken');
          setCashier(null);
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then((data) => {
        if (data?.cashier) {
          setCashier(data.cashier);
          setLastActivity(Date.now());
        }
        // Intentional: no else-branch. For admin route the synthesized manager cashier
        // remains in place on non-401 errors. For cashier route the offline synthesis
        // already handled the !navigator.onLine case; online non-401 errors are
        // transient and should not wipe the session.
      })
      .catch(() => {
        // Network error — keep synthesized cashier (admin) or cached session (cashier).
      });
  }, [isCashierMode]);

  // Synchronize selectedCashier with cashier state or cashierName prop
  useEffect(() => {
    if (cashier) {
      setSelectedCashier({ id: cashier.id, name: cashier.name });
    } else {
      setSelectedCashier({ id: 0, name: cashierName || 'Manager' });
    }
  }, [cashier, cashierName]);

  // 🛡️ Phase 4 — Activity tracker (touch, key, click) resets session timer
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    const onActivity = () => setLastActivity(Date.now());
    events.forEach((e) => document.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => document.removeEventListener(e, onActivity));
  }, []);

  // 🛡️ Phase 4 — Session expiry check every minute
  useEffect(() => {
    const id = setInterval(() => {
      if (!cashier) return;
      const idle = Date.now() - lastActivity;
      if (idle > SESSION_TIMEOUT_MS) {
        setSessionExpiring(true);
        // Auto-logout after grace period
        setTimeout(() => {
          setCashier(null);
          localStorage.removeItem('kangavena:cashierToken');
          setSessionExpiring(false);
        }, 30000); // 30s grace
      }
    }, 60000);
    return () => clearInterval(id);
  }, [cashier, lastActivity]);

  // 🔐 Phase 6 — Poll for pending cashier login approvals (manager only, every 8s)
  useEffect(() => {
    if (isCashierMode || !cashier) {
      setPendingLogins([]);
      if (pendingPollRef.current) { clearInterval(pendingPollRef.current); pendingPollRef.current = null; }
      return;
    }
    const fetchPending = async () => {
      const token = localStorage.getItem('kangavena:cashierToken');
      if (!token) return;
      try {
        const res = await fetch('/api/manager/login-requests', { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) { const d = await res.json(); setPendingLogins(d.requests || []); }
      } catch { /* non-fatal */ }
    };
    fetchPending();
    pendingPollRef.current = setInterval(fetchPending, 8000);
    return () => { if (pendingPollRef.current) { clearInterval(pendingPollRef.current); pendingPollRef.current = null; } };
  }, [isCashierMode, cashier?.id, setPendingLogins]);

  // 🕐 Phase 7 — Fetch active shift when cashier session is established
  useEffect(() => {
    if (cashier && cashier.id !== 0) {
      (async () => {
        const token = localStorage.getItem('kangavena:cashierToken');
        if (!token) return;
        try {
          const res = await fetch(`/api/shifts/current?storeApiKey=${encodeURIComponent(storeApiKey)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) { const d = await res.json(); setCurrentShift(d.shift || null); }
          else { setCurrentShift(null); }
        } catch { setCurrentShift(null); }
      })();
    } else {
      setCurrentShift(null);
    }
  }, [cashier?.id, cashier?.storeId, storeApiKey]);

  const filtered = useMemo(() => {
    const isAllCat = !activeCategory || activeCategory.toLowerCase() === 'all';
    const isAllModel = !activeBaseModel || activeBaseModel.toLowerCase() === 'all';

    let list = isAllCat
      ? inventory
      : inventory.filter(p => (p.category || '').trim().toLowerCase() === activeCategory.trim().toLowerCase());

    if (!isAllModel) {
      list = list.filter(p => (p.baseModel || '').trim().toLowerCase() === activeBaseModel.trim().toLowerCase());
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      
      // 🔍 Fuzzy match all products and score them
      const scored = list.map(p => {
        const nameScore = fuzzyMatch(q, p.name || '');
        const catScore = p.category ? fuzzyMatch(q, p.category) : 0;
        // Prefer name matches over category matches
        const score = Math.max(nameScore * 1.2, catScore);
        return { product: p, score };
      }).filter(item => item.score > 0.5); // Threshold: 50% match
      
      // Sort by score descending
      scored.sort((a, b) => b.score - a.score);
      return scored.map(s => s.product);
    }

    return list;
  }, [search, inventory, activeCategory, activeBaseModel]);

  const searchSuggestions = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    const isAllCat = !activeCategory || activeCategory.toLowerCase() === 'all';
    const isAllModel = !activeBaseModel || activeBaseModel.toLowerCase() === 'all';

    let list = isAllCat
      ? inventory
      : inventory.filter(p => (p.category || '').trim().toLowerCase() === activeCategory.trim().toLowerCase());

    if (!isAllModel) {
      list = list.filter(p => (p.baseModel || '').trim().toLowerCase() === activeBaseModel.trim().toLowerCase());
    }

    const scored = list.map(p => {
      const nameScore = fuzzyMatch(q, p.name || '');
      const catScore = p.category ? fuzzyMatch(q, p.category) : 0;
      const score = Math.max(nameScore * 1.2, catScore);
      return { product: p, score };
    }).filter(item => item.score >= 0.7);

    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 3).map(s => s.product);
  }, [search, inventory, activeCategory, activeBaseModel]);

  const categories: string[] = useMemo(() => ['All', ...(Array.from(new Set(inventory.map(p => p.category))) as string[]).filter(Boolean)], [inventory]);
  const baseModels: string[] = useMemo(() => ['All', ...(Array.from(new Set(inventory.map(p => p.baseModel).filter(Boolean))) as string[])], [inventory]);
  const catColor = (cat: string) => CATEGORY_COLORS[cat] || 'bg-purple-500/15 border-purple-500/30 text-purple-300';

  // Unit-aware step: Pieces use 1, weight/volume use 0.1
  const isWeightVolume = (p: Product) => !!p.unitType && p.unitType !== 'Pieces';
  const getQtyStep = (product: Product) => isWeightVolume(product) ? (product.unitType === 'ml' || product.unitType === 'Grams' ? 10 : 0.1) : 1;
  const getInitialQty = (product: Product) => isWeightVolume(product) ? (product.unitType === 'ml' ? 100 : product.unitType === 'Grams' ? 100 : 0.5) : 1;
  const getUnitLabel = (product: Product) => isWeightVolume(product) ? product.unitType! : '';

  // Price picker helper — shows picker and returns selected price
  const showPricePicker = (product: any): Promise<{ price: number; label: string } | null> => {
    return new Promise((resolve) => {
      if (!product.priceList || product.priceList.length === 0) {
        resolve(null);
        return;
      }
      setPricePickerProduct(product);
      setPricePickerOpen(true);
      setPricePickerResolve(() => resolve);
    });
  };

  const addToCart = async (product: Product) => {
    if (product.stockCount <= 0) { showToast(`${product.name} — Out of Stock`, 'error'); Haptics.fire('error'); return; }

    // 🏷️ Price picker — if product has multiple prices, let user choose
    let selectedPrice = product.price;
    let selectedPriceLabel = '';
    if ((product as any).priceList && (product as any).priceList.length > 0) {
      const picked = await showPricePicker(product);
      if (!picked) return; // User cancelled
      selectedPrice = picked.price;
      selectedPriceLabel = picked.label;
    }

    // 📦 Consume stock items — auto-remove expired, consume unexpired
    try {
      const res = await fetch(`/api/products/${product.id}/consume-stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
        body: JSON.stringify({ quantity: 1 }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.expiredRemoved > 0) {
          showToast(`🗑️ ${data.expiredRemoved} expired item${data.expiredRemoved > 1 ? 's' : ''} removed`, 'error');
        }
        if (data.consumed === 0) {
          showToast(`${product.name} — No unexpired stock left`, 'error');
          Haptics.fire('error');
          // Update stock count in local state
          setInventory(prev => prev.map(p => p.id === product.id ? { ...p, stockCount: data.stockCount } : p));
          return;
        }
        // Update stock count in local state
        setInventory(prev => prev.map(p => p.id === product.id ? { ...p, stockCount: data.stockCount } : p));
        // Update the product stockCount for cart validation
        product = { ...product, stockCount: data.stockCount };
      }
    } catch { /* silently continue if API fails — legacy mode */ }

    // Auto-dismiss receipt and start fresh bill if receipt showing
    if (lastTransaction) {
      setLastTransaction(null);
      setDiscount(0);
      setFlatDiscount(0);
      setShowDiscount(false);
      setSidebarPayMode(false);
      setCashAmount('');
      setMobileProvider('');
      setDrawerPayMode(false);
      setShowBill(false);
      SoundManager.play('ui_click');
      Haptics.fire('tap');
      const step = getQtyStep(product);
      setCart([{ product: selectedPrice !== product.price ? { ...product, price: selectedPrice } : product, qty: step, addedAt: Date.now(), warrantyPeriod: product.warrantyPeriod || '', returnPolicy: product.returnPolicy || '', originalPrice: selectedPrice !== product.price ? product.price : undefined, priceLabel: selectedPriceLabel || undefined }]);
      return;
    }
    SoundManager.play('ui_click');
    Haptics.fire('tap');
    
    // Resolve effective warranty & return policy (product override → category default)
    const cat = categoriesCache.find(c => c.id === product.categoryId);
    const effectiveWarranty = product.warrantyPeriod || cat?.warrantyPeriod || '';
    const effectiveReturnPolicy = product.returnPolicy || cat?.returnPolicy || '';

    setCart(prev => {
      const existing = prev.find(i => i.product.id === product.id && (i.priceLabel || '') === (selectedPriceLabel || '') && i.product.price === selectedPrice);
      const step = getQtyStep(product);
      if (existing) {
        if (existing.qty >= product.stockCount) { showToast('Maximum stock reached', 'error'); return prev; }
        const newQty = Math.round((existing.qty + step) * 1000) / 1000;
        return prev.map(i => (i.product.id === product.id && (i.priceLabel || '') === (selectedPriceLabel || '') && i.product.price === selectedPrice) ? { ...i, qty: Math.min(newQty, product.stockCount) } : i);
      }
      return [...prev, { 
        product: { ...product, price: selectedPrice }, 
        qty: getInitialQty(product),
        warrantyPeriod: effectiveWarranty,
        returnPolicy: effectiveReturnPolicy,
        originalPrice: selectedPrice !== product.price ? product.price : undefined,
        priceLabel: selectedPriceLabel || undefined,
      }];
    });
  };

  const updateQty = (productId: number, delta: number) => {
    SoundManager.play('ui_click');
    setCart(prev => prev.map(i => {
      if (i.product.id !== productId) return i;
      const step = getQtyStep(i.product);
      const newQty = Math.round((i.qty + delta * step) * 1000) / 1000;
      return { ...i, qty: Math.max(0, newQty) };
    }).filter(i => i.qty > 0));
  };

  const updateItemDiscount = useCallback((productId: number, discountAmt: number) => {
    SoundManager.play('ui_click');
    setCart(prev => prev.map(item =>
      item.product.id === productId
        ? { ...item, itemDiscount: discountAmt }
        : item
    ));
  }, []);

  const updateItemMarkup = useCallback((productId: number, markupAmt: number) => {
    SoundManager.play('ui_click');
    setCart(prev => prev.map(item =>
      item.product.id === productId
        ? { ...item, itemMarkup: markupAmt }
        : item
    ));
  }, []);

  const toggleDamageFlag = useCallback((productId: number) => {
    SoundManager.play('ui_click');
    setCart(prev => prev.map(item =>
      item.product.id === productId
        ? { ...item, damageFlag: !item.damageFlag }
        : item
    ));
  }, []);

  const updateItemSellPrice = useCallback((productId: number, newPrice: number | null) => {
    SoundManager.play('ui_click');
    setCart(prev => prev.map(item =>
      item.product.id === productId
        ? { ...item, adjustedPrice: newPrice !== null ? newPrice : undefined }
        : item
    ));
  }, []);

  // 🔧 Per-cart-item warranty/return policy override (cashier override at checkout)
  const [editingPolicyId, setEditingPolicyId] = useState<number | null>(null);
  const [editReturnPolicy, setEditReturnPolicy] = useState('');
  const [editWarrantyPeriod, setEditWarrantyPeriod] = useState('');

  // Open/close policy editor — initializes local editing state from cart item
  const openPolicyEditor = useCallback((productId: number, currentReturnPolicy?: string, currentWarrantyPeriod?: string) => {
    setEditingPolicyId(prev => prev === productId ? null : productId);
    setEditReturnPolicy(currentReturnPolicy || '');
    setEditWarrantyPeriod(currentWarrantyPeriod || '');
  }, []);

  // Confirm policy edit — commits local state to cart, then closes editor
  const confirmPolicyEdit = useCallback((productId: number) => {
    SoundManager.play('ui_click');
    setCart(prev => prev.map(item =>
      item.product.id === productId ? { ...item, returnPolicy: editReturnPolicy, warrantyPeriod: editWarrantyPeriod } : item
    ));
    setEditingPolicyId(null);
  }, [editReturnPolicy, editWarrantyPeriod]);

  // 💵 Get effective selling price (honors adjustedPrice override, then discount/markup)
  const getItemSellPrice = useCallback((item: CartItem): number => {
    if (item.adjustedPrice !== undefined && item.adjustedPrice !== null) {
      return item.adjustedPrice;
    }
    const basePrice = item.product.price;
    const discount = item.itemDiscount || 0;
    const markup = item.itemMarkup || 0;
    return Math.max(0, basePrice - discount + markup);
  }, []);

  const removeFromCart = (itemOrId: CartItem | number) => {
    SoundManager.play('ui_click');
    Haptics.fire('warn');
    
    // Handle both CartItem object and product ID (bulletproof)
    let item: CartItem | undefined;
    if (typeof itemOrId === 'object' && itemOrId !== null) {
      item = itemOrId as CartItem;
    } else {
      // It's an ID - find the item in cart
      const id = itemOrId as number;
      item = cart.find(i => i.product.id === id);
    }
    
    if (!item) return; // Safety: item not found
    
    // Store item for potential undo
    const removedItem = { ...item };
    
    // Immediately remove from cart
    setCart(prev => prev.filter(i => i.product.id !== item!.product.id));
    
    // Show undo toast
    showToast(
      `${item.product.name} removed`,
      'undo',
      () => {
        // UNDO: Restore item to cart
        setCart(prev => {
          const exists = prev.find(i => i.product.id === removedItem.product.id);
          if (exists) {
            // If somehow already back (edge case), just update qty
            return prev.map(i => i.product.id === removedItem.product.id ? removedItem : i);
          }
          return [...prev, removedItem];
        });
        showToast(`${removedItem.product.name} restored`, 'success');
      }
    );
  };

  const clearCart = () => {
    SoundManager.play('ui_click'); setCart([]); setDiscount(0); setFlatDiscount(0); setShowDiscount(false); setDismissedSuggestions(new Set());
    // 🎯 Phase 3 — also detach customer + reset redemption
    setCustomer(null); setPointsToRedeem(0); setStoreCreditToUse(0);
    upsellRef.current = { shown: false, accepted: false, item: null };
  };

  // ✨ Add the suggested item: find it in inventory (fuzzy match), push into cart, clear suggestion
  const handleAddSuggestedItem = (itemName: string) => {
    const match = inventory.find(p => p.name.toLowerCase().includes(itemName.toLowerCase()));
    if (match) {
      addToCart(match);
      setDismissedSuggestions(prev => new Set(prev).add(itemName));
      setAiSuggestion(null);
      upsellRef.current = { shown: true, accepted: true, item: itemName };
    } else {
      showToast(`Suggested item "${itemName}" not found in inventory`, 'error');
    }
  };

  const removeProduct = async (id: number) => {
    const ok = await confirmAction({
      title: 'Remove this product?',
      message: 'It will be removed from your inventory and current cart. This cannot be undone.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetch(`/api/products/${id}`, { method: 'DELETE', headers: { 'x-store-api-key': storeApiKey } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || 'Failed to remove product', 'error');
        return;
      }
    } catch { /* product may be local-only, still remove from UI */ }
    setInventory(prev => prev.filter(p => p.id !== id));
    setCart(prev => prev.filter(i => i.product.id !== id));
    showToast('Product removed', 'success');
  };

  const handleBulkDelete = async () => {
    if (selectedProductIds.length === 0) return;
    const ok = await confirmAction({
      title: `Delete ${selectedProductIds.length} products?`,
      message: 'Selected products will be permanently removed from inventory. This cannot be undone.',
      confirmLabel: 'Delete All',
      destructive: true,
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const res = await fetch('/api/products/bulk', {
        method: 'DELETE',
        headers: { 'x-store-api-key': storeApiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedProductIds }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || 'Failed to delete products', 'error');
        return;
      }
      setSelectedProductIds([]);
      showToast(`Deleted ${selectedProductIds.length} products`, 'success');
      await fetchInventory();
    } catch {
      showToast('Network error — could not delete products', 'error');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkLunaVisibility = async () => {
    if (selectedProductIds.length === 0 || !bulkLunaVisibility) return;
    setBulkVisLoading(true);
    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch('/api/products/bulk-visibility', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ids: selectedProductIds, visibility: bulkLunaVisibility }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || 'Failed to update visibility', 'error');
        return;
      }
      const data = await res.json();
      setSelectedProductIds([]);
      showToast(`Updated ${data.updatedCount} products → ${bulkLunaVisibility}`, 'success');
      await fetchInventory();
    } catch {
      showToast('Network error — could not update visibility', 'error');
    } finally {
      setBulkVisLoading(false);
    }
  };

  const fetchCashiers = async () => {
    if (!storeApiKey) {
      setCashiersError('Store context not ready — please wait');
      setCashiersLoading(false);
      return;
    }
    setCashiersLoading(true); setCashiersError('');
    try {
      const res = await fetch('/api/store/cashiers', { headers: { 'x-store-api-key': storeApiKey } });
      let data: any;
      try { data = await res.json(); } catch { setCashiersError('Server returned unexpected response — restart the dev server'); return; }
      if (!res.ok) { setCashiersError(data.error || 'Failed to load'); return; }
      setCashiersList(data.cashiers || []);
    } catch { setCashiersError('Cannot reach server — check that the server is running'); }
    finally { setCashiersLoading(false); }
  };

  // 💳 Phase 10 — Fetch payment reconciliation
  const fetchPaymentRecon = async () => {
    setPaymentReconLoading(true);
    try {
      const res = await fetch(`/api/payments/reconciliation?apiKey=${encodeURIComponent(storeApiKey)}`, {
        headers: { 'x-store-api-key': storeApiKey }
      });
      if (!res.ok) throw new Error('Failed to fetch payment reconciliation');
      const data = await res.json();
      setPaymentRecon(data);
    } catch (err: any) {
      console.error('Payment recon error:', err);
    } finally {
      setPaymentReconLoading(false);
    }
  };

  // 🕐 Phase 7 — Shift helpers
  const fetchCurrentShift = async () => {
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) return;
    try {
      const res = await fetch(`/api/shifts/current?storeApiKey=${encodeURIComponent(storeApiKey)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.shift) {
        setCurrentShift(data.shift);
      } else {
        setCurrentShift(null);
      }
    } catch { /* silent — shift is optional UX */ }
  };

  const startShift = async () => {
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) { showToast('Sign in to clock in', 'error'); return; }
    
    // Collect starting drawer float (Rs.)
    const startingCashStr = prompt("Enter Starting Drawer Cash Float (Rs.):", "5000");
    if (startingCashStr === null) return; // user cancelled clock-in
    const startingCash = parseFloat(startingCashStr) || 0;

    setShiftLoading(true);
    try {
      const res = await fetch('/api/shifts/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ storeApiKey, startingCash }),
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Clock in failed', 'error'); return; }
      setCurrentShift(data.shift);
      showToast('Clocked in · Shift active', 'success');
    } catch {
      showToast('Network error — check connection', 'error');
    } finally { setShiftLoading(false); }
  };

  // 🔐 Phase 6 — Approve / reject a pending cashier login request
  const approveLogin = async (reqId: number) => {
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) return;
    try {
      await fetch(`/api/manager/login-requests/${reqId}/approve`, { method: 'PATCH', headers: { Authorization: 'Bearer ' + token } });
      setPendingLogins(prev => prev.filter((r: any) => r.id !== reqId));
      showToast('Cashier login approved', 'success');
    } catch { showToast('Failed to approve login', 'error'); }
  };
  const rejectLogin = async (reqId: number) => {
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) return;
    try {
      await fetch(`/api/manager/login-requests/${reqId}/reject`, { method: 'PATCH', headers: { Authorization: 'Bearer ' + token } });
      setPendingLogins(prev => prev.filter((r: any) => r.id !== reqId));
      showToast('Cashier login rejected', 'success');
    } catch { showToast('Failed to reject login', 'error'); }
  };

  const endShift = async () => {
    if (!currentShift) return;
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) return;

    // Collect closing drawer cash count (Rs.)
    const closingCashStr = prompt("Enter Closing Drawer Cash Count (Rs.):");
    if (closingCashStr === null) return; // user cancelled clock-out
    const closingCashActual = parseFloat(closingCashStr) || 0;

    setShiftLoading(true);
    try {
      const res = await fetch(`/api/shifts/${currentShift.id}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Clock out failed', 'error'); return; }
      setCurrentShift(null);
      showToast(`Clocked out · ${data.totalHours != null ? data.totalHours.toFixed(1) + 'h' : ''}`, 'success');

      // Show gorgeous audit reconciliation modal
      const diff = data.shortOverAmount || 0;
      const statusText = diff === 0
        ? "Perfect Match! (හරියටම හරි!)"
        : diff > 0
          ? `Over by Rs. ${diff.toLocaleString()} (වැඩි මුදල්!)`
          : `Short by Rs. ${Math.abs(diff).toLocaleString()} (හිඟ මුදල්!)`;

      confirmAction({
        title: "Shift Closed & Reconciled!",
        message: `Expected Drawer Cash: Rs. ${data.closingCashExpected?.toLocaleString()}
Actual Drawer Cash Counted: Rs. ${closingCashActual.toLocaleString()}
Reconciliation Difference: ${statusText}

Total active hours worked: ${data.totalHours != null ? data.totalHours.toFixed(1) + 'h' : '—'}`,
        confirmLabel: "OK, Settle"
      });
    } catch {
      showToast('Network error — check connection', 'error');
    } finally { setShiftLoading(false); }
  };

  const addCashierAccount = async () => {
    if (!newCashier.name.trim() || !newCashier.username.trim() || !newCashier.password) {
      showToast('Name, username and password are required', 'error'); return;
    }
    setNewCashierAdding(true);
    try {
      const res = await fetch('/api/store/cashiers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeApiKey, ...newCashier }),
      });
      let data: any;
      try { data = await res.json(); } catch { showToast('Server error — restart dev server & run db:sync', 'error'); return; }
      if (!res.ok) { showToast(data.error || 'Failed to add cashier', 'error'); return; }
      setCashiersList(prev => [...prev, data.cashier]);
      setNewCashier({ name: '', username: '', password: '' });
      showToast(`${data.cashier.name} added as cashier`, 'success');
    } catch { showToast('Cannot reach server — check server is running', 'error'); }
    finally { setNewCashierAdding(false); }
  };

  const removeCashierAccount = async (id: number, name: string) => {
    const ok = await confirmAction({
      title: `Remove cashier "${name}"?`,
      message: 'They will lose access to this store immediately.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetch(`/api/store/cashiers/${id}`, { method: 'DELETE', headers: { 'x-store-api-key': storeApiKey } });
      if (!res.ok) { showToast('Failed to remove cashier', 'error'); return; }
      setCashiersList(prev => prev.filter(c => c.id !== id));
      showToast(`${name} removed`, 'success');
    } catch { showToast('Cannot reach server', 'error'); }
  };

  const handleProductAdded = useCallback((product: Product) => {
    setInventory(prev => [product, ...prev]);
    showToast(`${product.name} added to inventory`, 'success');
  }, []);

  // 🎯 Phase 3 — Detach customer
  const detachCustomer = () => {
    setCustomer(null);
    setPointsToRedeem(0);
    setStoreCreditToUse(0);
    Haptics.fire('warn');
  };

  const subtotal = cart.reduce((s, i) => s + getItemSellPrice(i) * i.qty, 0);
  const discountAmt = Math.round(subtotal * (discount / 100)) + flatDiscount;
  const coreChargesTotal = 0;
  // 🎯 Phase 3 — Loyalty redemption discount
  const redemptionValue = +(pointsToRedeem * loyaltyConfig.rupeePerPoint).toFixed(2);
  const totalBeforeRedemption = subtotal - discountAmt + coreChargesTotal;
  // 💰 Store Credit — cap at available balance and remaining bill
  const maxStoreCredit = customer ? Math.min(Number(customer.storeCredit || 0), totalBeforeRedemption - redemptionValue) : 0;
  const effectiveStoreCredit = Math.max(0, Math.min(storeCreditToUse, maxStoreCredit));
  const grandTotal = Math.max(0, +(totalBeforeRedemption - redemptionValue - effectiveStoreCredit).toFixed(2));
  // Estimated points earned on this bill (for live preview only)
  const estimatedPointsEarn = loyaltyConfig.enabled && customer
    ? +(grandTotal * loyaltyConfig.pointsPerRupee).toFixed(2)
    : 0;
  const itemCount = cart.reduce((s, i) => s + i.qty, 0);

  // Track whether a suggestion was ever shown for this bill (for upsell analytics)
  useEffect(() => {
    if (aiSuggestion) upsellRef.current.shown = true;
  }, [aiSuggestion]);

  // ✨ Intelligent Luna Upsell Suggestions Engine
  // Analyzes cart items against actual in-stock store inventory. Never suggests out-of-stock or missing items.
  useEffect(() => {
    if (!enableLunaSuggestions || cart.length === 0) {
      setAiSuggestion(null);
      return;
    }

    const cartProductIds = new Set(cart.map(i => i.product.id));
    const cartCategories = new Set(cart.map(i => i.product.category.toLowerCase()));

    // Filter store's actual in-stock inventory items that are NOT already in the cart
    const availableInventory = inventory.filter(p => p.stockCount > 0 && !cartProductIds.has(p.id));
    if (availableInventory.length === 0) {
      setAiSuggestion(null);
      return;
    }

    // 1. Try to find a complementary in-stock item from a category matching current cart items
    const sameCategoryCandidates = availableInventory.filter(p => cartCategories.has(p.category.toLowerCase()) && !dismissedSuggestions.has(p.name));
    
    let candidate: Product | null = null;
    let reason = '';

    if (sameCategoryCandidates.length > 0) {
      candidate = sameCategoryCandidates[0];
      reason = `Recommended add-on from ${candidate.category} to pair with your bill!`;
    } else {
      // 2. Otherwise pick the top available in-stock item in the store that hasn't been dismissed
      const undismissed = availableInventory.filter(p => !dismissedSuggestions.has(p.name));
      if (undismissed.length > 0) {
        candidate = undismissed[0];
        reason = `Popular in-stock item available in ${storeName || 'store'}!`;
      }
    }

    if (candidate) {
      setAiSuggestion({
        item: candidate.name,
        reason: reason
      });
    } else {
      setAiSuggestion(null);
    }
  }, [cart, inventory, dismissedSuggestions, enableLunaSuggestions, storeName]);

  // ── Barcode input from hardware scanner (keyboard wedge)
  // Works from ANY page — no need to click search bar.
  // Only fires on FAST keystrokes (scanner: <200ms between chars).
  // Slow human typing is ignored by this listener.
  const barcodeBufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const handleBarcodeInputRef = useRef<(code: string) => void>(() => {});
  const scannerProcessedRef = useRef(false); // flag to prevent search box double-fire
  const lastProcessedBarcodeRef = useRef<{ code: string; time: number }>({ code: '', time: 0 });
  
  const handleBarcodeInput = useCallback(async (code: string) => {
    const trimmedCode = code.trim();
    // Debounce: skip if same barcode processed within last 500ms (prevents double-add from dual listeners)
    const now = Date.now();
    if (trimmedCode === lastProcessedBarcodeRef.current.code && now - lastProcessedBarcodeRef.current.time < 500) return;
    lastProcessedBarcodeRef.current = { code: trimmedCode, time: now };

    const found = inventory.find(p => p.barcode && p.barcode.trim() === trimmedCode);
    if (found) {
      addToCart(found);
      showToast(`Added: ${found.name}`, 'success');
      setSearch('');
      Haptics.fire('scan');
      SoundManager.play('product_select');
      triggerScanBurst({ label: `+ ${found.name}`, tone: 'success' });
    } else {
      // 🧾 Check if this is a receipt barcode (transaction ID) — fetch & display
      try {
        Haptics.fire('scan');
        const res = await fetch(`/api/transactions/${encodeURIComponent(trimmedCode)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.transaction) {
            const tx = data.transaction;
            // Build lastTransaction-compatible object from API response
            const txItems = (tx.items || tx.TransactionItems || []).map((item: any) => ({
              product: { id: item.productId, name: item.productName || item.name, price: item.unitPrice || item.price, emoji: item.emoji || '📦' },
              qty: item.quantity || item.qty,
              returnPolicy: item.returnPolicy,
              warrantyPeriod: item.warrantyPeriod,
            }));
            setLastTransaction({
              id: tx.txRef || tx.id,
              cart: txItems,
              subtotal: tx.subtotal,
              discountPct: tx.discountPct || 0,
              discountAmt: tx.discountAmt || 0,
              total: tx.grandTotal || tx.total,
              payments: tx.Payments || tx.payments || [],
              time: new Date(tx.createdAt),
              storeName: storeName || profileForm.name,
              storeAddress: profileForm.address,
              storePhone: profileForm.phone,
              storeLogo: profileForm.logo,
              customer: tx.customerName ? { name: tx.customerName, phone: tx.customerPhone, address: tx.customerAddress } : null,
            });
            showToast(`🧾 Receipt loaded: ${tx.txRef || tx.id}`, 'success');
            triggerScanBurst({ label: `🧾 ${tx.txRef || tx.id}`, tone: 'success' });
            return;
          }
        }
      } catch {
        // Not a transaction — fall through to add product
      }

      // 📱 Not found — open Add Product modal with barcode pre-filled
      setAddProductBarcodePrefill(code);
      setAddProductOpen(true);
      showToast(`New barcode "${code}" — add as product`, 'success');
      triggerScanBurst({ label: `📱 ${code}`, tone: 'success' });
    }
  }, [inventory, addToCart, setSearch, setAddProductBarcodePrefill, setAddProductOpen, storeName, profileForm]);

  // Keep ref in sync so the effect always calls the latest handleBarcodeInput
  useEffect(() => { handleBarcodeInputRef.current = handleBarcodeInput; }, [handleBarcodeInput]);

  // Global scanner listener — works from ANYWHERE in the POS
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Skip if typing in modal input fields (Add Product form etc.)
      if (addProductOpen) return;
      if (cameraOpen || lunaOpen) return;
      
      const now = Date.now();
      // If gap > 300ms, reset buffer (human typing or pause)
      if (now - lastKeyTimeRef.current > 300) barcodeBufferRef.current = '';
      lastKeyTimeRef.current = now;
      
      // Skip modifier keys and non-character keys (except Enter)
      if (e.key.length > 1 && e.key !== 'Enter') return;
      
      if (e.key === 'Enter') {
        const buffer = barcodeBufferRef.current.trim();
        barcodeBufferRef.current = '';
        if (buffer.length >= 3) {
          scannerProcessedRef.current = true; // flag search box to skip
          handleBarcodeInputRef.current(buffer);
          e.preventDefault(); // prevent search box from also processing
          setSearch('');
        }
        return;
      }
      
      // Buffer character (only printable chars)
      if (e.key.length === 1) {
        barcodeBufferRef.current += e.key;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addProductOpen, cameraOpen, lunaOpen, setSearch]);

  // Auto-disconnect scanner after 30s of no scans
  useEffect(() => {
    if (!scannerConnected) return;
    const timer = setInterval(() => {
      if (Date.now() - scannerLastScanRef.current > 30000) {
        setScannerConnected(false);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [scannerConnected]);

  // TODO: Implement Secure Backend Print Architecture
  // WebUSB printer detection removed — print agent integration torn down

  // TODO: Implement Secure Backend Print Architecture
  // handleConnectPrinter removed — print agent integration torn down

  // Toggle auto-print
  const toggleAutoPrint = useCallback(() => {
    setAutoPrintBill((prev: boolean) => {
      const next = !prev;
      localStorage.setItem('kangavena:autoPrint', String(next));
      showToast(next ? '🖨️ Auto Print ON' : '🖨️ Auto Print OFF', 'success');
      return next;
    });
  }, [showToast]);

  // 🖨️ Print via Smart Print Agent v7.2.0
  const printThermalReceipt = useCallback(async () => {
    if (!isAgentConfigured(storeApiKey)) {
      showToast('🖨️ Print Agent not configured — go to Settings → Printer Setup', 'error');
      return;
    }

    const tx = lastTransaction;
    const receiptData: ReceiptData = {
      storeName: tx ? tx.storeName : (storeName || profileForm.name || 'Your Store'),
      storeAddress: tx ? tx.storeAddress : (profileForm.address || ''),
      storePhone: tx ? tx.storePhone : (profileForm.phone || ''),
      date: tx ? tx.time.toLocaleDateString('en-LK') : new Date().toLocaleDateString('en-LK'),
      time: tx ? tx.time.toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' }),
      refId: tx ? tx.id : 'PENDING',
      cashierName: cashierName || undefined,
      customer: tx ? tx.customer : (customer ? { name: customer.name, phone: customer.phone, address: customer.address } : null),
      items: (tx ? tx.cart : cart).map((item: any) => ({
        name: item.product.name,
        qty: item.qty,
        price: item.product.price,
        total: item.product.price * item.qty,
        returnPolicy: item.returnPolicy,
        warrantyPeriod: item.warrantyPeriod,
      })),
      subtotal: tx ? tx.subtotal : subtotal,
      discountPct: tx ? tx.discountPct : discount,
      discountAmt: tx ? tx.discountAmt : discountAmt,
      grandTotal: tx ? tx.total : grandTotal,
      payments: tx ? (tx.payments || []) : [{ method: 'pending', amount: grandTotal }],
      isInvoice: !!tx,
    };

    const result = await printReceiptImage('kangavena-receipt-print', storeApiKey);
    if (result.success) {
      setPrinterConnected(true);
      showToast(`🖨️ Printed via ${result.printer || 'Agent'}`, 'success');
    } else {
      showToast(`🖨️ ${result.error || 'Print failed'}`, 'error');
    }
  }, [storeApiKey, lastTransaction, storeName, profileForm, cashierName, customer, cart, subtotal, discount, discountAmt, grandTotal, showToast]);

  // Auto-print after checkout (with duplicate guard)
  const lastPrintedTxRef = useRef<string | null>(null);
  const printThermalReceiptRef = useRef(printThermalReceipt);
  printThermalReceiptRef.current = printThermalReceipt;
  useEffect(() => {
    if (!autoPrintBill || !lastTransaction) return;
    const txId = lastTransaction.id || '';
    if (!txId) return;
    if (lastPrintedTxRef.current === txId) return;
    lastPrintedTxRef.current = txId;
    const timer = setTimeout(() => {
      if (lastPrintedTxRef.current === txId) {
        printThermalReceiptRef.current();
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [lastTransaction?.id, autoPrintBill]);

  // 💳 Phase 10 — Bridge PaymentMethodPicker output to existing checkout flow
  const handlePaymentConfirm = (config: any) => {
    if (config.splitDetails) {
      // Split payment
      processCheckout('split', { 
        primaryMethod: 'split', 
        splitDetails: config.splitDetails,
        creditAmount: config.creditAmount,
        creditPromiseDate: config.creditPromiseDate,
        creditStatus: config.creditStatus,
        brokerName: config.brokerName,
        brokerCommission: config.brokerCommission
      });
    } else {
      // Single payment — set legacy state vars then call existing checkout
      const method = config.method || 'cash';
      setPaymentMethod(method as any);
      if (method === 'cash' && config.metadata?.cashReceived) {
        setCashAmount(String(config.metadata.cashReceived));
      }
      if ((method === 'mobile' || method === 'qr_code') && config.provider) {
        setMobileProvider(config.provider);
      }
      processCheckout(method, {
        primaryMethod: method,
        splitDetails: null,
        creditAmount: config.creditAmount,
        creditPromiseDate: config.creditPromiseDate,
        creditStatus: config.creditStatus,
        brokerName: config.brokerName,
        brokerCommission: config.brokerCommission
      });
    }
  };

  const processCheckout = async (method: string = 'cash', splitOverride?: { primaryMethod: string; splitDetails: any; creditAmount?: number; creditPromiseDate?: string; creditStatus?: string; brokerName?: string; brokerCommission?: number }) => {
    if (cart.length === 0) return;
    if (!storeApiKey) {
      showToast('Store context not ready. Reconnecting…', 'error');
      return;
    }
    setIsProcessing(true);
    try {
      const txRef = `tx_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const effectiveMethod = splitOverride ? splitOverride.primaryMethod : (method === 'mobile' ? 'mobile' : method);
      const payload = {
        apiKey: storeApiKey,
        txRef,
        cashierName: selectedCashier?.name || cashierName,
        cashierId: selectedCashier?.id || null,
        cart: cart.map(i => ({
          productId: i.product.id,
          qty: i.qty,
          appliedCoreCharge: 0,
          itemDiscount: i.itemDiscount || 0,
          itemMarkup: i.itemMarkup || 0,
          damageFlag: i.damageFlag || false,
          adjustedPrice: i.adjustedPrice !== undefined ? i.adjustedPrice : null,
          productName: i.product.id < 0 ? i.product.name : undefined,
          unitPrice: i.product.id < 0 ? i.product.price : undefined
        })),
        subtotal,
        discountPct: discount,
        discountAmt,
        total: grandTotal,
        paymentMethod: (splitOverride && splitOverride.splitDetails) ? 'split' : effectiveMethod,
        cashTendered: (!(splitOverride && splitOverride.splitDetails) && method === 'cash') ? (parseFloat(cashAmount) || null) : null,
        changeGiven: (!(splitOverride && splitOverride.splitDetails) && method === 'cash' && parseFloat(cashAmount) >= grandTotal)
          ? parseFloat((parseFloat(cashAmount) - grandTotal).toFixed(2))
          : null,
        mobileProvider: (!(splitOverride && splitOverride.splitDetails) && method === 'mobile') ? mobileProvider : null,
        splitDetails: (splitOverride && splitOverride.splitDetails) ? splitOverride.splitDetails : null,
        // 💳 Credit/Debt options
        creditAmount: splitOverride?.creditAmount || 0,
        creditPromiseDate: splitOverride?.creditPromiseDate || null,
        creditStatus: splitOverride?.creditStatus || null,
        // 🤝 Broker / Middleman options
        brokerName: splitOverride?.brokerName || null,
        brokerCommission: splitOverride?.brokerCommission || 0,
        // 🎯 Phase 3 — loyalty
        customerId: customer ? customer.id : null,
        pointsRedeemed: pointsToRedeem > 0 ? pointsToRedeem : 0,
        pointsRedeemedValue: pointsToRedeem > 0 ? redemptionValue : 0,
        // 🚗 Auto Parts — Total core charges
        totalCoreCharges: coreChargesTotal,
        // 💰 Store Credit — amount applied from customer's store credit balance
        storeCreditApplied: effectiveStoreCredit > 0 ? effectiveStoreCredit : 0,
      };
      // 🛡️ Phase 1 — route through OfflineQueue: queues if offline / network fails
      const result = await OfflineQueue.submitCheckout(payload);
      if (result.ok === false) {
        throw new Error(result.error);
      }
      SoundManager.play('cha_ching');
      Haptics.fire('success');
      triggerScanBurst({ label: `✓ ${formatRs(grandTotal)}`, tone: 'success' });
      const transactionTime = new Date();
      const stat: SaleStat = {
        id: txRef,
        total: grandTotal,
        subtotal,
        discountPct: discount,
        discountAmt,
        items: itemCount,
        method: (splitOverride && splitOverride.splitDetails) ? 'split' : method,
        time: transactionTime,
        cashierName: selectedCashier?.name || cashierName,
        cart: cart.map(i => ({ product: i.product, qty: i.qty })),
        upsellShown: upsellRef.current.shown,
        upsellAccepted: upsellRef.current.accepted,
        upsellItem: upsellRef.current.item || undefined,
        splitDetails: (splitOverride && splitOverride.splitDetails) ? splitOverride.splitDetails : null,
      };
      setTodaySales(prev => [stat, ...prev].slice(0, 200));
      // 🧾 Set receipt data BEFORE clearing cart
      // 💳 Phase 10 — Build payment summary for receipt display
      const paymentSummary = splitOverride
        ? (splitOverride.splitDetails || []).map((leg: any) => ({
            method: leg.method || 'other',
            amount: parseFloat(leg.amount) || 0,
            provider: leg.provider || null,
            reference: leg.reference || null,
          }))
        : method === 'cash'
        ? [{ method: 'cash', amount: grandTotal, cashTendered: parseFloat(cashAmount || '0'), change: parseFloat(cashAmount || '0') >= grandTotal ? +(parseFloat(cashAmount || '0') - grandTotal).toFixed(2) : 0 }]
        : [{ method, amount: grandTotal, provider: mobileProvider || null }];

      const ptsEarned = (result as any).data?.customer?.pointsEarned || (customer ? Math.floor(grandTotal * 0.01) : 0);

      setLastTransaction({
        id: txRef,
        storeName: storeName || profileForm.name || 'Your Store',
        storeLogo,
        storeAddress: profileForm.address || '',
        storePhone: profileForm.phone || '',
        time: transactionTime,
        cart: [...cart],
        subtotal,
        discountPct: discount,
        discountAmt,
        total: grandTotal,
        method,
        cashierName,
        itemCount,
        payments: paymentSummary, // 💳 Phase 10 — granular payment breakdown
        customer: customer ? { ...customer } : null,
        pointsEarned: ptsEarned,
        creditAmount: splitOverride?.creditAmount || (method === 'credit' ? grandTotal : 0),
        creditPromiseDate: splitOverride?.creditPromiseDate || null,
      });
      const queuedOffline = (result as any).online === false;
      showToast(
        queuedOffline
          ? `📡 ${formatRs(grandTotal)} queued — will sync when online`
          : `✓ ${formatRs(grandTotal)} received — Ayubowan! 🙏`,
        'success'
      );
      // 🎯 Phase 3 — sync customer with server-returned loyalty state, then surface a points toast
      const updatedCustomer: any = (result as any).data?.customer || null;
      if (updatedCustomer) {
        setCustomer({
          ...(customer as any),
          ...updatedCustomer,
        });
        const earned = Number(updatedCustomer.pointsEarned) || 0;
        const redeemed = Number(updatedCustomer.pointsRedeemed) || 0;
        const creditUsed = Number(updatedCustomer.storeCreditUsed) || 0;
        if (earned > 0 || redeemed > 0) {
          showToast(
            `🏆 ${updatedCustomer.name}: ${earned > 0 ? `+${earned.toFixed(0)} pts` : ''}${earned > 0 && redeemed > 0 ? ' · ' : ''}${redeemed > 0 ? `−${redeemed.toFixed(0)} redeemed` : ''} → ${Number(updatedCustomer.points).toFixed(0)} balance`,
            'success'
          );
        }
        // 💰 Store credit toast
        if (effectiveStoreCredit > 0) {
          showToast(
            `💰 Rs. ${effectiveStoreCredit.toFixed(0)} store credit applied → Rs. ${Number(updatedCustomer.storeCredit || 0).toFixed(0)} remaining`,
            'success'
          );
        }
      }
      setCart([]); setDiscount(0); setFlatDiscount(0); setShowDiscount(false);
      setSidebarPayMode(false); setCashAmount(''); setMobileProvider('');
      setDrawerPayMode(false);
      setCustomer(null); setPointsToRedeem(0); setStoreCreditToUse(0);
      // Keep #bill view open to show receipt
      upsellRef.current = { shown: false, accepted: false, item: null };
      // Refresh inventory so grid card stock badges reflect decremented stockCount
      if (!queuedOffline) fetchInventory();
    } catch (err: any) {
      const msg = err?.message || 'Transaction failed';
      // JWT missing or expired — handle auth failure gracefully instead of generic toast
      if (msg.includes('Cashier authentication required')) {
        if (!isCashierMode) {
          showToast('Session expired. Redirecting to login…', 'error');
          setTimeout(() => { onExit?.(); }, 1500);
        } else {
          showToast('Sign in required to process payment.', 'error');
          setCashierAuthOpen(true);
        }
        return;
      }
      showToast(msg.includes('Product not found') ? `${msg} — Your inventory may need to sync. Try refreshing the page.` : `${msg}. Please retry.`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Double-Enter checkout confirmation ──
  const lastEnterTimeRef = useRef(0);
  // Auto-close mobile drawer after checkout so product grid is accessible
  useEffect(() => {
    if (lastTransaction && showBill) {
      const timer = setTimeout(() => { setShowBill(false); }, 800);
      return () => clearTimeout(timer);
    }
  }, [lastTransaction, showBill]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Only handle Enter when cart has items, not processing, no modals open
      if (e.key !== 'Enter') return;
      // If receipt showing (Start New Sale), dismiss it on Enter
      if (lastTransaction) {
        setLastTransaction(null);
        return;
      }
      if (cart.length === 0 || isProcessing || cameraOpen || lunaOpen || addProductOpen) return;
      // Don't intercept if user is typing in an input
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT')) return;
      
      const now = Date.now();
      const timeSinceLastEnter = now - lastEnterTimeRef.current;
      lastEnterTimeRef.current = now;
      
      if (enterConfirmStep === 0) {
        // First Enter → open payment panel
        setEnterConfirmStep(1);
        setSidebarPayMode(true);
        // Reset after 10 seconds if no second Enter
        setTimeout(() => setEnterConfirmStep(0), 10000);
      } else if (enterConfirmStep === 1 && timeSinceLastEnter < 8000) {
        // Second Enter → confirm payment
        setEnterConfirmStep(0);
        confirmPaymentRef.current();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cart, isProcessing, cameraOpen, lunaOpen, addProductOpen, enterConfirmStep, sidebarPayMode, lastTransaction, processCheckout]);

  const holdBill = () => {
    if (cart.length === 0) {
      setHeldBillsOpen(true);
      return;
    }
    const label = `Bill ${heldBills.length + 1} — ${now.toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}`;
    setHeldBills(prev => [...prev, { id: Date.now().toString(), label, items: [...cart], time: new Date() }]);
    clearCart();
    showToast('Bill held — restore from Hold Bills', 'success');
  };

  const restoreBill = async (id: string) => {
    const held = heldBills.find(b => b.id === id);
    if (!held) return;
    if (cart.length > 0) {
      const ok = await confirmAction({
        title: 'Replace current bill?',
        message: 'Your in-progress cart will be discarded and replaced with the held bill.',
        confirmLabel: 'Replace',
      });
      if (!ok) return;
    }
    setCart(held.items);
    setHeldBills(prev => prev.filter(b => b.id !== id));
    setHeldBillsOpen(false);
    showToast('Bill restored', 'success');
  };

  const confirmPayment = () => {
    processCheckout(paymentMethod === 'mobile' ? mobileProvider || 'Mobile' : paymentMethod);
  };
  useEffect(() => { confirmPaymentRef.current = confirmPayment; });

  // Always-fresh refs → stable Luna event handler
  const inventoryRef = useRef(inventory);
  const cartRef = useRef(cart);
  useEffect(() => { inventoryRef.current = inventory; }, [inventory]);
  useEffect(() => { cartRef.current = cart; }, [cart]);
  useEffect(() => {
    const handler = (e: Event) => {
      const { action, itemName } = (e as CustomEvent).detail || {};
      if (action === 'ADD_TO_BILL' && itemName) {
        const match = inventoryRef.current.find(p => p.name.toLowerCase().includes((itemName as string).toLowerCase()));
        match ? addToCart(match) : showToast(`Luna: "${itemName}" not found`, 'error');
      } else if (action === 'REMOVE_FROM_BILL' && itemName) {
        const match = cartRef.current.find(i => i.product.name.toLowerCase().includes((itemName as string).toLowerCase()));
        if (match) removeFromCart(match);
      } else if (action === 'CLEAR_BILL') { clearCart(); }
        else if (action === 'CHECKOUT') {
          if (window.innerWidth < 768) { openBill(); setDrawerPayMode(true); }
          else { setSidebarPayMode(true); }
        }
    };
    window.addEventListener('luna-pos-action', handler);
    return () => window.removeEventListener('luna-pos-action', handler);
  }, []);

  // Luna voice product hologram — open/close
  useEffect(() => {
    const handler = (e: Event) => {
      const { action } = (e as CustomEvent).detail || {};
      if (action === 'OPEN') setVoiceProductOpen(true);
      else if (action === 'CLOSE') setVoiceProductOpen(false);
    };
    window.addEventListener('luna-voice-product', handler);
    return () => window.removeEventListener('luna-voice-product', handler);
  }, []);

  // Luna live form open — for Fast Stock express inventory mode
  useEffect(() => {
    const handler = () => {
      setVoiceProductOpen(true);
    };
    window.addEventListener('luna-live-form-open', handler);
    return () => window.removeEventListener('luna-live-form-open', handler);
  }, []);

  // Luna express checkout — open from LunaVoiceModel trigger
  useEffect(() => {
    const handler = () => {
      setExpressCheckoutOpen(true);
    };
    window.addEventListener('luna-express-checkout-open', handler);
    return () => window.removeEventListener('luna-express-checkout-open', handler);
  }, []);

  // Luna bulk inventory — open modal with AI-parsed products
  useEffect(() => {
    const handler = (e: Event) => {
      const { products } = (e as CustomEvent).detail || {};
      if (products && Array.isArray(products) && products.length > 0) {
        setBulkInitialProducts(products);
        setBulkInventoryOpen(true);
      }
    };
    window.addEventListener('luna-bulk-inventory-open', handler);
    return () => window.removeEventListener('luna-bulk-inventory-open', handler);
  }, []);

  // ── VPN Heartbeat: re-check IP every 15s while Luna is open ──
  // Catches mid-session VPN activation. Force-closes Luna if VPN detected.
  useEffect(() => {
    if (!lunaOpen) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/luna/heartbeat');
        const data = await res.json();
        if (!data.allowed) {
          setLunaOpen(false);
          lunaRef.current?.forceKill();
        }
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [lunaOpen]);

  const todayTotal = todaySales.reduce((s, x) => s + x.total, 0);
  const [dailySummary, setDailySummary] = useState<{ todayNetProfit: number; todayRevenue: number; totalAllTimeRevenue: number; todaySalesCount: number; totalNetProfit: number; totalDebt?: number; totalUnpaidBroker?: number; totalStoreCredit?: number; storeCreditCustomers?: number } | null>(null);
  const [kpiTrends, setKpiTrends] = useState<{ revenuePct: number; salesPct: number; avgTicketPct: number } | null>(null);

  async function fetchTodaySales(dateStr?: string) {
    if (!storeApiKey) return;
    try {
      const _token = localStorage.getItem('kangavena:cashierToken') || '';
      const dateParam = dateStr || analyticsDate;
      const res = await fetch(`/api/reports/day-end?date=${encodeURIComponent(dateParam)}`, {
        headers: {
          'x-store-api-key': storeApiKey,
          ...(_token ? { 'Authorization': `Bearer ${_token}` } : {}),
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      const stats: SaleStat[] = (data.transactions || []).map((tx: any) => ({
        id: tx.txRef,
        total: tx.total,
        subtotal: tx.total,
        discountPct: 0,
        discountAmt: 0,
        items: tx.itemCount,
        method: tx.paymentMethod,
        time: new Date(tx.time),
        cashierName: tx.cashierName,
        cart: (tx.items || []).map((item: any) => ({
          product: {
            id: item.productId || 0,
            name: item.productName || 'Item',
            category: item.category || 'Unknown',
            price: parseFloat(item.unitPrice) || 0,
            stockCount: 0,
            emoji: '📦',
          },
          qty: parseFloat(item.qty) || 0,
        })),
        upsellShown: false,
        upsellAccepted: false,
        creditAmount: tx.creditAmount || 0,
        creditPaidAmount: tx.creditPaidAmount || 0,
        creditPromiseDate: tx.creditPromiseDate || null,
        creditStatus: tx.creditStatus || null,
        creditPaymentHistory: tx.creditPaymentHistory || null,
                customerId: tx.customer?.id || null,
        customer: tx.customer || null,
        brokerName: tx.brokerName || null,
        brokerCommission: tx.brokerCommission || 0,
        brokerStatus: tx.brokerStatus || null,
        brokerPaymentHistory: tx.brokerPaymentHistory || null,
        splitDetails: tx.splitDetails || null,
        storeCreditUsed: tx.storeCreditUsed || 0,
      }));
      setTodaySales(stats);
      const dsRes = await fetch('/api/reports/daily-summary', {
        headers: { 'x-store-api-key': storeApiKey },
      });
      if (dsRes.ok) setDailySummary(await dsRes.json());
    } catch (e) {
      console.error('Failed to fetch today sales:', e);
    }
  };

  async function fetchHistoricalTxs() {
    if (!storeApiKey) return;
    setTxLoading(true);
    try {
      const _token = localStorage.getItem('kangavena:cashierToken') || '';
      const url = `/api/reports/transactions?q=${encodeURIComponent(txQuery)}&date=${txDate}&limit=15&offset=${txPage * 15}`;
      const res = await fetch(url, {
        headers: {
          'x-store-api-key': storeApiKey,
          ...(_token ? { 'Authorization': `Bearer ${_token}` } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        // Map to same SaleStat format as fetchTodaySales
        const mapped: SaleStat[] = (data.transactions || []).map((tx: any) => ({
          id: tx.txRef,
          total: tx.total,
          subtotal: tx.total,
          discountPct: 0,
          discountAmt: 0,
          items: tx.itemCount,
          method: tx.paymentMethod,
          time: new Date(tx.time),
          cashierName: tx.cashierName,
          cart: (tx.items || []).map((item: any) => ({
            product: {
              id: item.productId || 0,
              name: item.productName || 'Item',
              category: item.category || 'Unknown',
              price: parseFloat(item.unitPrice) || 0,
              stockCount: 0,
              emoji: '📦',
            },
            qty: parseFloat(item.qty) || 0,
          })),
          upsellShown: false,
          upsellAccepted: false,
          creditAmount: tx.creditAmount || 0,
          creditPaidAmount: tx.creditPaidAmount || 0,
          creditPromiseDate: tx.creditPromiseDate || null,
          creditStatus: tx.creditStatus || null,
          creditPaymentHistory: tx.creditPaymentHistory || null,
                  customerId: tx.customer?.id || null,
        customer: tx.customer || null,
          brokerName: tx.brokerName || null,
          brokerCommission: tx.brokerCommission || 0,
          brokerStatus: tx.brokerStatus || null,
          brokerPaymentHistory: tx.brokerPaymentHistory || null,
          splitDetails: tx.splitDetails || null,
          storeCreditUsed: tx.storeCreditUsed || 0,
        }));
        setHistoricalTxs(mapped);
        setHistoricalCount(data.count || 0);
      }
    } catch (e) {
      console.error('Failed to fetch historical transactions:', e);
    } finally {
      setTxLoading(false);
    }
  }

  // Sync txDate with analyticsDate when on transactions tab
  useEffect(() => {
    if (analyticsTab === 'transactions') {
      setTxDate(analyticsDate);
    }
  }, [analyticsDate, analyticsTab]);

  // 🧾 Receipt barcode scanner handler — lookup transaction by scanned ID
  const handleReceiptScan = async (barcode: string) => {
    setReceiptScanOpen(false);
    setScannedTx(null);
    setScannedTxError('');
    setScannedTxLoading(true);
    try {
      const _token = localStorage.getItem('kangavena:cashierToken') || '';
      const res = await fetch(`/api/reports/transaction/${encodeURIComponent(barcode.trim())}`, {
        headers: {
          'x-store-api-key': storeApiKey,
          ...(_token ? { 'Authorization': `Bearer ${_token}` } : {}),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setScannedTx(data.transaction);
        // Switch to transactions tab to show the result
        setAnalyticsTab('transactions');
      } else if (res.status === 404) {
        setScannedTxError(`No transaction found for barcode: ${barcode}`);
      } else {
        setScannedTxError('Failed to look up transaction');
      }
    } catch {
      setScannedTxError('Network error — could not reach server');
    } finally {
      setScannedTxLoading(false);
    }
  };

  // Trigger historical fetch on tab/search/date/page change
  useEffect(() => {
    if (currentView === 'analytics' && analyticsTab === 'transactions') {
      fetchHistoricalTxs();
    }
  }, [currentView, analyticsTab, txQuery, txDate, txPage, storeApiKey]);

  // Auto-load payment reconciliation when Payments tab opens
  useEffect(() => {
    if (currentView === 'analytics' && analyticsTab === 'payments' && !paymentRecon && !paymentReconLoading) {
      fetchPaymentRecon();
    }
  }, [currentView, analyticsTab]);

  // Fetch today's transactions from DB on mount / when storeApiKey changes
  useEffect(() => {
    fetchTodaySales();
  }, [storeApiKey, currentView]);

  // Re-fetch when analytics opens to ensure fresh data, or when the selected date changes
  useEffect(() => {
    if (currentView !== 'analytics') return;
    fetchTodaySales(analyticsDate);
  }, [currentView, storeApiKey, analyticsDate]);

  // ⏱️ Auto-refresh Live Pulse every 60s when viewing live pulse tab (today only)
  useEffect(() => {
    if (currentView !== 'analytics' || analyticsTab !== 'pulse') return;
    // Only auto-refresh if viewing today's data
    const today = new Date();
    const lkToday = new Date(today.getTime() + 5.5 * 60 * 60 * 1000);
    const todayStr = lkToday.toISOString().slice(0, 10);
    if (analyticsDate !== todayStr) return;
    const interval = setInterval(() => fetchTodaySales(analyticsDate), 60000);
    return () => clearInterval(interval);
  }, [currentView, analyticsTab, analyticsDate, storeApiKey]);

  // 🔮 Manager Analytics — all memoised so the Command Center feels instant
  const analytics = React.useMemo(() => {
    const txs = todaySales;
    const hoursElapsed = Math.max(0.5, (Date.now() - sessionStart.getTime()) / 3_600_000);

    // Peak hour
    const hourBuckets: Record<number, { count: number; revenue: number }> = {};
    for (const t of txs) {
      const h = new Date(t.time).getHours();
      hourBuckets[h] = hourBuckets[h] || { count: 0, revenue: 0 };
      hourBuckets[h].count += 1;
      hourBuckets[h].revenue += t.total;
    }
    const hourEntries = Object.entries(hourBuckets).map(([h, v]) => ({ hour: Number(h), ...v }));
    const peak = hourEntries.slice().sort((a, b) => b.count - a.count)[0];

    // Top category (revenue share)
    const catRev: Record<string, number> = {};
    let totalRev = 0;
    for (const t of txs) {
      for (const ci of t.cart) {
        const rev = ci.product.price * ci.qty;
        catRev[ci.product.category] = (catRev[ci.product.category] || 0) + rev;
        totalRev += rev;
      }
    }
    const catRanking = Object.entries(catRev).map(([name, revenue]) => ({
      name, revenue, share: totalRev ? (revenue / totalRev) * 100 : 0,
    })).sort((a, b) => b.revenue - a.revenue);

    // Upsell success rate
    const upsellShownCount = txs.filter(t => t.upsellShown).length;
    const upsellAcceptedCount = txs.filter(t => t.upsellAccepted).length;
    const upsellRate = upsellShownCount ? (upsellAcceptedCount / upsellShownCount) * 100 : 0;

    // Per-product velocity & restock priority
    const sold: Record<number, number> = {};
    for (const t of txs) {
      for (const ci of t.cart) sold[ci.product.id] = (sold[ci.product.id] || 0) + ci.qty;
    }
    const inventoryHealth = inventory.map(p => {
      const velocity = (sold[p.id] || 0) / hoursElapsed; // units/hr
      const daysOfStock = velocity > 0 ? p.stockCount / (velocity * 24) : Infinity;
      const demand24h = velocity * 24;
      let priority: 'high' | 'medium' | 'low' = 'low';
      if (p.stockCount <= 0) priority = 'high';
      else if (velocity > 0 && p.stockCount < demand24h) priority = 'high';
      else if (velocity > 0 && p.stockCount < demand24h * 3) priority = 'medium';
      return { product: p, sold: sold[p.id] || 0, velocity, daysOfStock, priority };
    }).sort((a, b) => {
      const rank = (pr: 'high'|'medium'|'low') => pr === 'high' ? 0 : pr === 'medium' ? 1 : 2;
      return rank(a.priority) - rank(b.priority) || b.velocity - a.velocity;
    });

    return {
      txCount: txs.length,
      revenue: todayTotal,
      avgTicket: txs.length ? todayTotal / txs.length : 0,
      hourBuckets: hourEntries,
      peak,
      catRanking,
      upsellShownCount,
      upsellAcceptedCount,
      upsellRate,
      inventoryHealth,
      hoursElapsed,
    };
  }, [todaySales, inventory, todayTotal, sessionStart]);

  // Weekly trends — fetched lazily when analytics tab first opens (manager role required)
  const weeklyTrendsFetchedRef = React.useRef(false);
  // Reset when store changes so trends refetch for the new store
  React.useEffect(() => { weeklyTrendsFetchedRef.current = false; }, [storeApiKey]);
  React.useEffect(() => {
    if (analyticsTab !== 'analytics' || weeklyTrendsFetchedRef.current) return;
    weeklyTrendsFetchedRef.current = true;
    const token = localStorage.getItem('kangavena:cashierToken');
    if (!token) return;
    fetch('/api/analytics/sales-trends', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setWeeklyTrendsData(d))
      .catch(() => {});
  }, [analyticsTab]);

  // KPI trend percentages — fetch real period-over-period comparison
  const kpiTrendsFetchedRef = React.useRef(false);
  // Reset when store changes so KPI trends refetch for the new store
  React.useEffect(() => { kpiTrendsFetchedRef.current = false; setPaymentRecon(null); }, [storeApiKey]);
  React.useEffect(() => {
    if (currentView !== 'analytics' || kpiTrendsFetchedRef.current || !storeApiKey) return;
    kpiTrendsFetchedRef.current = true;
    fetch(`/api/analytics/period-comparison?apiKey=${storeApiKey}&period=1d`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d?.changes) return;
        setKpiTrends({
          revenuePct: d.changes.revenue || 0,
          salesPct: d.changes.transactions || 0,
          avgTicketPct: d.changes.avgTicket || 0,
        });
      })
      .catch(() => {});
  }, [currentView, storeApiKey]);

  // 🎯 Phase 3 — Customer pill (rendered above grand total in both panels)
  const customerPillJsx = !customer ? (
    <button
      onClick={() => { Haptics.fire('tap'); setCustomerPickerOpen(true); }}
      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-slate-300 hover:border-brand-primary text-slate-500 hover:text-brand-primary text-[10px] font-black uppercase tracking-widest transition-all"
    >
      <UserCircle2 size={12} /> Attach customer
    </button>
  ) : (
    <div className="rounded-xl bg-gradient-to-r from-amber-50 to-emerald-50 border border-amber-200 p-2.5">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-full bg-white border border-amber-300 flex items-center justify-center shrink-0">
          <UserCircle2 size={14} className="text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black text-slate-800 truncate leading-tight">{customer.name}</div>
          <div className="text-[9px] text-slate-500 font-mono">{customer.phone}</div>
        </div>
        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black">
          <Award size={9} /> {Number(customer.points).toFixed(0)}
        </div>
        {/* 💰 Store Credit badge */}
        {Number(customer.storeCredit) > 0 && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-black">
            💰 Rs. {Number(customer.storeCredit).toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </div>
        )}
        <button
          onClick={detachCustomer}
          className="w-6 h-6 rounded-md hover:bg-rose-100 text-slate-400 hover:text-rose-500 flex items-center justify-center transition-colors"
          aria-label="Detach customer"
          title="Remove customer from this bill"
        >
          <UserMinus size={11} />
        </button>
      </div>

      {/* Redemption controls */}
      {loyaltyConfig.enabled && Number(customer.points) > 0 && (
        <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-amber-200/60">
          <span className="text-[9px] font-bold uppercase tracking-widest text-amber-700 shrink-0">Redeem</span>
          <input
            type="number"
            min={0}
            max={Math.min(Number(customer.points), Math.floor(totalBeforeRedemption / loyaltyConfig.rupeePerPoint))}
            value={pointsToRedeem || ''}
            onChange={(e) => {
              const n = Math.max(0, parseInt(e.target.value || '0', 10) || 0);
              const maxByBalance = Math.floor(Number(customer.points));
              const maxByBill = Math.floor(totalBeforeRedemption / Math.max(loyaltyConfig.rupeePerPoint, 0.0001));
              setPointsToRedeem(Math.min(n, maxByBalance, maxByBill));
            }}
            placeholder="0"
            className="flex-1 min-w-0 px-2 py-1 rounded-md bg-white border border-amber-200 outline-none focus:border-amber-400 text-[11px] font-mono font-bold text-slate-800 text-right tabular-nums"
          />
          <span className="text-[9px] font-mono text-slate-500 shrink-0">pts</span>
          {pointsToRedeem > 0 && (
            <span className="text-[10px] font-black text-emerald-600 font-mono shrink-0">
              −{formatRs(redemptionValue)}
            </span>
          )}
          {pointsToRedeem === 0 && (
            <button
              onClick={() => {
                const maxByBalance = Math.floor(Number(customer.points));
                const maxByBill = Math.floor(totalBeforeRedemption / Math.max(loyaltyConfig.rupeePerPoint, 0.0001));
                setPointsToRedeem(Math.min(maxByBalance, maxByBill));
                Haptics.fire('tap');
              }}
              className="px-1.5 py-0.5 rounded bg-amber-500 hover:bg-amber-600 text-white text-[8px] font-black uppercase tracking-wider transition-colors shrink-0"
            >
              Max
            </button>
          )}
        </div>
      )}

      {/* 💰 Store Credit — prominent apply section */}
      {Number(customer.storeCredit) > 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-emerald-200/60">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">💰 Store Credit</span>
            <span className="text-[11px] font-black text-emerald-600 font-mono">Rs. {Number(customer.storeCredit).toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} available</span>
          </div>
          {storeCreditToUse > 0 ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 rounded-lg px-2 py-1.5">
                <span className="text-[10px] font-bold text-emerald-700">Applying:</span>
                <input
                  type="number"
                  min={0}
                  max={maxStoreCredit}
                  value={storeCreditToUse || ''}
                  onChange={(e) => {
                    const n = Math.max(0, parseFloat(e.target.value || '0') || 0);
                    setStoreCreditToUse(Math.min(n, maxStoreCredit));
                  }}
                  className="flex-1 w-16 px-1 py-0.5 rounded bg-white border border-emerald-300 text-[12px] font-mono font-black text-emerald-800 text-right outline-none focus:border-emerald-500"
                />
                <span className="text-[10px] font-mono text-emerald-600">Rs.</span>
              </div>
              <button
                onClick={() => { setStoreCreditToUse(0); Haptics.fire('tap'); }}
                className="px-2 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-[10px] font-bold hover:bg-rose-100 transition-colors shrink-0"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                setStoreCreditToUse(maxStoreCredit);
                Haptics.fire('tap');
              }}
              className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-[11px] font-black uppercase tracking-wider shadow-sm shadow-emerald-500/20 active:scale-[0.98] transition-all flex items-center justify-center gap-1.5"
            >
              <Wallet size={13} />
              Apply Store Credit — Rs. {maxStoreCredit.toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </button>
          )}
        </div>
      )}

      {/* Earn preview */}
      {estimatedPointsEarn > 0 && (
        <div className="text-[9px] font-mono text-emerald-700 mt-1 text-center">
          Earns <span className="font-black">+{estimatedPointsEarn.toFixed(0)} pts</span> on this bill
        </div>
      )}
    </div>
  );

  return (
    <BillingLockGate apiKey={storeApiKey}>
    <>
    <div className="h-screen flex flex-col text-slate-800 font-sans overflow-hidden select-none relative" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f0f9ff 30%, #faf5ff 60%, #f8fafc 100%)' }}>

      {/* Ambient glows */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden print:hidden">
        <div className="absolute -top-60 -left-60 w-[700px] h-[700px] rounded-full bg-sky-300/25 blur-[140px]" />
        <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-violet-300/15 blur-[120px]" />
        <div className="absolute -bottom-40 left-1/4 w-[450px] h-[450px] rounded-full bg-emerald-300/15 blur-[100px]" />
        <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-cyan-300/12 rounded-full blur-[120px] -z-10 pointer-events-none" />
        <div className="absolute top-2/3 right-1/4 w-[350px] h-[350px] bg-amber-200/10 rounded-full blur-[100px] -z-10 pointer-events-none" />
      </div>

      {/* ══ TOP BAR ══════════════════════════════════════════════ */}
      <header className={`h-16 shrink-0 flex items-center justify-between px-3 sm:px-6 z-30 print:hidden relative pos-header-glow ${showBill ? 'hidden md:flex' : ''}`} style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.88) 100%)', backdropFilter: 'blur(24px) saturate(2)', borderBottom: '1px solid rgba(30,27,75,0.06)' }}>
        <div className="flex items-center gap-1.5 sm:gap-2.5 overflow-hidden">
          {/* Back button - visible when not on #pos */}
          {currentView !== 'pos' && (
            <motion.button
              whileTap={{ scale: 0.93 }}
              onClick={goBack}
              className="flex items-center gap-1 px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200 hover:text-slate-800 transition-colors"
            >
              <ChevronLeft size={13} />
              <span className="text-[9px] sm:text-[10px] font-bold hidden sm:inline">Back</span>
            </motion.button>
          )}
          
          <div className="flex items-center gap-1 px-1.5 py-1 sm:px-2.5 sm:py-1.5 rounded-lg bg-brand-surface border border-brand-surface-border shrink-0">
            <Zap size={11} className="text-brand-primary-600" />
            <span className="text-[9px] sm:text-[10px] font-black tracking-[0.12em] sm:tracking-[0.18em] text-brand-primary-600 uppercase">POS.OS</span>
          </div>

          {/* Brand watermark — mobile only */}
          <span className="absolute left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400/50 pointer-events-none select-none md:hidden">Kangavena</span>

          {/* 🛡️ Phase 4 — Cashier session badge (hidden on mobile, accessible via bottom nav More / hamburger) */}
          {cashier ? (
            <button
              onClick={() => { Haptics.fire('tap'); setCashierAuthOpen(true); }}
              className={`hidden md:flex items-center gap-1 px-2 py-1 rounded-lg border text-[9px] font-bold uppercase tracking-wider sm:tracking-widest transition-all truncate max-w-[110px] sm:max-w-none ${
                cashier.role === 'manager'
                  ? 'bg-violet-500/10 border-violet-500/20 text-violet-600'
                  : cashier.role === 'inventory'
                  ? 'bg-amber-500/10 border-amber-500/20 text-amber-600'
                  : cashier.role === 'readonly'
                  ? 'bg-slate-100 border-slate-200 text-slate-500'
                  : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600'
              }`}
              title={`${cashier.name} (${cashier.roleLabel}) — Click to switch`}
            >
              <User size={8} /> <span className="truncate">{cashier.name}</span>
            </button>
          ) : (
            <button
              onClick={() => { Haptics.fire('tap'); setCashierAuthOpen(true); }}
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-600 text-[9px] font-bold uppercase tracking-wider sm:tracking-widest shrink-0"
            >
              <Shield size={8} /> Sign In
            </button>
          )}

          {/* 🔲 Barcode Scanner Status — auto-detected via fast typing pattern */}
          {scannerConnected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 text-[9px] font-bold uppercase tracking-wider shrink-0"
              title="USB barcode scanner connected — scans auto-add to bill"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
              <Barcode size={9} /> Scanner
            </motion.div>
          )}
          {!scannerConnected && (
            <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 border border-slate-200 text-slate-400 text-[9px] font-bold uppercase tracking-wider shrink-0" title="No barcode scanner detected — scan once to connect">
              <Barcode size={9} /> No Scanner
            </div>
          )}

          {/* 🖨️ Printer Status Badge + Connect button */}
          {printerConnected ? (
            <button
              onClick={() => setPrinterSetupOpen(true)}
              className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-300 text-emerald-700 text-[9px] font-bold uppercase tracking-wider shrink-0 hover:bg-emerald-100 transition-all shadow-sm"
              title="Thermal printer online & connected. Click to manage printer settings."
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              🖨️ Printer Online
            </button>
          ) : (
            <button
              onClick={() => setPrinterSetupOpen(true)}
              className="hidden md:flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 border border-amber-300 text-amber-600 text-[9px] font-bold uppercase tracking-wider shrink-0 hover:bg-amber-100 transition-all"
              title="Click to connect USB thermal printer for direct printing"
            >
              🖨️ Connect Printer
            </button>
          )}

          {/* 🖨️ Auto Print Toggle */}
          {/* 🖨️ Auto Print Toggle */}
          <button
            onClick={toggleAutoPrint}
            className={`hidden md:flex items-center gap-1 px-2 py-1 rounded-lg border text-[9px] font-bold uppercase tracking-wider shrink-0 transition-all ${
              autoPrintBill
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
            }`}
            title={autoPrintBill ? 'Auto Print ON' : 'Auto Print OFF'}
          >
            {autoPrintBill ? '✅ Auto Print' : '🖨️ Auto Print'}
          </button>

          {/* DESKTOP ONLY: Inline Shift/Shop controls */}
          <div className="hidden md:flex items-center gap-2">
            {/* 🕐 Phase 7 — Clock In/Out button (cashier path only, not owner) */}
            {cashier && cashier.id !== 0 && (
              currentShift ? (
                <button
                  onClick={() => { Haptics.fire('tap'); endShift(); }}
                  disabled={shiftLoading}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-[9px] font-bold uppercase tracking-widest hover:bg-emerald-500/20 transition-all disabled:opacity-50"
                  title="Click to clock out"
                >
                  <Timer size={9} />
                  {shiftLoading ? '…' : 'Clock Out'}
                </button>
              ) : (
                <button
                  onClick={() => { Haptics.fire('tap'); startShift(); }}
                  disabled={shiftLoading}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-600 text-[9px] font-bold uppercase tracking-widest hover:bg-amber-500/20 transition-all disabled:opacity-50"
                  title="Click to clock in"
                >
                  <Clock size={9} />
                  {shiftLoading ? '…' : 'Clock In'}
                </button>
              )
            )}

            {/* 🏪 Shop Open/Closed toggle (manager only) */}
            {!isCashierMode && (
              <button
                onClick={toggleShop}
                disabled={shopSyncing}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[9px] font-bold uppercase tracking-widest transition-all disabled:opacity-50 ${
                  shopOpen
                    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600'
                    : 'bg-rose-500/10 border-rose-500/20 text-rose-600'
                }`}
                title={shopOpen ? 'Shop is OPEN — click to close & send report' : 'Shop is CLOSED — click to open'}
              >
                <DoorOpen size={9} />
                {shopSyncing ? '…' : shopOpen ? 'Open' : 'Closed'}
              </button>
            )}

            {/* 🔐 Phase 6 — Pending login approvals badge (manager only) */}
            {!isCashierMode && pendingLogins.length > 0 && (
              <button
                onClick={() => { Haptics.fire('tap'); setShowApprovalPanel(true); }}
                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600 text-[9px] font-bold uppercase tracking-widest hover:bg-amber-500/20 transition-all"
                title={`${pendingLogins.length} cashier login${pendingLogins.length > 1 ? 's' : ''} waiting for approval`}
              >
                <Bell size={9} className="animate-bounce" />
                <span>{pendingLogins.length}</span>
              </button>
            )}
          </div>

          <div className="hidden md:block h-4 w-px bg-slate-200 shrink-0" />
          <div className="hidden md:flex flex-col leading-none shrink-0 overflow-hidden max-w-[120px]">
            <span className="text-[11px] font-bold text-slate-800 truncate">{storeName}</span>
            <span className="text-[8px] text-slate-500 font-mono truncate">{selectedCashier?.name || cashierName}</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          {/* Stats pill */}
          {todaySales.length > 0 && (
            <div className="hidden md:flex items-center gap-2 px-2.5 py-1 rounded-lg bg-emerald-500/8 border border-emerald-500/15 text-[9px] font-mono text-emerald-400 shrink-0">
              <TrendingUp size={9} />
              <span>{todaySales.length} sales · {formatRs(todayTotal)}</span>
            </div>
          )}

          {/* Clock */}
          <div className="hidden xl:flex flex-col items-end shrink-0">
            <span className="text-[11px] font-black font-mono text-slate-800 tabular-nums">
              {now.toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-wide">
              {now.toLocaleDateString('en-LK', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>

          {/* 🛡️ Tri-state status pill — clickable to manually retry pending sync */}
          <button
            onClick={() => { void handleManualSync(); }}
            disabled={offlineStatus === 'syncing' || offlineStatus === 'offline'}
            className={`hidden md:flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold border transition-colors focus:outline-none shrink-0 ${
              offlineStatus === 'syncing'
                ? 'border-amber-500/30 bg-amber-500/10 text-amber-500 cursor-not-allowed'
                : offlineStatus === 'offline'
                ? 'border-rose-500/30 bg-rose-500/10 text-rose-500 cursor-not-allowed'
                : pendingTxCount > 0
                ? 'border-amber-500/30 bg-amber-500/10 text-amber-500 cursor-pointer hover:bg-amber-500/20 hover:border-amber-500/50 active:scale-95'
                : 'border-emerald-500/25 bg-emerald-500/10 text-emerald-600 cursor-default'
            }`}
            title={
              offlineStatus === 'syncing'
                ? `Syncing ${pendingTxCount} transaction${pendingTxCount !== 1 ? 's' : ''}…`
                : offlineStatus === 'offline'
                ? `Offline — ${pendingTxCount} queued`
                : pendingTxCount > 0
                ? `${failedTxCount > 0 ? `${failedTxCount} failed, ` : ''}${pendingTxCount} pending — click to retry`
                : 'All transactions synced'
            }
          >
            {offlineStatus === 'syncing' ? (
              <RefreshCw size={8} className="animate-spin" />
            ) : offlineStatus === 'offline' ? (
              <WifiOff size={8} />
            ) : pendingTxCount > 0 ? (
              <RefreshCw size={8} />
            ) : (
              <Wifi size={8} />
            )}
            <span className="hidden sm:inline">
              {offlineStatus === 'syncing'
                ? 'Syncing'
                : offlineStatus === 'offline'
                ? 'Offline'
                : pendingTxCount > 0
                ? 'Pending'
                : 'Synced'}
            </span>
            {pendingTxCount > 0 && (
              <span className="ml-0.5 px-1.5 rounded-full bg-current/20 text-[8px] font-black tabular-nums">
                {pendingTxCount}
              </span>
            )}
          </button>

          {/* DESKTOP ONLY Inline secondary options */}
          <div className="hidden md:flex items-center gap-1.5">
            {/* 🚀 Marketing Center — manager only */}
            {!isCashierMode && (
              <button
                onClick={() => setMarketingCenterOpen(true)}
                title="Marketing Center — අලෙවිකරණ මධ්‍යස්ථානය"
                className="relative flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold border border-cyan-300/40 bg-cyan-500/8 text-cyan-600 hover:bg-cyan-500/15 hover:text-cyan-700 hover:border-cyan-400/50 transition-colors focus:outline-none"
              >
                <Rocket size={8} />
                <span>Marketing</span>
              </button>
            )}

            {/* ⚙ Settings — manager only */}
            {!isCashierMode && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('kangavena:open-settings'))}
                title="Billing & Store Settings"
                className="relative flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold border border-slate-200/80 bg-white/70 text-slate-500 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200 transition-colors focus:outline-none"
              >
                <Settings size={8} />
                <span>Settings</span>
              </button>
            )}

            {/* 🔍 Accessibility: Display Size Control */}
            <div className="relative">
              <motion.button
                whileTap={{ scale: 0.93 }}
                onClick={() => setUiScaleOpen(v => !v)}
                title="Display Size / දර්ශන ප්‍රමාණය"
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-bold transition-all ${
                  uiScaleOpen ? 'bg-brand-surface border-brand-primary-300 text-brand-primary-700' : 'bg-white border-slate-200 text-slate-500 hover:text-brand-primary hover:border-brand-primary'
                }`}
              >
                <Eye size={12} />
                <span>{uiScale}%</span>
              </motion.button>

              {/* Scale Popover */}
              {uiScaleOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.96 }}
                  className="absolute top-full right-0 mt-2 w-56 p-3 rounded-xl bg-white/95 backdrop-blur-xl border border-slate-200 shadow-2xl z-[200]"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-slate-800">Display Size</span>
                    <span className="text-[10px] text-slate-800 font-mono">{uiScale}%</span>
                  </div>

                  <input
                    type="range"
                    min={80}
                    max={150}
                    step={5}
                    value={uiScale}
                    onChange={e => setUiScale(parseInt(e.target.value))}
                    className="w-full h-1.5 mb-3 rounded-full bg-slate-200 accent-themeBrand cursor-pointer"
                  />

                  <div className="grid grid-cols-4 gap-1.5">
                    {[90, 100, 120, 135].map((scale) => (
                      <button
                        key={scale}
                        onClick={() => setUiScale(scale)}
                        className={`px-2 py-1.5 rounded-lg text-[9px] font-bold transition-all ${
                          uiScale === scale ? 'bg-brand-surface text-brand-primary-700 border border-brand-primary-300' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                      >
                        {scale === 100 ? '100%' : scale === 90 ? 'A-' : scale === 120 ? 'A+' : 'A++'}
                      </button>
                    ))}
                  </div>

                  <p className="mt-2 text-[8px] text-slate-400 text-center">දර්ශන ප්‍රමාණය</p>
                </motion.div>
              )}
            </div>

            {/* Desktop view toggle */}
            <button
              onClick={() => { Haptics.fire('tap'); setForceDesktop(v => !v); }}
              title={forceDesktop ? 'Switch to mobile view' : 'Switch to desktop view'}
              className={`
                flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold border transition-all active:scale-95
                ${forceDesktop
                  ? 'bg-brand-surface border-brand-primary-300 text-brand-primary-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:text-brand-primary hover:border-brand-primary'
                }
              `}
            >
              {forceDesktop ? <Minimize2 size={14} /> : <Monitor size={14} />}
              <span>{forceDesktop ? 'Mobile' : 'Desktop'}</span>
            </button>

            {onSwitchShop && (
              <button onClick={handleSwitchShop} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-sky-50 text-slate-400 hover:text-sky-600 transition-colors text-[10px] font-bold border border-transparent hover:border-sky-200" title="Switch Shop">
                <Layers size={12} /> <span>Shops</span>
              </button>
            )}
            {onExit && (
              <button onClick={handleExit} className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-500 transition-colors" title="Log Out">
                <LogOut size={13} />
              </button>
            )}
          </div>

          {/* MOBILE ONLY: Hamburger Menu Trigger */}
          <button
            onClick={() => { Haptics.fire('tap'); setShowMobileMenu(prev => !prev); }}
            className={`md:hidden p-2 rounded-lg border transition-all ${
              showMobileMenu 
                ? 'bg-brand-primary/10 border-brand-primary/30 text-brand-primary-600' 
                : 'bg-white border-slate-200 text-slate-500 hover:text-brand-primary hover:border-brand-primary'
            }`}
            title="Menu"
          >
            {showMobileMenu ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>

        {/* MOBILE DROPDOWN MENU — Fully Animated via AnimatePresence */}
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="md:hidden absolute top-full right-3 mt-2 w-64 p-3 rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/80 shadow-2xl z-[100] flex flex-col gap-2.5"
              style={{ boxShadow: '0 10px 30px -5px rgba(14,165,233,0.15)' }}
            >
              {/* Header Info */}
              <div className="flex flex-col border-b border-slate-100 pb-2 px-1">
                <span className="text-[11px] font-black text-slate-800 truncate">{storeName}</span>
                <span className="text-[9px] text-slate-500 font-mono truncate">{cashierName}</span>
              </div>

              {/* 🕐 Shift / Clock Control inside Mobile Menu */}
              {cashier && cashier.id !== 0 && (
                currentShift ? (
                  <button
                    onClick={() => { Haptics.fire('tap'); endShift(); setShowMobileMenu(false); }}
                    disabled={shiftLoading}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider text-emerald-600 bg-emerald-500/8 border border-emerald-500/15 hover:bg-emerald-500/12 transition-all disabled:opacity-50"
                  >
                    <Timer size={14} />
                    <span>Clock Out</span>
                  </button>
                ) : (
                  <button
                    onClick={() => { Haptics.fire('tap'); startShift(); setShowMobileMenu(false); }}
                    disabled={shiftLoading}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-500/8 border border-amber-500/15 hover:bg-amber-500/12 transition-all disabled:opacity-50"
                  >
                    <Clock size={14} />
                    <span>Clock In</span>
                  </button>
                )
              )}

              {/* 🏪 Shop Toggle inside Mobile Menu */}
              {!isCashierMode && (
                <button
                  onClick={() => { toggleShop(); setShowMobileMenu(false); }}
                  disabled={shopSyncing}
                  className={`flex items-center gap-3 w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider border transition-all disabled:opacity-50 ${
                    shopOpen
                      ? 'bg-emerald-500/8 border-emerald-500/15 text-emerald-600 hover:bg-emerald-500/12'
                      : 'bg-rose-500/8 border-rose-500/15 text-rose-600 hover:bg-rose-500/12'
                  }`}
                >
                  <DoorOpen size={14} />
                  <span>Shop Status: {shopOpen ? 'Open' : 'Closed'}</span>
                </button>
              )}

              {/* 🔐 Pending Approvals Badge inside Mobile Menu */}
              {!isCashierMode && pendingLogins.length > 0 && (
                <button
                  onClick={() => { Haptics.fire('tap'); setShowApprovalPanel(true); setShowMobileMenu(false); }}
                  className="flex items-center justify-between w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider bg-amber-500/8 border border-amber-500/15 text-amber-600 hover:bg-amber-500/12 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <Bell size={14} className="animate-bounce" />
                    <span>Approvals ({pendingLogins.length})</span>
                  </div>
                </button>
              )}

              {/* 🚀 Marketing Center inside Mobile Menu */}
              {!isCashierMode && (
                <button
                  onClick={() => { setMarketingCenterOpen(true); setShowMobileMenu(false); }}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider bg-cyan-500/8 border border-cyan-500/15 text-cyan-600 hover:bg-cyan-500/12 transition-all"
                >
                  <Rocket size={14} />
                  <span>Marketing Center</span>
                </button>
              )}

              {/* ⚙ Settings inside Mobile Menu */}
              {!isCashierMode && (
                <button
                  onClick={() => { window.dispatchEvent(new CustomEvent('kangavena:open-settings')); setShowMobileMenu(false); }}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 transition-all"
                >
                  <Settings size={14} />
                  <span>Store Settings</span>
                </button>
              )}

              {/* 🔍 Accessibility Display Size inside Mobile Menu */}
              <div className="p-2 rounded-xl bg-slate-50 border border-slate-150/50">
                <div className="flex items-center justify-between mb-1.5 px-1">
                  <span className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-1.5">
                    <Eye size={12} /> Text Size
                  </span>
                  <span className="text-[10px] text-slate-800 font-mono font-bold">{uiScale}%</span>
                </div>
                <input
                  type="range"
                  min={80}
                  max={150}
                  step={5}
                  value={uiScale}
                  onChange={e => setUiScale(parseInt(e.target.value))}
                  className="w-full h-1 mb-2 rounded-full bg-slate-200 accent-themeBrand cursor-pointer"
                />
                <div className="grid grid-cols-4 gap-1">
                  {[90, 100, 120, 135].map((scale) => (
                    <button
                      key={scale}
                      onClick={() => setUiScale(scale)}
                      className={`py-1 rounded-md text-[8px] font-bold transition-all ${
                        uiScale === scale ? 'bg-brand-primary text-white border-transparent' : 'bg-white text-slate-500 border border-slate-200'
                      }`}
                    >
                      {scale === 100 ? '100%' : scale === 90 ? 'A-' : scale === 120 ? 'A+' : 'A++'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Shops Switcher inside Mobile Menu */}
              {onSwitchShop && (
                <button
                  onClick={() => { handleSwitchShop(); setShowMobileMenu(false); }}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider bg-sky-50 border border-sky-100 text-sky-600 hover:bg-sky-100 transition-all"
                >
                  <Layers size={14} />
                  <span>Switch Shop</span>
                </button>
              )}

              {/* Exit / Log Out inside Mobile Menu */}
              {onExit && (
                <button
                  onClick={() => { handleExit(); setShowMobileMenu(false); }}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider bg-rose-50 border border-rose-100 text-rose-600 hover:bg-rose-100 transition-all"
                >
                  <LogOut size={14} />
                  <span>Log Out</span>
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        {/* Gradient accent line at bottom of header */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#1e1b4b] via-brand-primary to-brand-secondary opacity-80" />
      </header>

      {/* ══ RESPONSIVE ACTION GRID / BAR ═══════════════════════════ */}
      {!isCashierMode && (
        <div className={`shrink-0 z-20 transition-all duration-300 ease-in-out overflow-hidden print:hidden border-b border-slate-100/80 ${forceDesktop ? '' : 'hidden md:block'} ${
          showMobileHeader
            ? 'max-h-[620px] py-1.5 opacity-100'
            : 'max-h-0 py-0 opacity-0 md:max-h-[620px] md:py-1.5 md:opacity-100'
        }`}>
          <div className="max-w-7xl mx-auto px-2 sm:px-3">
            <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 md:flex md:flex-row md:items-center md:gap-2.5 w-full transition-all duration-300 ${isGridExpanded ? 'max-h-[520px] md:max-h-none' : 'max-h-[108px] md:max-h-none'} overflow-hidden md:overflow-visible`}>

              {/* ── Edit ── */}
              <button onClick={() => { setIsEditMode(!isEditMode); setEditProduct(null); setSelectedProductIds([]); setActiveWeightCardId(null); setWeightInputValue(''); }}
                className={`h-[42px] md:h-[38px] flex items-center justify-center gap-1.5 rounded-lg border active:scale-[0.98] transition-all duration-150 px-3 ${
                  isEditMode
                    ? 'bg-[#d97706] text-white border-[#d97706]'
                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}>
                <Edit size={14} />
                <span className="text-[11px] font-semibold tracking-wide">{isEditMode ? 'Done' : 'Edit'}</span>
              </button>

              {/* ── Add Item ── */}
              <button onClick={() => setAddProductOpen(true)}
                className="h-[42px] md:h-[38px] flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-[#1e1b4b] hover:bg-[#1e1b4b]/90 text-white active:scale-[0.98] transition-all duration-150 shadow-sm">
                <PlusCircle size={15} />
                <span className="text-[11px] font-semibold tracking-wide">Add Item</span>
              </button>

              {/* ── Scan ── */}
              <button onClick={() => setCameraOpen(true)}
                className="h-[42px] md:h-[38px] flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-[#1e1b4b]/8 hover:bg-[#1e1b4b]/15 text-[#1e1b4b] border border-[#1e1b4b]/15 active:scale-[0.98] transition-all duration-150">
                <Camera size={15} />
                <span className="text-[11px] font-semibold tracking-wide">Scan</span>
              </button>

              {/* ── Discount ── */}
              <button onClick={() => setShowDiscount(v => !v)}
                className="h-[42px] md:h-[38px] flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200/80 active:scale-[0.98] transition-all duration-150">
                <Percent size={15} />
                <span className="text-[11px] font-semibold tracking-wide">{discount > 0 ? `${discount}% Off` : 'Discount'}</span>
              </button>

              {/* ── Hold ── */}
              <button onClick={() => { if (cart.length === 0) { navigateTo('hold_manager'); } else { holdBill(); } }}
                className="h-[42px] md:h-[38px] flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-600 border border-slate-200 active:scale-[0.98] transition-all duration-150 relative">
                <PauseCircle size={15} />
                <span className="text-[11px] font-semibold tracking-wide">{cart.length > 0 ? 'Hold' : 'Held'}</span>
                {heldBills.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center shadow-sm border-2 border-white px-1">{heldBills.length}</span>
                )}
              </button>

              {/* ── SECONDARY ROW ── */}
              <button onClick={() => { navigateTo('analytics'); setAnalyticsTab('pulse'); }}
                className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                <BarChart3 size={14} className="text-brand-primary" />
                <span className="text-[11px] font-bold text-slate-700">Dashboard</span>
              </button>
              <Can action="dayend:run" session={cashier}>
                <button onClick={() => navigateTo('dayend')}
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                  <Receipt size={14} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-700">Day-End</span>
                </button>
              </Can>
              <button onClick={() => navigateTo('customers')}
                className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                <UserCircle2 size={14} className="text-slate-500" />
                <span className="text-[11px] font-bold text-slate-700">Customers</span>
              </button>
              <Can action="cashier:view" session={cashier}>
                <button onClick={() => { navigateTo('cashiers'); fetchCashiers(); }}
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                  <Users size={14} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-700">Cashiers</span>
                </button>
              </Can>
              <Can action="dayend:run" session={cashier}>
                <button onClick={() => { navigateTo('employees'); }}
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                  <Users size={14} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-700">Employees</span>
                </button>
              </Can>
              <Can action="shift:manage" session={cashier}>
                <button onClick={() => { navigateTo('shifts'); }}
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                  <Clock size={14} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-700">Shifts</span>
                </button>
              </Can>
              <Can action="inventory:bulk" session={cashier}>
                <button onClick={() => { setBulkInitialProducts([]); setBulkInventoryOpen(true); }}
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                  <Package size={14} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-700">Bulk Add</span>
                </button>
              </Can>
              <Can action="inventory:bulk" session={cashier}>
                <button onClick={() => setInventorySheetScannerOpen(true)}
                  title="Scan & Ingest Inventory Sheet via Luna Live Vision"
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-cyan-500/10 via-emerald-500/10 to-transparent border border-cyan-500/30 hover:border-cyan-500/60 active:scale-95 transition-all duration-200 pos-btn-shadow group">
                  <Camera size={14} className="text-cyan-600 group-hover:scale-110 transition-transform" />
                  <span className="text-[11px] font-bold text-cyan-800">Scan Sheet</span>
                </button>
              </Can>
              <button onClick={() => navigateTo('estimates')}
                className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                <FileText size={14} className="text-slate-500" />
                <span className="text-[11px] font-bold text-slate-700">Estimates</span>
              </button>
              <Can action="inventory:create" session={cashier}>
                <button onClick={() => navigateTo('suppliers')}
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                  <Building2 size={14} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-700">Suppliers</span>
                </button>
              </Can>
              {isAutoParts && (
                <>
                  <button onClick={() => { navigateTo('garage_jobs'); }}
                    className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200/60 hover:bg-orange-100 hover:border-orange-300 active:scale-95 transition-all duration-200 pos-btn-shadow">
                    <ClipboardList size={14} className="text-orange-600" />
                    <span className="text-[11px] font-bold text-orange-700">Jobs</span>
                  </button>
                  <button onClick={() => { navigateTo('vin_lookup'); }}
                    className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200/60 hover:bg-orange-100 hover:border-orange-300 active:scale-95 transition-all duration-200 pos-btn-shadow">
                    <Car size={14} className="text-orange-600" />
                    <span className="text-[11px] font-bold text-orange-700">VIN</span>
                  </button>
                </>
              )}
              <Can action="store:settings" session={cashier}>
                <button onClick={() => { navigateTo('settings'); setProfileError(''); }}
                  className="h-[48px] md:h-[40px] md:flex-1 flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-br from-white to-slate-50 border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all duration-200 pos-btn-shadow">
                  <Settings size={14} className="text-slate-500" />
                  <span className="text-[11px] font-bold text-slate-700">Settings</span>
                </button>
              </Can>
            </div>

            {/* Expand Toggle — mobile only */}
            <div className="flex justify-center md:hidden mt-1 pb-1">
              <button
                onClick={() => setIsGridExpanded(!isGridExpanded)}
                className="mt -1 flex items-center justify-center gap-1 text-[9px] font-bold text-[#1e1b4b]/50 hover:text-[#1e1b4b] transition-colors py-0.5 px-2.5 rounded-full hover:bg-[#1e1b4b]/5"
              >
                {isGridExpanded ? 'Less' : 'More'} {isGridExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount quick-pick strip — manager: toggled from grid; cashier: toggled from dock Discount button */}
      <AnimatePresence>
        {showDiscount && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className={`shrink-0 flex items-center justify-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-xl border-b border-slate-100/80 z-20 print:hidden ${showBill ? 'hidden md:flex' : ''}`}>
            {[5, 10, 15, 20, 25].map(d => (
              <button key={d} onClick={() => { Haptics.fire('tap'); setDiscount(discount === d ? 0 : d); }}
                className={`w-12 h-8 rounded-xl text-[11px] font-black border transition-all ${
                  discount === d ? 'bg-violet-500 border-violet-400 text-white' : 'border-slate-200 bg-white text-violet-500 hover:bg-violet-50'
                }`}>{d}%</button>
            ))}
            {/* ⚡ Phase 2.7 — Custom discount % via numpad */}
            <button
              onClick={async () => {
                const val = await openNumpad({ title: 'Custom Discount %', initial: discount ? String(discount) : '', suffix: '%', max: 100, allowDecimal: false });
                if (val !== null) {
                  const n = Math.max(0, Math.min(100, parseInt(val, 10) || 0));
                  setDiscount(n);
                }
              }}
              className={`w-12 h-8 rounded-xl text-[11px] font-black border transition-all ${
                discount > 0 && ![5,10,15,20,25].includes(discount)
                  ? 'bg-violet-500 border-violet-400 text-white'
                  : 'border-slate-200 bg-white text-violet-500 hover:bg-violet-50'
              }`}
              title="Custom discount %"
            >
              {discount > 0 && ![5,10,15,20,25].includes(discount) ? `${discount}%` : '…%'}
            </button>
            {/* 💸 Custom Flat Cash Discount (Rs.) */}
            <button
              onClick={async () => {
                const val = await openNumpad({ title: 'Flat Discount (Rs.)', initial: flatDiscount ? String(flatDiscount) : '', suffix: ' Rs.', max: subtotal, allowDecimal: false });
                if (val !== null) {
                  const n = Math.max(0, Math.min(subtotal, parseInt(val, 10) || 0));
                  setFlatDiscount(n);
                }
              }}
              className={`px-3.5 h-8 rounded-xl text-[11px] font-black border transition-all ${
                flatDiscount > 0
                  ? 'bg-emerald-500 border-emerald-400 text-white'
                  : 'border-slate-200 bg-white text-emerald-600 hover:bg-emerald-50'
              }`}
              title="Custom Flat Cash Discount (Rs.)"
            >
              {flatDiscount > 0 ? `Rs. ${flatDiscount}` : 'Flat Rs.'}
            </button>
            <button onClick={() => { setDiscount(0); setFlatDiscount(0); setShowDiscount(false); }}
              className="w-12 h-8 rounded-xl text-[11px] font-bold border border-slate-200 bg-white text-red-500 hover:bg-red-50 transition-all">
              Clear
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ MAIN BODY ════════════════════════════════════════════ */}
      <div className="relative z-10 flex flex-1 overflow-hidden">

        {/* ═══ LEFT — Products ═══ */}
        <div className="flex flex-col flex-1 overflow-hidden print:hidden bg-gradient-to-b from-slate-50/80 to-slate-50/50">

          {/* Search + Actions */}
          <div className="px-3 md:px-4 pt-2 pb-1 shrink-0 space-y-1 print:hidden">
            <div className="flex items-center gap-2.5">
              <div className="pos-search-shadow flex-1 flex items-center gap-2.5 bg-white backdrop-blur-md border border-slate-200 rounded-2xl px-3.5 py-2 shadow-sm shadow-slate-200/50 focus-within:ring-2 focus-within:ring-brand-primary/20 focus-within:border-brand-primary/40 transition-all duration-300 hover:border-slate-300">
                <Search size={18} className="text-slate-400 shrink-0" />
                <input
                  id="product-search"
                  ref={searchRef}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && search.trim()) {
                      // Skip if global scanner listener already processed this Enter
                      if (scannerProcessedRef.current) {
                        scannerProcessedRef.current = false;
                        return;
                      }
                      handleBarcodeInput(search.trim());
                      e.preventDefault();
                    }
                  }}
                  onInput={() => {
                    // Fast typing into search box = likely scanner
                    const now = Date.now();
                    if (now - scannerLastScanRef.current < 1000) {
                      setScannerConnected(true);
                    }
                    scannerLastScanRef.current = now;
                  }}
                  placeholder="Search products or scan barcode…"
                  className="flex-1 bg-transparent text-[15px] text-slate-800 placeholder:text-slate-400 outline-none font-medium"
                  autoFocus
                />
                {search
                  ? <button onClick={() => setSearch('')} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"><X size={15} /></button>
                  : <Barcode size={18} className="text-slate-300 shrink-0" />
                }
              </div>
              <motion.button whileTap={{ scale: 0.93 }} onClick={() => setCameraOpen(true)}
                className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e1b4b] to-brand-primary flex items-center justify-center text-white hover:brightness-110 transition-all shrink-0 shadow-md shadow-[#1e1b4b]/20" title="Scan Barcode">
                <Camera size={18} />
              </motion.button>
            </div>

            {/* 🔍 Fuzzy Search Suggestions Dropdown */}
            <AnimatePresence>
              {searchSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="bg-white border border-sky-100 rounded-xl shadow-lg shadow-sky-100/50 overflow-hidden"
                >
                  <div className="px-3 py-2 bg-brand-surface border-b border-brand-surface-border">
                    <span className="text-[10px] font-semibold text-brand-primary-700 uppercase tracking-wider">Did you mean?</span>
                  </div>
                  {searchSuggestions.map((product, idx) => (
                    <button
                      key={product.id}
                      onClick={() => {
                        addToCart(product);
                        showToast(`Added: ${product.name}`, 'success');
                        setSearch('');
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-sky-50 transition-colors ${
                        idx !== searchSuggestions.length - 1 ? 'border-b border-slate-50' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-lg">
                        {product.emoji || getCategoryEmoji(product.category)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-slate-800 truncate">{product.name}</p>
                        <p className="text-[10px] text-slate-500">{formatRs(product.price)} · {product.category}</p>
                      </div>
                      <Plus size={14} className="text-sky-500" />
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

          </div>

          {/* Edit Mode bulk tools — only visible when edit mode is active */}
          {isEditMode && selectedProductIds.length > 0 && (
            <div className="flex items-center gap-2 px-3 md:px-4 pb-1.5">
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 text-white text-[10px] font-black uppercase tracking-widest shadow-md shadow-red-600/30 hover:brightness-110 transition-all disabled:opacity-50"
              >
                <Trash2 size={12} />
                {bulkDeleting ? 'Deleting...' : `Delete (${selectedProductIds.length})`}
              </button>
              <select
                value={bulkLunaVisibility}
                onChange={e => setBulkLunaVisibility(e.target.value)}
                className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-[10px] font-bold text-slate-700 outline-none focus:border-emerald-400"
              >
                <option value="PUBLIC">🌐 Public</option>
                <option value="HIDDEN">🚫 Hidden</option>
                <option value="PRICE_HIDDEN">🔒 Hide Price</option>
              </select>
              <button
                onClick={handleBulkLunaVisibility}
                disabled={bulkVisLoading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest shadow-md shadow-emerald-600/30 hover:brightness-110 transition-all disabled:opacity-50"
              >
                {bulkVisLoading ? 'Applying...' : `Apply (${selectedProductIds.length})`}
              </button>
            </div>
          )}

          {/* Category tabs */}
          <div className="flex gap-2 px-3 md:px-4 pb-1.5 overflow-x-auto shrink-0 scrollbar-none print:hidden snap-x snap-mandatory" style={{ WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)', maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)' }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className={`snap-start px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest whitespace-nowrap border transition-all duration-300 ${
                  activeCategory === cat
                    ? 'text-white border-transparent shadow-lg shadow-[#1e1b4b]/30 bg-gradient-to-r from-[#1e1b4b] to-brand-primary ring-2 ring-brand-primary/20'
                    : 'bg-slate-50/80 text-slate-500 border-slate-100 hover:bg-slate-100 hover:text-slate-700 hover:border-[#1e1b4b]/20 hover:shadow-sm'
                }`}
              >
                {cat}
              </button>
            ))}

            {/* 🤖 Bulk Auto Categorize button */}
            <button
              onClick={async () => {
                if (!confirm('Auto-categorize ALL products with Luna AI? This will update categories for every product.')) return;
                try {
                  showToast('🤖 Luna is categorizing all products...', 'success');
                  const res = await fetch('/api/auto-categorize/bulk', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey },
                  });
                  const data = await res.json();
                  if (data.success) {
                    showToast(`✅ Categorized ${data.categorized}/${data.total} products`, 'success');
                    fetchInventory(); // refresh
                  } else {
                    showToast('Auto-categorization failed', 'error');
                  }
                } catch { showToast('Auto-categorization failed', 'error'); }
              }}
              className="snap-start px-4 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap border border-violet-300 bg-violet-50 text-violet-600 hover:bg-violet-100 hover:border-violet-400 transition-all duration-300"
            >
              ✨ Auto Categorize All
            </button>
          </div>

          {/* Base Model filter — only shows when products have baseModel set */}
          {baseModels.length > 1 && (
            <div className="flex gap-1.5 px-3 md:px-4 pb-1.5 overflow-x-auto shrink-0 scrollbar-none print:hidden">
              {baseModels.map(m => (
                <button key={m} onClick={() => setActiveBaseModel(m)}
                  className={`px-3 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-wider whitespace-nowrap border transition-all duration-300 ${
                    activeBaseModel === m
                      ? 'bg-emerald-50 border-emerald-400/40 text-emerald-700 shadow-sm shadow-emerald-500/10'
                      : 'bg-white text-slate-400 border-slate-100 hover:bg-slate-50 hover:text-slate-600 hover:border-emerald-300/30 hover:shadow-sm'
                  }`}
                >
                  {m === 'All' ? '🏷️ All Models' : m}
                </button>
              ))}
            </div>
          )}

          {/* Product Grid */}
          <div ref={productScrollRef} onScroll={onProductScroll} className={`flex-1 overflow-y-auto px-3 md:px-4 ${forceDesktop ? 'pb-48' : 'pb-40 md:pb-48'} print:hidden`}>
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 gap-3">
                <motion.div
                  animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgb(var(--brand-primary-rgb)/0.12), rgb(var(--brand-secondary-rgb)/0.08))', border: '1px solid rgb(var(--brand-primary-rgb)/0.18)' }}
                >
                  <Package size={30} strokeWidth={1.2} className="text-[#1e1b4b]/40" />
                </motion.div>
                <div className="text-center space-y-1">
                  <p className="text-sm font-bold text-slate-600">No items found</p>
                  <p className="text-[11px] text-slate-400">Try a different search or add products first</p>
                  <button
                    onClick={() => fetchInventory()}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-primary text-white text-[11px] font-bold shadow-sm hover:opacity-90 transition-all active:scale-95"
                  >
                    <RefreshCw size={12} /> Reload Products
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 md:gap-2.5">
                {filtered.map(product => {
                  const inCart = cart.find(i => i.product.id === product.id)?.qty || 0;
                  const wv = isWeightVolume(product);
                  const isSmallUnit = product.unitType === 'ml' || product.unitType === 'Grams';
                  const fmtStock = (v: number) => wv ? (isSmallUnit ? Math.round(v) + product.unitType : v.toFixed(1) + product.unitType) : String(v);
                  const outOfStock = product.stockCount <= 0;
                  const lowStock = product.stockCount > 0 && product.stockCount <= (wv ? (isSmallUnit ? 100 : 1) : 5);
                  const medStock = product.stockCount > (wv ? (isSmallUnit ? 100 : 1) : 5) && product.stockCount <= (wv ? (isSmallUnit ? 500 : 5) : 15);
                  const isActiveWeight = activeWeightCardId === product.id;
                  return (
                    <motion.button key={product.id}
                      whileTap={!isActiveWeight ? { scale: 0.93 } : {}}
                      whileHover={!outOfStock && !isActiveWeight ? { y: -3, scale: 1.02 } : {}}
                      onClick={() => {
                        if (isEditMode) {
                          setSelectedProductIds(prev => prev.includes(product.id) ? prev.filter(id => id !== product.id) : [...prev, product.id]);
                          return;
                        }
                        if (isActiveWeight) return;
                        if (wv && !outOfStock) {
                          setActiveWeightCardId(product.id);
                          setWeightInputValue('');
                          return;
                        }
                        addToCart(product);
                      }}
                      disabled={outOfStock && !isEditMode && !isActiveWeight}
                      className={`relative flex flex-col lg:flex-col items-start p-2.5 lg:p-3 rounded-xl bg-white border border-slate-100 text-left overflow-hidden transition-all duration-300 ring-0 hover:border-slate-200 pos-card-shadow hover:shadow-xl hover:shadow-slate-300/40 active:scale-[0.97]
                        ${isActiveWeight
                          ? 'border-[#1e1b4b]/40 bg-[#1e1b4b]/[0.03] ring-2 ring-[#1e1b4b]/20 shadow-inner'
                          : isEditMode && selectedProductIds.includes(product.id)
                            ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-400/40 shadow-md'
                            : outOfStock && !isEditMode && !isActiveWeight
                              ? 'opacity-40 cursor-not-allowed border-slate-100 bg-slate-50/50'
                              : inCart > 0 && !isEditMode
                                ? 'border-[#1e1b4b]/30 bg-gradient-to-br from-white to-[#1e1b4b]/[0.03] shadow-lg shadow-[#1e1b4b]/8'
                                : lowStock
                                  ? 'border-rose-200 bg-gradient-to-br from-white to-rose-50/50 hover:border-rose-300 hover:shadow-xl hover:shadow-rose-100/60'
                                  : medStock
                                    ? 'border-amber-200 bg-gradient-to-br from-white to-amber-50/30 hover:border-amber-300 hover:shadow-xl hover:shadow-amber-100/60'
                                    : 'border-slate-100 bg-gradient-to-br from-white to-slate-50/80 hover:shadow-xl hover:shadow-slate-200/50 hover:border-[#1e1b4b]/20'
                        }`}
                    >
                      {isActiveWeight ? (
                        <div className="flex flex-col items-center justify-center w-full h-full gap-1.5 py-1">
                          <span className="text-[10px] lg:text-[11px] font-extrabold text-slate-800 text-center leading-tight truncate w-full px-1">{product.name}</span>
                          <div className="flex items-center gap-1 w-full">
                            <input type="number" step={getQtyStep(product)} min="0" autoFocus
                              value={weightInputValue}
                              onChange={e => setWeightInputValue(e.target.value)}
                              onClick={e => e.stopPropagation()}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.stopPropagation();
                                  const qty = parseFloat(weightInputValue);
                                  if (qty > 0 && qty <= product.stockCount) {
                                    setCart(prev => {
                                      const existing = prev.find(i => i.product.id === product.id);
                                      if (existing) {
                                        return prev.map(i => i.product.id === product.id ? { ...i, qty: Math.min(existing.qty + qty, product.stockCount) } : i);
                                      }
                                      return [...prev, { product, qty: Math.min(qty, product.stockCount) }];
                                    });
                                    setWeightInputValue('');
                                    setActiveWeightCardId(null);
                                    Haptics.fire('tap');
                                    SoundManager.play('product_select');
                                  }
                                }
                              }}
                              className="w-full px-2 py-1.5 rounded-lg border border-brand-primary/40 text-xs font-mono font-bold text-slate-800 text-center bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                            />
                            <span className="text-[9px] font-bold text-slate-500 uppercase whitespace-nowrap shrink-0">{product.unitType}</span>
                          </div>
                          {(() => {
                            const parsed = parseFloat(weightInputValue);
                            const tooMuch = !isNaN(parsed) && parsed > product.stockCount;
                            return (
                              <div className="flex flex-col w-full gap-1 mt-0.5">
                                {/* Price Display capsule - centered and clear */}
                                <div className="text-center w-full py-0.5 shrink-0">
                                  <span className="text-[10px] sm:text-xs font-black text-brand-primary font-mono bg-brand-primary/5 px-2 py-0.5 rounded-md border border-brand-primary/10">
                                    {formatRs(product.price * (parsed || 0))}
                                  </span>
                                </div>
                                
                                {/* Action Buttons - Stacked on own row to avoid horizontal squishing */}
                                <div className="flex items-center gap-1 w-full">
                                  <button onClick={e => { e.stopPropagation(); setActiveWeightCardId(null); setWeightInputValue(''); }}
                                    className="flex-1 py-1 rounded-lg bg-slate-100 text-slate-500 text-[9px] font-black uppercase tracking-wider hover:bg-slate-200 transition-all">
                                    Cancel
                                  </button>
                                  <button onClick={e => {
                                    e.stopPropagation();
                                    const qty = parseFloat(weightInputValue);
                                    if (qty > 0 && qty <= product.stockCount) {
                                      setCart(prev => {
                                        const existing = prev.find(i => i.product.id === product.id);
                                        if (existing) {
                                          return prev.map(i => i.product.id === product.id ? { ...i, qty: Math.min(existing.qty + qty, product.stockCount) } : i);
                                        }
                                        return [...prev, { product, qty }];
                                      });
                                      Haptics.fire('tap');
                                      SoundManager.play('ui_click');
                                    }
                                    setActiveWeightCardId(null);
                                    setWeightInputValue('');
                                  }}
                                    disabled={tooMuch}
                                    className={`flex-1 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all ${
                                      tooMuch
                                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                        : 'bg-brand-primary text-brand-text-on-primary hover:brightness-110 shadow-sm'
                                    }`}>
                                    {tooMuch ? 'Limit' : 'Add'}
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                          {(() => {
                            const parsed = parseFloat(weightInputValue);
                            const tooMuch = !isNaN(parsed) && parsed > product.stockCount;
                            return tooMuch ? (
                              <span className="text-[8px] sm:text-[9px] text-red-500 font-bold text-center leading-none mt-0.5">Max: {product.stockCount} {product.unitType}</span>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        <>
                      {isEditMode && (
                        <span className={`absolute top-1.5 left-1.5 z-10 w-4 h-4 lg:w-5 lg:h-5 rounded border-2 flex items-center justify-center transition-all ${
                          selectedProductIds.includes(product.id)
                            ? 'bg-amber-500 border-amber-500'
                            : 'bg-white border-slate-300'
                        }`}>
                          {selectedProductIds.includes(product.id) && (
                            <svg viewBox="0 0 24 24" className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                      )}
                      {isEditMode && (
                        <button onClick={e => { e.stopPropagation(); setEditProduct(product); }}
                          className="absolute top-1.5 right-1.5 z-10 w-5 h-5 lg:w-6 lg:h-6 rounded-full bg-white/90 border border-slate-200 shadow-sm flex items-center justify-center hover:bg-white transition-all">
                          <Edit size={10} className="text-slate-500" />
                        </button>
                      )}
                      {inCart > 0 && !isEditMode && (
                        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}
                          className="absolute top-1.5 right-1.5 min-w-5 h-5 lg:min-w-6 lg:h-6 px-1 rounded-full bg-brand-primary text-brand-text-on-primary text-[8px] lg:text-[9px] font-black flex items-center justify-center shadow-md shadow-brand-primary/30 z-10">
                          {wv ? (isSmallUnit ? Math.round(inCart) : inCart.toFixed(1)) : inCart}
                        </motion.span>
                      )}
                      {product.productImage ? (
                        <img src={product.productImage} alt={product.name} className="w-10 h-16 sm:h-20 lg:w-12 lg:h-12 object-cover rounded-lg mb-1 border border-slate-100 shadow-sm" />
                      ) : (
                        <span className="w-10 h-16 sm:h-20 lg:w-12 lg:h-12 flex items-center justify-center text-base lg:text-lg mb-1 leading-none">{product.emoji || getCategoryEmoji(product.category)}</span>
                      )}
                      <span className="text-[11px] lg:text-[12px] font-extrabold text-[#1e1b4b] leading-snug mb-0.5 line-clamp-2 pr-3">{product.name}</span>
                      <span className="hidden md:block text-[8px] text-slate-400 uppercase tracking-wide mb-1.5">{product.category}</span>
                      {/* 🔒 Luna Visibility Badge */}
                      {product.lunaVisibility && product.lunaVisibility !== 'PUBLIC' && (
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[7px] lg:text-[8px] font-bold uppercase tracking-wider mb-1 ${
                          product.lunaVisibility === 'HIDDEN'
                            ? 'bg-red-100 text-red-600 border border-red-200'
                            : 'bg-amber-100 text-amber-600 border border-amber-200'
                        }`}>
                          {product.lunaVisibility === 'HIDDEN' ? '🚫 Luna Hidden' : '🔒 Price Hidden'}
                        </span>
                      )}
                      <span className="text-xs lg:text-sm font-black text-brand-primary font-mono leading-none tracking-tight">{formatRs(product.price)}{wv ? <span className="text-[7px] lg:text-[8px] text-brand-secondary">/{product.unitType}</span> : ''}</span>
                      {/* ↔️ Between Price Range Badge */}
                      {(product.minPrice != null || product.maxPrice != null) && (
                        <span className="text-[7.5px] lg:text-[8.5px] font-extrabold text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-1.5 py-0.5 rounded-md mt-1 inline-block font-mono tracking-tight shadow-2xs">
                          ↔️ Range: {formatRs(product.minPrice || 0)} - {formatRs(product.maxPrice || 0)}
                        </span>
                      )}
                      <span className={`text-[8px] lg:text-[10px] mt-1 lg:mt-1.5 font-bold inline-flex items-center ${
                        outOfStock ? 'text-white bg-red-500 px-2 py-0.5 rounded-full' : lowStock ? 'text-white bg-rose-500 px-2 py-0.5 rounded-full' : medStock ? 'text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full' : 'text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full'
                      }`}>
                        {outOfStock ? 'Out of Stock' : lowStock ? `Low Stock` : `${fmtStock(product.stockCount)} in stock`}
                      </span>
                        </>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ═══ RIGHT — Bill (desktop sidebar + mobile slide-up drawer) ═══ */}
        {/* Desktop sidebar */}
        <CartSidebar
          cart={cart}
          lastTransaction={lastTransaction}
          cashiersList={cashiersList}
          selectedCashier={selectedCashier}
          setSelectedCashier={setSelectedCashier}
          cashierName={cashierName}
          subtotal={subtotal}
          discount={discount}
          discountAmt={discountAmt}
          coreChargesTotal={coreChargesTotal}
          grandTotal={grandTotal}
          itemCount={itemCount}
          storeName={profileForm.name || storeName}
          now={now}
          isCashierMode={isCashierMode}
          sidebarPayMode={sidebarPayMode}
          aiSuggestion={aiSuggestion}
          dismissedSuggestions={dismissedSuggestions}
            updateQty={updateQty}
          removeFromCart={removeFromCart}
          handleCheckout={processCheckout}
          setLastTransaction={setLastTransaction}
          setCart={setCart}
          setDiscount={setDiscount}
          setShowDiscount={setShowDiscount}
          setSidebarPayMode={setSidebarPayMode}
          setCashAmount={setCashAmount}
          setMobileProvider={setMobileProvider}
          setDismissedSuggestions={setDismissedSuggestions}
          setAiSuggestion={setAiSuggestion}
          handleAddSuggestedItem={handleAddSuggestedItem}
          isProcessing={isProcessing}
          handlePaymentConfirm={handlePaymentConfirm}
          onSaveEstimate={() => setEstimateSaveModalOpen(true)}
          formatRs={formatRs}
          getUnitLabel={getUnitLabel}
          isWeightVolume={isWeightVolume}
          Haptics={Haptics}
          customerPillJsx={customerPillJsx}
          PaymentMethodPicker={PaymentMethodPicker}
          openNumpad={openNumpad}
          updateItemDiscount={updateItemDiscount}
          updateItemMarkup={updateItemMarkup}
          toggleDamageFlag={toggleDamageFlag}
          updateItemSellPrice={updateItemSellPrice}
          getItemSellPrice={getItemSellPrice}
          editingPolicyId={editingPolicyId}
          openPolicyEditor={openPolicyEditor}
          confirmPolicyEdit={confirmPolicyEdit}
          editReturnPolicy={editReturnPolicy}
          setEditReturnPolicy={setEditReturnPolicy}
          editWarrantyPeriod={editWarrantyPeriod}
          setEditWarrantyPeriod={setEditWarrantyPeriod}
          customer={customer}
          storeApiKey={storeApiKey}
          onCustomerAttached={(c) => setCustomer(c)}
          customFieldLabel={customFieldLabel}
          loyaltyConfig={loyaltyConfig}
          pointsToRedeem={pointsToRedeem}
          setPointsToRedeem={setPointsToRedeem}
          totalBeforeRedemption={totalBeforeRedemption}
          redemptionValue={redemptionValue}
          estimatedPointsEarn={estimatedPointsEarn}
          storeCreditToUse={storeCreditToUse}
          setStoreCreditToUse={setStoreCreditToUse}
          maxStoreCredit={maxStoreCredit}
          effectiveStoreCredit={effectiveStoreCredit}
          autoPrintBill={autoPrintBill}
          onToggleAutoPrint={toggleAutoPrint}
          showLogoOnBill={showLogoOnBill}
          hidePoweredByOnBill={hidePoweredByOnBill}
          onPrintThermalReceipt={printThermalReceipt}
          receiptTemplate={receiptTemplate}
        />

        {/* Mobile slide-up bill drawer */}
        <AnimatePresence>
          {showBill && (
            <motion.div
              key="mobile-bill-drawer"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="md:hidden fixed inset-0 z-[9999] flex flex-col bg-white overflow-hidden"
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1 shrink-0 md:hidden">
                <div className="w-10 h-1 rounded-full bg-slate-300" />
              </div>
              {/* Drawer header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0 bg-gradient-to-r from-white to-slate-50 shadow-sm">
                <button
                  onClick={() => { if (lastTransaction) { setLastTransaction(null); } closeBill(); }}
                  className="flex items-center gap-1.5 text-slate-800 font-bold text-[13px] active:opacity-70"
                >
                  <ChevronDown size={18} />
                  <span>බඩු ලිස්ට් එක</span>
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-[#1e1b4b]/10 flex items-center justify-center">
                    <Receipt size={11} className="text-[#1e1b4b]" />
                  </div>
                  <span className="font-black text-[10px] text-[#1e1b4b]/80 uppercase tracking-[0.15em]">Current Bill</span>
                  {itemCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-[#059669]/10 text-[#059669] text-[8px] font-bold border border-[#059669]/20">
                      {itemCount}
                    </span>
                  )}
                </div>
              </div>

              {/* Cart items OR Receipt */}
              <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 pb-4 space-y-1.5">
                {lastTransaction ? (
                  <>
                    {/* 🧾 PROFESSIONAL RECEIPT PREVIEW — 100% WYSIWYG */}
                    <div className="flex flex-col items-center">
                      <div className="w-full max-w-sm rounded-xl overflow-hidden shadow-lg border border-slate-200 bg-white">
                        <ReceiptTemplateRenderer
                          templateId={receiptTemplate}
                          data={{
                            id: lastTransaction.id,
                            storeName: profileForm.name || storeName || lastTransaction.storeName,
                            storeLogo: lastTransaction.storeLogo,
                            storeAddress: lastTransaction.storeAddress,
                            storePhone: lastTransaction.storePhone,
                            time: lastTransaction.time,
                            cashierName: lastTransaction.cashierName,
                            customer: lastTransaction.customer,
                            cart: lastTransaction.cart,
                            subtotal: lastTransaction.subtotal,
                            discountPct: lastTransaction.discountPct,
                            discountAmt: lastTransaction.discountAmt,
                            storeCreditUsed: lastTransaction.storeCreditUsed,
                            total: lastTransaction.total,
                            payments: lastTransaction.payments,
                            change: (lastTransaction.payments || []).find((p: any) => p.change !== undefined)?.change || lastTransaction.change || 0,
                          }}
                          is4Inch={false}
                          showLogoOnBill={showLogoOnBill}
                          hidePoweredByOnBill={hidePoweredByOnBill}
                        />
                      </div>
                    </div>
                  </>
                ) : cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 gap-2">
                    <ShoppingCart size={24} className="text-[#1e1b4b]/20" strokeWidth={1.5} />
                    <span className="text-[10px] font-mono text-slate-400">Bill is empty — tap a product</span>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {cart.map(item => {
                      const effectivePrice = getItemSellPrice(item);
                      const hasAdjustment = (item.itemDiscount && item.itemDiscount > 0) || (item.itemMarkup && item.itemMarkup > 0) || (item.adjustedPrice !== undefined && item.adjustedPrice !== null);
                      
                      return (
                      <SwipeableRow key={item.product.id} onDelete={() => removeFromCart(item)}>
                        <div className="flex flex-col gap-1.5 p-2 rounded-xl bg-slate-50/90 border border-slate-200/80 shadow-xs">
                          {/* Product Row */}
                          <div className="flex items-center gap-2">
                            <span className="text-base shrink-0">{item.product.emoji || '📦'}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1">
                                <p className="text-[10px] font-bold text-slate-800 truncate">{item.product.name}</p>
                                {hasAdjustment && (
                                  <span className="shrink-0 px-1 py-0.5 rounded bg-blue-500/10 text-blue-600 border border-blue-500/20 text-[7px] font-bold font-mono leading-none">
                                    adj
                                  </span>
                                )}
                                <button
                                  onClick={() => openPolicyEditor(item.product.id, item.returnPolicy, item.warrantyPeriod)}
                                  className="shrink-0 p-1 rounded hover:bg-slate-200 cursor-pointer transition-colors"
                                  title="Override warranty & return policy"
                                >
                                  <Settings size={16} className="text-slate-400 hover:text-slate-700" />
                                </button>
                              </div>
                              {(item.returnPolicy || item.warrantyPeriod) && editingPolicyId !== item.product.id ? (
                                <p className="text-[9px] text-slate-500 italic leading-tight mt-0.5">{[item.returnPolicy, item.warrantyPeriod ? `Warranty: ${item.warrantyPeriod}` : null].filter(Boolean).join(" | ")}</p>
                              ) : null}
                              <p className="text-[8px] text-slate-500 font-mono mt-0.5">
                                {hasAdjustment ? (
                                  <>
                                    <span className="line-through text-slate-400">{formatRs(item.product.price)}</span>
                                    {item.itemDiscount && item.itemDiscount > 0 && <span className="ml-1 text-rose-600">-{formatRs(item.itemDiscount)}</span>}
                                    {item.itemMarkup && item.itemMarkup > 0 && <span className="ml-1 text-emerald-600">+{formatRs(item.itemMarkup)}</span>}
                                    {item.adjustedPrice !== undefined && item.adjustedPrice !== null && <span className="ml-1 text-blue-600">={formatRs(item.adjustedPrice)}</span>}
                                    <span className="ml-1 text-slate-800 font-bold">→ {formatRs(effectivePrice)}</span>
                                  </>
                                ) : (
                                  formatRs(item.product.price)
                                )}
                                {getUnitLabel(item.product) ? `/${item.product.unitType}` : ''} × {isWeightVolume(item.product) ? ((item.product.unitType === 'ml' || item.product.unitType === 'Grams') ? Math.round(item.qty) + item.product.unitType : item.qty.toFixed(1) + item.product.unitType) : item.qty} = <span className="text-slate-800 font-bold">{formatRs(effectivePrice * item.qty)}</span>
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button onClick={() => updateQty(item.product.id, -1)} className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
                                <Minus size={12} />
                              </button>
                              <span className="w-8 text-center text-[10px] font-black text-slate-800 font-mono">{isWeightVolume(item.product) ? ((item.product.unitType === 'ml' || item.product.unitType === 'Grams') ? Math.round(item.qty) : item.qty.toFixed(1)) : item.qty}</span>
                              <button onClick={() => updateQty(item.product.id, 1)} className="w-7 h-7 rounded-lg bg-brand-surface hover:bg-brand-primary-100 border border-brand-surface-border flex items-center justify-center text-brand-primary transition-colors">
                                <Plus size={12} />
                              </button>
                              <button onClick={() => removeFromCart(item)} className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 flex items-center justify-center text-red-400/70 hover:text-red-400 transition-all ml-1">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          {/* 🔧 Policy Editor — NEW ROW below product */}
                          {editingPolicyId === item.product.id && (
                            <div className="flex flex-col gap-2 w-full p-2 bg-slate-800 rounded border border-slate-600">
                              <div className="flex flex-col gap-1">
                                <label className="text-[9px] text-slate-400 font-mono uppercase tracking-wider">Return Policy</label>
                                <input
                                  type="text"
                                  value={editReturnPolicy}
                                  onChange={(e) => setEditReturnPolicy(e.target.value)}
                                  placeholder="e.g. 14 Days, No Return, Standard"
                                  className="h-8 w-full text-xs rounded border-2 border-slate-500 bg-slate-900 text-white px-2 focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                              <div className="flex flex-col gap-1">
                                <label className="text-[9px] text-slate-400 font-mono uppercase tracking-wider">Warranty Period</label>
                                <input
                                  type="text"
                                  value={editWarrantyPeriod}
                                  onChange={(e) => setEditWarrantyPeriod(e.target.value)}
                                  placeholder="e.g. 6 Months, 1 Year, No Warranty"
                                  className="h-8 w-full text-xs rounded border-2 border-slate-500 bg-slate-900 text-white px-2 focus:outline-none focus:border-emerald-500"
                                />
                              </div>
                              <button
                                onClick={() => confirmPolicyEdit(item.product.id)}
                                className="w-full h-8 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1 cursor-pointer transition-colors"
                              >✓ Confirm</button>
                            </div>
                          )}

                          {/* Inline Controls Row */}
                          <div className="flex items-center justify-between gap-1 pt-1 mt-0.5 border-t border-slate-200/60">
                            <div className="flex items-center gap-1">
                              {/* Damage Flag */}
                              <button
                                onClick={() => toggleDamageFlag(item.product.id)}
                                className={`px-1.5 py-0.5 text-[8px] font-semibold rounded border transition-all ${
                                  item.damageFlag
                                    ? 'bg-amber-100 border-amber-300 text-amber-800'
                                    : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200/60'
                                }`}
                              >
                                {item.damageFlag ? '⚠️ Dmg' : '🏷️ Dmg?'}
                              </button>

                              {/* Discount Input */}
                              <div className="flex items-center gap-0.5 bg-rose-50/60 px-1 py-0.5 rounded border border-rose-200/70">
                                <span className="text-[8px] text-rose-600 font-bold">−</span>
                                <input
                                  type="number"
                                  value={item.itemDiscount || ''}
                                  onChange={(e) => updateItemDiscount(item.product.id, parseFloat(e.target.value) || 0)}
                                  className="w-9 h-4 text-[8.5px] text-center font-mono rounded bg-white border border-rose-300 focus:border-rose-500 focus:outline-none p-0"
                                  placeholder="0"
                                  min="0"
                                  step="10"
                                />
                                <span className="text-[7.5px] text-rose-500 font-medium">disc</span>
                              </div>

                              {/* Markup Input */}
                              <div className="flex items-center gap-0.5 bg-emerald-50/60 px-1 py-0.5 rounded border border-emerald-200/70">
                                <span className="text-[8px] text-emerald-600 font-bold">+</span>
                                <input
                                  type="number"
                                  value={item.itemMarkup || ''}
                                  onChange={(e) => updateItemMarkup(item.product.id, parseFloat(e.target.value) || 0)}
                                  className="w-9 h-4 text-[8.5px] text-center font-mono rounded bg-white border border-emerald-300 focus:border-emerald-500 focus:outline-none p-0"
                                  placeholder="0"
                                  min="0"
                                  step="10"
                                />
                                <span className="text-[7.5px] text-emerald-500 font-medium">markup</span>
                              </div>
                            </div>

                            {/* Price Override */}
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button
                                onClick={async () => {
                                  const val = await openNumpad({ 
                                    title: `Set price for ${item.product.name}`, 
                                    initial: item.adjustedPrice?.toString() || item.product.price.toString(), 
                                    suffix: ' Rs.', 
                                    allowDecimal: true 
                                  });
                                  if (val !== null) {
                                    if (val.trim() === '') {
                                      updateItemSellPrice(item.product.id, null);
                                      return;
                                    }
                                    const parsed = parseFloat(val);
                                    if (!isNaN(parsed) && parsed >= 0) {
                                      updateItemSellPrice(item.product.id, parsed);
                                    }
                                  }
                                }}
                                title={item.adjustedPrice !== undefined && item.adjustedPrice !== null ? `Price overridden: Rs. ${formatRs(item.adjustedPrice)}` : 'Override price'}
                                className={`px-1.5 py-0.5 text-[8.5px] font-bold rounded flex items-center gap-1 transition-all ${
                                  item.adjustedPrice !== undefined && item.adjustedPrice !== null
                                    ? 'bg-blue-600 text-white border border-blue-700 shadow-xs'
                                    : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                                }`}
                              >
                                <span>💲</span>
                                <span>{item.adjustedPrice !== undefined && item.adjustedPrice !== null ? formatRs(item.adjustedPrice) : 'Override'}</span>
                              </button>
                              {item.adjustedPrice !== undefined && item.adjustedPrice !== null && (
                                <button
                                  type="button"
                                  onClick={() => updateItemSellPrice(item.product.id, null)}
                                  title="Reset price"
                                  className="w-4 h-4 rounded-full bg-slate-200 hover:bg-rose-100 hover:text-rose-600 text-slate-500 flex items-center justify-center text-[8.5px] transition-colors"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </SwipeableRow>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Footer: receipt action OR bill summary OR inline payment ── */}
              <div className="border-t border-slate-100 shrink-0 bg-white transition-all duration-300">

                {lastTransaction ? (
                  /* 🧾 RECEIPT ACTIONS: Print + Start New Sale */
                  <div className="p-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] space-y-3">
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => { Haptics.fire('tap'); printThermalReceipt(); }}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-black text-[13px] uppercase tracking-wider bg-gradient-to-r from-sky-500 to-cyan-500 text-white shadow-lg shadow-sky-500/25 active:scale-95 transition-all print:hidden"
                    >
                      🖨️ Print Receipt
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        setLastTransaction(null);
                        setCart([]);
                        setDiscount(0);
                        setFlatDiscount(0);
                        setShowDiscount(false);
                        setSidebarPayMode(false);
                        setCashAmount('');
                        setMobileProvider('');
                        setDrawerPayMode(false);
                        closeBill();
                      }}
                      className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-[14px] uppercase tracking-wider bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25 active:scale-95 transition-all"
                    >
                      <CheckCircle2 size={18} />
                      START NEW SALE
                    </motion.button>
                  </div>
                ) : !drawerPayMode ? (
                  /* ── BILL SUMMARY + CHARGE BUTTON ── */
                  <div className="p-3 pb-[max(1rem,env(safe-area-inset-bottom,0px))] space-y-2">
                    {/* Luna suggestion (mobile drawer) */}
                    {aiSuggestion && (
                      <div className="relative p-2 rounded-xl bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 border border-violet-200">
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Sparkles className="text-violet-600 shrink-0" size={12} />
                            <p className="text-[10px] text-slate-800 font-bold truncate">
                              <span className="text-violet-600 mr-1 text-[9px] uppercase">Luna:</span>
                              {aiSuggestion.item}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => handleAddSuggestedItem(aiSuggestion.item)}
                              className="flex items-center gap-0.5 py-0.5 px-2 bg-violet-600 hover:bg-violet-700 text-white text-[9px] font-bold rounded shadow-xs">
                              <Plus size={9} /> Add
                            </button>
                            <button onClick={() => { setDismissedSuggestions(prev => new Set(prev).add(aiSuggestion.item)); setAiSuggestion(null); }}
                              className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-rose-500">
                              <X size={10} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 🎯 Phase 3 — Customer / Loyalty pill (mobile drawer) */}
                    {!isCashierMode && cart.length > 0 && (
                      <div className="px-1">{customerPillJsx}</div>
                    )}

                    {/* Cashier Selector (Mobile) */}
                    {cart.length > 0 && (
                      <div className="px-1 py-1">
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-slate-50 border border-slate-200">
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider shrink-0">Cashier:</span>
                          <select
                            value={selectedCashier?.id || 0}
                            onChange={(e) => {
                              const id = parseInt(e.target.value, 10);
                              if (id === 0) {
                                setSelectedCashier({ id: 0, name: cashierName || 'Manager' });
                              } else {
                                const found = cashiersList.find(c => c.id === id);
                                if (found) {
                                  setSelectedCashier({ id: found.id, name: found.name });
                                }
                              }
                            }}
                            className="flex-1 bg-transparent text-[11px] font-extrabold text-slate-800 outline-none cursor-pointer"
                          >
                            <option value={0}>{cashierName || 'Manager'}</option>
                            {cashiersList.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* 📋 Print Estimate — above charge row, mirrors desktop spec */}
                    {cart.length > 0 && (
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => { Haptics.fire('tap'); setEstimateSaveModalOpen(true); }}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-[11px] uppercase tracking-wider bg-sky-100 hover:bg-sky-200 text-sky-700 border border-sky-200 hover:border-sky-300 transition-all print:hidden"
                      >
                        🖨️ Save &amp; Print Estimate
                      </motion.button>
                    )}

                    {/* Totals row */}
                    <div className="flex items-center justify-between px-1">
                      <div>
                        {discountAmt > 0 && (
                          <p className="text-[10px] text-violet-600 font-mono">− {formatRs(discountAmt)} discount {discount > 0 ? `(${discount}%)` : ''}</p>
                        )}
                        {pointsToRedeem > 0 && (
                          <p className="text-[10px] text-amber-600 font-mono">− {pointsToRedeem.toFixed(0)} pts redeemed</p>
                        )}
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wider font-mono">Grand Total</span>
                          <button
                            onClick={() => setShowDiscount(true)}
                            className="text-[10px] sm:text-[11px] font-bold text-violet-600 hover:text-violet-800 transition-colors uppercase tracking-wider flex items-center gap-0.5"
                          >
                            <Tag size={10} /> {discountAmt > 0 ? 'Edit' : 'Add Discount'}
                          </button>
                        </div>
                        <p className="text-[22px] font-black text-slate-800 font-mono leading-none">{formatRs(grandTotal)}</p>
                      </div>
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => { openBill(); setDrawerPayMode(true); }}
                        disabled={cart.length === 0}
                        className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-black text-[13px] uppercase tracking-wider transition-all
                          ${cart.length === 0
                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-brand-primary to-brand-secondary text-brand-text-on-primary shadow-lg shadow-brand-primary/25'
                          }`}>
                        <CreditCard size={14} /> Charge
                      </motion.button>
                    </div>
                  </div>
                ) : (
                  /* ── INLINE PAYMENT PANEL ── */
                  <div className="h-full flex flex-col overflow-y-auto px-3 pt-3 pb-4 space-y-3">
                    {/* 💳 Phase 10 — Inline Payment (Mobile Drawer) */}
                    <PaymentMethodPicker
                      grandTotal={grandTotal}
                      onConfirm={handlePaymentConfirm}
                      onClose={() => setDrawerPayMode(false)}
                      isProcessing={isProcessing}
                      hasCustomer={!!customer}
                      storeApiKey={storeApiKey}
                      onCustomerAttached={(c) => setCustomer(c)}
                      attachedCustomer={customer} // 🎯 Unified Customer Integration
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ══ LUNA — Always mounted (for event listening), conditionally visible ══════════════════ */}
      {!isCashierMode && (
        <>
          {/* Luna Voice Model — always rendered but hidden when not active */}
          <div className={`fixed inset-0 z-[9998] print:hidden ${lunaOpen ? '' : 'opacity-0 pointer-events-none [&_*]:pointer-events-none'}`}>
            <LunaVoiceModel
              ref={lunaRef}
              apiKey={storeApiKey}
              role="store"
              storeName={storeName}
              onDisconnect={() => setLunaOpen(false)}
            />
          </div>

          {/* Luna Overlay backdrop (visual only — pointer-events-none) */}
          <AnimatePresence>
            {lunaOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 z-[9997] w-screen h-screen bg-[#04060f]/90 backdrop-blur-md pointer-events-none print:hidden"
              />
            )}
          </AnimatePresence>
          {/* Minimize button — outside transform stacking context so z-[99999] is truly absolute */}
          <AnimatePresence>
            {lunaOpen && (
              <motion.button
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                onClick={() => setLunaOpen(false)}
                className="fixed top-4 right-4 z-[99999] flex items-center gap-2 px-3.5 py-2 rounded-xl bg-black/70 backdrop-blur-xl border border-white/10 text-slate-300 text-[11px] font-bold hover:border-red-500/30 hover:text-red-400 transition-all pointer-events-auto print:hidden"
              >
                <Minimize2 size={12} /> Minimize Luna
              </motion.button>
            )}
          </AnimatePresence>

        </>
      )}

      {/* ══ CAMERA SCANNER ══════════════════════════════════════ */}
      <AnimatePresence>
        {cameraOpen && (
          <CameraScanner
            onDetected={(code) => { handleBarcodeInput(code); setCameraOpen(false); }}
            onClose={() => setCameraOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* ══ PRICE PICKER ════════════════════════════════════ */}
      <AnimatePresence>
        {pricePickerOpen && pricePickerProduct && (
          <PricePicker
            productName={pricePickerProduct.name}
            basePrice={pricePickerProduct.price}
            priceList={(pricePickerProduct.priceList || []).map((p: any) => ({ label: p.label, price: p.price }))}
            onSelect={(price, label) => {
              setPricePickerOpen(false);
              pricePickerResolve?.({ price, label });
              setPricePickerResolve(null);
              setPricePickerProduct(null);
            }}
            onClose={() => {
              setPricePickerOpen(false);
              pricePickerResolve?.(null);
              setPricePickerResolve(null);
              setPricePickerProduct(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* ══ HOLD BILLS PANEL ════════════════════════════════════ */}
      <AnimatePresence>
        {heldBillsOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            className="fixed top-16 right-4 z-[9990] w-72 bg-[#080b18] border border-amber-500/20 rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-2 text-[11px] font-black text-amber-300">
                <BookMarked size={12} /> Held Bills ({heldBills.length})
              </div>
              <button onClick={() => setHeldBillsOpen(false)} className="text-slate-600 hover:text-white"><X size={13} /></button>
            </div>
            {heldBills.length === 0 ? (
              <div className="py-8 text-center text-[10px] text-slate-700 font-mono">No held bills</div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {heldBills.map(bill => (
                  <div key={bill.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-white/3 transition-colors">
                    <div>
                      <p className="text-[11px] font-bold text-white">{bill.label}</p>
                      <p className="text-[9px] text-slate-600 font-mono">{bill.items.length} item{bill.items.length !== 1 ? 's' : ''} · {formatRs(bill.items.reduce((s, i) => s + i.product.price * i.qty, 0))}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => restoreBill(bill.id)}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/12 border border-amber-500/20 text-amber-400 text-[9px] font-bold hover:bg-amber-500/20 transition-all">
                        <RotateCcw size={9} /> Restore
                      </button>
                      <button onClick={() => setHeldBills(prev => prev.filter(b => b.id !== bill.id))}
                        className="w-6 h-6 rounded-lg hover:bg-red-500/12 flex items-center justify-center text-slate-700 hover:text-red-400 transition-colors">
                        <X size={9} />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="p-2.5 bg-white/2 border-t border-white/[0.04] flex gap-1.5">
                  <button
                    onClick={() => {
                      setHeldBillsOpen(false);
                      navigateTo('hold_manager');
                    }}
                    className="w-full py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-[#080b18] text-[10px] font-black uppercase tracking-wider flex items-center justify-center gap-1 transition-all"
                  >
                    <BookMarked size={11} /> Open Hold Dashboard
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ MANAGE CASHIERS VIEW — delegated to CashierManager component at line ~4287 ══ */}
      {false && currentView === 'cashiers' && !isCashierMode && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[50] bg-slate-50 flex flex-col"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-3xl mx-auto bg-white border border-sky-100 rounded-2xl shadow-xl flex flex-col max-h-[85vh] mt-4"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-sky-100 shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-violet-100 border border-violet-200 flex items-center justify-center">
                    <Users size={13} className="text-violet-600" />
                  </div>
                  <div>
                    <p className="font-bold text-[13px] text-slate-800">Manage Cashiers</p>
                    <p className="text-[9px] text-slate-500 font-mono">{storeName}</p>
                  </div>
                </div>
                <button onClick={() => navigateTo('pos')} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"><X size={13} /></button>
              </div>

              {/* Add new cashier */}
              <div className="p-5 border-b border-slate-100 shrink-0 space-y-3">
                <p className="text-[9px] text-slate-500 uppercase tracking-widest font-semibold">Add New Cashier</p>
                <div className="grid grid-cols-2 gap-2">
                  <input value={newCashier.name} onChange={e => setNewCashier(p => ({ ...p, name: e.target.value }))}
                    placeholder="Full Name" className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" />
                  <input value={newCashier.username} onChange={e => setNewCashier(p => ({ ...p, username: e.target.value }))}
                    placeholder="username" className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" />
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input value={newCashier.password} onChange={e => setNewCashier(p => ({ ...p, password: e.target.value }))}
                      type={showCashierPw ? 'text' : 'password'} placeholder="Password"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 pr-8 py-2 text-[12px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" />
                    <button type="button" onClick={() => setShowCashierPw(v => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showCashierPw ? <EyeOff size={11} /> : <Eye size={11} />}
                    </button>
                  </div>
                  <motion.button whileTap={{ scale: 0.95 }} onClick={addCashierAccount} disabled={newCashierAdding}
                    className="px-4 py-2 rounded-xl bg-violet-100 border border-violet-200 text-violet-700 text-[11px] font-bold hover:bg-violet-200 transition-all disabled:opacity-40">
                    {newCashierAdding ? <RefreshCw size={11} className="animate-spin" /> : <PlusCircle size={11} />}
                  </motion.button>
                </div>
                <p className="text-[9px] text-slate-500">Cashiers log in at <span className="text-violet-600 font-medium">/pos/cashier</span> with Store API Key + username + password</p>
              </div>

              {/* Cashier list */}
              <div className="flex-1 overflow-y-auto p-5 space-y-2">
                {cashiersLoading ? (
                  <div className="flex items-center justify-center py-8 text-slate-500 text-[11px] gap-2">
                    <RefreshCw size={13} className="animate-spin" /> Loading…
                  </div>
                ) : cashiersError ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 text-[11px]">
                    <AlertTriangle size={11} /> {cashiersError}
                  </div>
                ) : cashiersList.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-[11px]">No cashiers yet. Add one above.</div>
                ) : cashiersList.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-lg bg-violet-100 border border-violet-200 flex items-center justify-center">
                        <User size={12} className="text-violet-600" />
                      </div>
                      <div>
                        <p className="text-[12px] font-bold text-slate-800">{c.name}</p>
                        <p className="text-[9px] text-slate-500">@{c.username}</p>
                      </div>
                    </div>
                    <button onClick={() => removeCashierAccount(c.id, c.name)}
                      className="p-1.5 rounded-lg hover:bg-rose-100 text-slate-400 hover:text-rose-600 transition-colors">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}

      {/* ══ ADD PRODUCT MODAL (standalone component — isolated state) ══ */}
      <AnimatePresence>
        <AddProductModal
          isOpen={addProductOpen}
          onClose={() => { setAddProductOpen(false); setAddProductBarcodePrefill(''); }}
          storeApiKey={storeApiKey}
          onProductAdded={handleProductAdded}
          prefillBarcode={addProductBarcodePrefill}
          inventory={inventory}
          onSelectExistingProduct={(prod) => {
            setAddProductOpen(false);
            setAddProductBarcodePrefill('');
            setIsEditMode(true);
            setEditProduct(prod);
          }}
        />
      </AnimatePresence>

      {/* ══ INVENTORY SHEET SCANNER MODAL (Multimodal Vision Engine) ══ */}
      <AnimatePresence>
        {inventorySheetScannerOpen && (
          <InventorySheetScannerModal
            isOpen={inventorySheetScannerOpen}
            onClose={() => setInventorySheetScannerOpen(false)}
            storeApiKey={storeApiKey}
            onInventoryUpdated={fetchInventory}
            showToast={showToast as any}
          />
        )}
      </AnimatePresence>

      {/* ══ BULK INVENTORY MODAL ══ */}
      <AnimatePresence>
        <BulkInventoryModal
          isOpen={bulkInventoryOpen}
          onClose={() => { setBulkInventoryOpen(false); setBulkInitialProducts([]); }}
          storeApiKey={storeApiKey}
          onProductsAdded={async (count) => {
            showToast(`${count} products added to inventory`, 'success');
            await fetchInventory();
          }}
          initialProducts={bulkInitialProducts}
        />
      </AnimatePresence>

      {/* ══ VOICE PRODUCT HOLOGRAM (J.A.R.V.I.S. live form) ══ */}
      <AnimatePresence>
        {voiceProductOpen && (
          <VoiceProductHologram
            isOpen={voiceProductOpen}
            onClose={() => {
              setVoiceProductOpen(false);
              lunaRef.current?.forceKill();
              fetchInventory();
            }}
            storeApiKey={storeApiKey}
            onSaveComplete={fetchInventory}
          />
        )}
      </AnimatePresence>

      {/* ══ EDIT PRODUCT MODAL ══ */}
      <AnimatePresence>
        {isEditMode && editProduct && (
          <EditProductModal
            product={editProduct}
            storeApiKey={storeApiKey}
            onClose={() => setEditProduct(null)}
            onSaved={() => { setEditProduct(null); fetchInventory(); }}
          />
        )}
      </AnimatePresence>

      {/* ══ EXPRESS VOICE CHECKOUT MODAL ══ */}
      <AnimatePresence>
        {expressCheckoutOpen && (
          <VoiceCheckoutModal
            isOpen={expressCheckoutOpen}
            onClose={() => {
              setExpressCheckoutOpen(false);
              lunaRef.current?.forceKill();
            }}
            storeApiKey={storeApiKey}
            inventory={inventory}
            onCheckoutComplete={() => {
              // Refresh inventory after sales
              fetchInventory();
            }}
          />
        )}
      </AnimatePresence>

      {/* ══ STORE SETTINGS VIEW — manager only ══════════════════════════════════ */}
      {currentView === 'settings' && !isCashierMode && (
        <SettingsView
          storeName={storeName}
          storeLogo={storeLogo}
          themePalette={themePalette}
          logoThemeEnabled={logoThemeEnabled}
          profileForm={profileForm}
          profileError={profileError}
          profileSaving={profileSaving}
          setStoreLogo={setStoreLogo}
          setThemePalette={setThemePalette}
          setLogoThemeEnabled={setLogoThemeEnabled}
          setProfileForm={setProfileForm}
          applyThemeColor={applyThemeColor}
          extractBrandPalette={extractBrandPalette}
          updateStoreProfile={updateStoreProfile}
          navigateTo={navigateTo}
          paMode={paMode}
          paModeSaving={paModeSaving}
          onTogglePaMode={handleTogglePaMode}
          isLunaIndexed={isLunaIndexed}
          onToggleLunaIndexed={handleToggleLunaIndexed}
          showLogoOnBill={showLogoOnBill}
          onToggleShowLogoOnBill={() => {
            const next = !showLogoOnBill;
            setShowLogoOnBill(next);
            // Persist immediately
            if (storeId) {
              const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
              fetch(`/api/stores/${storeId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey, ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {}) },
                body: JSON.stringify({ showLogoOnBill: next }),
              }).catch(() => {});
            }
          }}
          hidePoweredByOnBill={hidePoweredByOnBill}
          onToggleHidePoweredByOnBill={() => {
            const next = !hidePoweredByOnBill;
            setHidePoweredByOnBill(next);
            localStorage.setItem('kangavena:hidePoweredByOnBill', String(next));
            if (storeId) {
              const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
              fetch(`/api/stores/${storeId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-store-api-key': storeApiKey, ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {}) },
                body: JSON.stringify({ hidePoweredByOnBill: next }),
              }).catch(() => {});
            }
          }}
          enableLunaSuggestions={enableLunaSuggestions}
          onToggleEnableLunaSuggestions={handleToggleEnableLunaSuggestions}
          receiptTemplate={receiptTemplate}
          onSaveReceiptTemplate={handleSaveReceiptTemplate}
          storeApiKey={storeApiKey}
          showToast={showToast}
        />
      )}

      {/* ══ TOAST ════════════════════════════════════════════════ */}
      {/* 🔮 MANAGER COMMAND CENTER — Sky Blue Glass / Light SaaS Theme */}
      {/* ══ ANALYTICS VIEW — full-screen dashboard ════════════════════ */}
      {!isCashierMode && currentView === 'analytics' && (
        <MasterDashboard onClose={() => navigateTo('pos')} storeName={storeName} storeApiKey={storeApiKey}>

              {/* KPI Cards - Mobile: 2 cols, Desktop: 5 cols */}
              <div className="relative z-10 grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 px-3 sm:px-6 py-3 sm:py-4 border-b border-white/60 bg-gradient-to-r from-brand-primary-50/40 via-white/80 to-brand-secondary-50/30 backdrop-blur-xl">
                {[
                  { 
                    label: 'Total Revenue', 
                    value: dailySummary ? formatRs(dailySummary.todayRevenue) : formatRs(analytics.revenue), 
                    icon: DollarSign, 
                    trend: kpiTrends ? `${kpiTrends.revenuePct >= 0 ? '+' : ''}${kpiTrends.revenuePct.toFixed(1)}%` : '—', 
                    trendUp: kpiTrends ? kpiTrends.revenuePct >= 0 : true,
                    color: 'brand' 
                  },
                  { 
                    label: 'Total Sales', 
                    value: String(analytics.txCount), 
                    icon: ShoppingBag, 
                    trend: kpiTrends ? `${kpiTrends.salesPct >= 0 ? '+' : ''}${kpiTrends.salesPct.toFixed(1)}%` : '—', 
                    trendUp: kpiTrends ? kpiTrends.salesPct >= 0 : true,
                    color: 'emerald' 
                  },
                  { 
                    label: 'Net Profit', 
                    value: dailySummary ? formatRs(dailySummary.todayNetProfit) : '—', 
                    icon: TrendingUp, 
                    trend: dailySummary && dailySummary.todayNetProfit >= 0 ? '+COGS' : '',
                    trendUp: dailySummary ? dailySummary.todayNetProfit >= 0 : true,
                    color: 'teal' 
                  },
                  { 
                    label: 'Avg Ticket', 
                    value: analytics.txCount ? formatRs(analytics.avgTicket) : '—', 
                    icon: Award, 
                    trend: kpiTrends ? `${kpiTrends.avgTicketPct >= 0 ? '+' : ''}${kpiTrends.avgTicketPct.toFixed(1)}%` : '—', 
                    trendUp: kpiTrends ? kpiTrends.avgTicketPct >= 0 : true,
                    color: 'violet' 
                  },
                  { 
                    label: 'Top Category', 
                    value: analytics.catRanking[0]?.name || '—', 
                    icon: TrendingUp, 
                    trend: analytics.catRanking[0]?.share ? `${analytics.catRanking[0].share.toFixed(0)}%` : '',
                    trendUp: true,
                    color: 'amber' 
                  },
                  ...(dailySummary && (dailySummary.totalStoreCredit || 0) > 0 ? [{
                    label: 'Store Credit',
                    value: formatRs(dailySummary.totalStoreCredit || 0),
                    icon: Wallet,
                    trend: `${dailySummary.storeCreditCustomers || 0} customers`,
                    trendUp: true,
                    color: 'teal'
                  }] : []),
                ].map((k, i) => {
                  const Icon = k.icon;
                  const fillPct = [82, 64, 73, 71, 45, 60][i] ?? 50;
                  const colorMap: Record<string, { bg: string; border: string; icon: string; text: string; bar: string }> = {
                    brand: { bg: 'from-brand-primary-50/80 to-white', border: 'border-brand-primary/25', icon: 'text-brand-primary', text: 'text-brand-primary-700', bar: 'bg-gradient-to-r from-brand-primary to-brand-secondary' },
                    emerald: { bg: 'from-emerald-50/80 to-white', border: 'border-emerald-200/60', icon: 'text-emerald-500', text: 'text-emerald-700', bar: 'bg-gradient-to-r from-emerald-400 to-emerald-500' },
                    teal: { bg: 'from-teal-50/80 to-white', border: 'border-teal-200/60', icon: 'text-teal-500', text: 'text-teal-700', bar: 'bg-gradient-to-r from-teal-400 to-teal-500' },
                    violet: { bg: 'from-violet-50/80 to-white', border: 'border-violet-200/60', icon: 'text-violet-500', text: 'text-violet-700', bar: 'bg-gradient-to-r from-violet-400 to-violet-500' },
                    amber: { bg: 'from-amber-50/80 to-white', border: 'border-amber-200/60', icon: 'text-amber-500', text: 'text-amber-700', bar: 'bg-gradient-to-r from-amber-400 to-amber-500' },
                  };
                  const c = colorMap[k.color];
                  return (
                    <motion.div 
                      key={i} 
                      initial={{ opacity: 0, y: 10 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      transition={{ delay: i * 0.1 }}
                      className={`group relative overflow-hidden rounded-xl sm:rounded-2xl border ${c.border} bg-white/90 backdrop-blur-md p-3 sm:p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-xl hover:-translate-y-1 transition-all duration-200`}
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gradient-to-br from-white/60 to-transparent" />
                      <div className="relative z-10 flex items-center justify-between mb-1.5 sm:mb-2">
                        <span className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-wide">{k.label}</span>
                        <div className={`w-7 h-7 rounded-lg ${c.icon.replace('text-','bg-').replace('500','100/80')} flex items-center justify-center shadow-sm`}>
                          <Icon size={14} className={c.icon} />
                        </div>
                      </div>
                      <div className="relative z-10 text-[15px] sm:text-[20px] font-black text-slate-800 leading-none mb-1 tracking-tight">{k.value}</div>
                      {k.trend && (
                        <div className={`relative z-10 flex items-center gap-0.5 text-[9px] sm:text-[10px] font-bold ${k.trendUp ? 'text-emerald-700' : 'text-rose-700'}`}>
                          {k.trendUp ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                          {k.trend} <span className="text-slate-400 ml-0.5">today</span>
                        </div>
                      )}
                      <div className="relative z-10 mt-2 h-1 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${c.bar} transition-all duration-500 shadow-[0_0_8px_rgba(255,255,255,0.6)]`} style={{ width: `${fillPct}%` }} />
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* 📊 REAL-WORLD RETAIL FINANCES: CUSTOMER DEBT (RED) & BROKER COMMISSIONS (ORANGE) */}
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-3 px-3 sm:px-6 py-2.5 bg-white/70 border-b border-brand-primary/10 backdrop-blur-sm">
                {/* Customer Debt Card */}
                <div className="rounded-2xl border border-rose-200 bg-rose-50/70 p-3 sm:p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-all">
                  <div className="space-y-1 min-w-0 flex-1">
                    <span className="text-[9px] sm:text-[10px] font-black text-red-800 uppercase tracking-wider flex items-center gap-1">
                      <span className="hidden sm:inline">🚨 Customer Outstanding Debt (ලැබීමට ඇති ණය)</span>
                      <span className="sm:hidden">🚨 Outstanding Debt</span>
                    </span>
                    <p className="text-[18px] font-black text-red-600 font-mono">
                      {dailySummary && dailySummary.totalDebt !== undefined
                        ? formatRs(dailySummary.totalDebt)
                        : formatRs(todaySales.reduce((sum, s) => sum + (s.creditAmount ? (s.creditAmount - (s.creditPaidAmount || 0)) : 0), 0))}
                    </p>
                    <p className="text-[9px] text-red-500 font-medium font-bold">Total outstanding credit (all-time) needing collection</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 shrink-0">
                    <CircleDollarSign size={18} />
                  </div>
                </div>

                {/* Broker Commission Card */}
                <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3 sm:p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-all">
                  <div className="space-y-1 min-w-0 flex-1">
                    <span className="text-[9px] sm:text-[10px] font-black text-orange-800 uppercase tracking-wider flex items-center gap-1">
                      <span className="hidden sm:inline">🤝 Broker Commission Outstanding (ගෙවීමට ඇති කොමිස්)</span>
                      <span className="sm:hidden">🤝 Broker Due</span>
                    </span>
                    <p className="text-[18px] font-black text-orange-600 font-mono">
                      {dailySummary && dailySummary.totalUnpaidBroker !== undefined
                        ? formatRs(dailySummary.totalUnpaidBroker)
                        : formatRs(todaySales.reduce((sum, s) => sum + (s.brokerStatus === 'unpaid' ? (s.brokerCommission || 0) : 0), 0))}
                    </p>
                    <p className="text-[9px] text-orange-500 font-medium font-bold">Total unpaid broker commissions (all-time) tracked from referrals</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 shrink-0">
                    <Users size={18} />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="relative z-10 flex items-center gap-0.5 sm:gap-1 px-2 sm:px-6 pt-2 sm:pt-3 border-b border-white/70 bg-white/70 backdrop-blur-lg overflow-x-auto no-scrollbar">
                {([{
                  id: 'pulse', label: 'Live Pulse', icon: Activity },
                  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
                  { id: 'advanced', label: 'Advanced', icon: TrendingUp },
                  { id: 'lifetime', label: 'Lifetime', icon: Calendar },
                  { id: 'transactions', label: 'Transactions', icon: FileText },
                  { id: 'inventory', label: 'Inventory', icon: Package },
                  { id: 'payments', label: 'Payments', icon: CreditCard },
                ] as const).map(t => {
                  const Icon = t.icon;
                  const active = analyticsTab === t.id;
                  return (
                    <button key={t.id} onClick={() => setAnalyticsTab(t.id)}
                      className={`flex items-center gap-0.5 sm:gap-1.5 px-2 sm:px-3.5 py-2 rounded-t-lg text-[9px] sm:text-[11px] font-semibold whitespace-nowrap transition-all ${active ? 'bg-brand-primary text-white border-b-2 border-brand-secondary shadow-sm' : 'text-slate-500 hover:text-slate-700 border-b-2 border-transparent hover:bg-white/60'}`}>
                        <Icon size={12} /> <span className="hidden sm:inline">{t.label}</span>
                        <span className="sm:hidden">{t.label.split(' ')[0]}</span>
                      </button>
                  );
                })}
                {/* 📅 Date picker — scope Live Pulse / Analytics / Transactions to any day */}
                <div className="ml-auto flex items-center gap-1 sm:gap-1.5 pr-0.5 sm:pr-1 shrink-0">
                  <Calendar size={12} className="text-slate-400 shrink-0 hidden sm:block" />
                  <input
                    type="date"
                    value={analyticsDate}
                    max={(() => {
                      const now = new Date();
                      const lk = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Colombo' }));
                      const y = lk.getFullYear(), m = String(lk.getMonth() + 1).padStart(2, '0'), d = String(lk.getDate()).padStart(2, '0');
                      return `${y}-${m}-${d}`;
                    })()}
                    onChange={(e) => { if (e.target.value) setAnalyticsDate(e.target.value); }}
                    className="text-[9px] sm:text-[11px] font-semibold text-slate-700 bg-white border-2 border-slate-300 rounded-lg px-1.5 sm:px-2 py-0.5 sm:py-1 outline-none focus:border-sky-500 transition-all w-[100px] sm:w-auto"
                  />
                </div>
              </div>

              {/* Body */}
              <div className="relative z-10 flex-1 overflow-auto bg-slate-50/50">
                {/* LIVE PULSE — Hero Revenue Trajectory + Activity Split */}
                {analyticsTab === 'pulse' && (
                  <div className="p-3 sm:p-6 space-y-4">
                    {/* Hero Chart: Today's Revenue Trajectory */}
                    <div className="rounded-xl sm:rounded-2xl border border-indigo-100 bg-white/90 backdrop-blur-md p-4 sm:p-5 shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center shadow-sm">
                            <TrendingUp size={14} className="text-white" />
                          </div>
                          <div>
                            <h3 className="text-[12px] sm:text-[14px] font-black text-slate-800">Revenue Trajectory — {analyticsDate}</h3>
                            <p className="text-[9px] sm:text-[10px] text-slate-500">Hourly sales volume</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-500">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand-primary" /> Revenue</span>
                          {todaySales.length === 0 && (
                            <span className="px-1.5 py-0.5 rounded-md bg-brand-primary-50 text-brand-primary-700 border border-brand-primary/20 text-[8px] font-bold">Projection</span>
                          )}
                        </div>
                      </div>
                      <div className="h-44 sm:h-52">
                        <ResponsiveContainer width="100%" height="100%">
                          {todaySales.length > 0 && analytics.hourBuckets.length > 0 ? (
                            <AreaChart data={(() => {
                              const pts = analytics.hourBuckets.map((h: any) => ({ hour: `${String(h.hour).padStart(2, '0')}:00`, revenue: h.revenue }));
                              if (pts.length < 2) pts.unshift({ hour: '00:00', revenue: 0 });
                              return pts;
                            })()}>
                              <defs>
                                <linearGradient id="heroRevGrad" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.25} />
                                  <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={2} />
                              <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.9)', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px', backdropFilter: 'blur(8px)' }} formatter={(value: any) => [formatRs(value), 'Revenue']} />
                              <Area type="monotone" dataKey="revenue" stroke="#0ea5e9" strokeWidth={2.5} fill="url(#heroRevGrad)" dot={false} activeDot={{ r: 4, stroke: '#0ea5e9', strokeWidth: 2, fill: 'white' }} />
                            </AreaChart>
                          ) : (
                            <AreaChart data={Array.from({ length: 12 }, (_, i) => ({ hour: `${i * 2}:00`, revenue: Math.max(0, (i * 75 + Math.sin(i) * 200 + 100)) }))}>
                              <defs>
                                <linearGradient id="heroRevGradEmpty" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.15} />
                                  <stop offset="95%" stopColor="#94a3b8" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: '#cbd5e1' }} axisLine={false} tickLine={false} />
                              <YAxis tick={{ fontSize: 9, fill: '#cbd5e1' }} axisLine={false} tickLine={false} />
                              <Tooltip contentStyle={{ backgroundColor: 'rgba(255,255,255,0.9)', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px' }} formatter={(value: any) => [formatRs(value), 'Projected']} />
                              <Area type="monotone" dataKey="revenue" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 4" fill="url(#heroRevGradEmpty)" dot={false} />
                            </AreaChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Split: Recent Transactions + Top Items */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Recent Transactions */}
                      <div className="rounded-xl sm:rounded-2xl border border-brand-primary/10 bg-white/90 backdrop-blur-sm p-4 sm:p-5 shadow-sm">
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Clock size={12} className="text-emerald-600" />
                          </div>
                          <h3 className="text-[11px] sm:text-[13px] font-bold text-slate-800">Recent Transactions</h3>
                          {todaySales.length > 0 && (
                            <span className="ml-auto text-[9px] font-mono text-slate-400">{todaySales.length} today</span>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          {todaySales.length === 0 ? (
                            <div className="flex items-center justify-center h-24 text-[11px] text-slate-400">
                              <Clock size={14} className="mr-2 opacity-30" /> No sales yet today
                            </div>
                          ) : (
                            todaySales.slice(0, 5).map(t => {
                              const remainingDebt = t.creditAmount ? (t.creditAmount - (t.creditPaidAmount || 0)) : 0;
                              return (
                                <button key={t.id} onClick={() => setForensicTx(t)}
                                  className="group w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-brand-primary-50/50 border border-transparent hover:border-brand-primary/15 transition-all text-left"
                                >
                                  <span className="text-[10px] font-mono text-slate-400 w-14 shrink-0">{new Date(t.time).toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}</span>
                                  <div className="flex-1">
                                    <div className="text-[12px] font-bold text-slate-800 font-mono">{formatRs(t.total)}</div>
                                    {remainingDebt > 0 ? (
                                      <p className="text-[8px] text-red-500 font-bold font-mono leading-none mt-0.5">
                                        🚨 Due: -{formatRs(remainingDebt)} {t.creditStatus === 'items_held' ? '(Held)' : '(Active)'}
                                      </p>
                                    ) : t.creditStatus === 'paid' ? (
                                      <p className="text-[8px] text-emerald-600 font-bold font-mono leading-none mt-0.5">
                                        ✓ Paid Credit
                                      </p>
                                    ) : null}
                                  </div>
                                  <span className={`px-1.5 py-0.5 rounded-md text-[8px] font-bold uppercase border ${
                                    t.method === 'cash' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                    t.method === 'card' ? 'bg-violet-50 text-violet-600 border-violet-200' :
                                    t.method === 'credit' ? 'bg-red-50 text-red-600 border-red-200' :
                                    'bg-sky-50 text-sky-600 border-sky-200'
                                  }`}>{t.method === 'credit' ? 'Credit' : t.method}</span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl sm:rounded-2xl border border-brand-primary/10 bg-white/90 backdrop-blur-sm p-4 sm:p-5 shadow-sm">
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Award size={12} className="text-amber-600" />
                          </div>
                          <h3 className="text-[11px] sm:text-[13px] font-bold text-slate-800">Top Performing Items</h3>
                        </div>
                        <div className="space-y-2">
                          {(() => {
                            const itemCounts: Record<string, { name: string; emoji: string; qty: number; rev: number }> = {};
                            todaySales.forEach(t => (t.cart || []).forEach((ci: any) => {
                              const key = ci.product?.id ?? ci.productId;
                              if (!itemCounts[key]) itemCounts[key] = { name: ci.product?.name || ci.productName || 'Item', emoji: ci.product?.emoji || '📦', qty: 0, rev: 0 };
                              itemCounts[key].qty += ci.qty || 0;
                              itemCounts[key].rev += (ci.product?.price || ci.unitPrice || 0) * (ci.qty || 0);
                            }));
                            const top = Object.values(itemCounts).sort((a, b) => b.rev - a.rev).slice(0, 3);
                            const maxRev = top[0]?.rev || 1;
                            return top.length > 0 ? top.map((item, idx) => (
                              <div key={item.name} className="flex items-center gap-3 px-2 py-2 rounded-lg bg-slate-50/50 border border-slate-100/50">
                                <span className="text-lg w-7 text-center">{item.emoji}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-semibold text-slate-700 truncate">{item.name}</span>
                                    <span className="text-[10px] font-bold text-slate-800 font-mono ml-2">{formatRs(item.rev)}</span>
                                  </div>
                                  <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                    <div className={`h-full rounded-full ${['bg-gradient-to-r from-brand-primary to-brand-secondary','bg-gradient-to-r from-emerald-400 to-emerald-500','bg-gradient-to-r from-violet-400 to-violet-500'][idx]}`} style={{ width: `${Math.max(8, (item.rev / maxRev) * 100)}%` }} />
                                  </div>
                                  <span className="text-[8px] text-slate-400 font-medium">{item.qty} units sold</span>
                                </div>
                              </div>
                            )) : (
                              <div className="flex items-center justify-center h-24 text-[11px] text-slate-400">
                                <Award size={14} className="mr-2 opacity-30" /> No items sold yet
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 📋 TRANSACTIONS AUDIT & HISTORY MANAGEMENT PANEL */}
                {analyticsTab === 'transactions' && (
                  <div className="p-3 sm:p-6 space-y-4">
                    <div className="rounded-xl sm:rounded-2xl border border-brand-primary/10 bg-white/95 backdrop-blur-sm p-4 sm:p-5 shadow-sm space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200">
                        <div>
                          <h3 className="text-[12px] sm:text-[14px] font-black text-slate-800">Transactions Audit — {analyticsDate}</h3>
                          <p className="text-[10px] text-slate-500">Search, view details, void sales, and settle broker commissions for the selected date</p>
                        </div>
                        {/* Search Input + Scan Receipt */}
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <div className="relative flex-1 sm:w-64">
                            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                              type="text"
                              placeholder="Search by ID, customer, broker…"
                              className="w-full pl-9 pr-3 py-1.5 rounded-xl bg-white border-2 border-slate-200 text-[11px] font-medium text-slate-800 outline-none focus:border-brand-primary focus:bg-white transition-all shadow-sm"
                              value={txQuery}
                              onChange={(e) => { setTxQuery(e.target.value); setTxPage(0); }}
                            />
                            {txLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />}
                          </div>
                          <button
                            onClick={() => setReceiptScanOpen(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-primary/10 border-2 border-brand-primary/20 text-[11px] font-bold text-brand-primary hover:bg-brand-primary/20 transition-all whitespace-nowrap"
                            title="Scan receipt barcode to look up transaction"
                          >
                            📷 Scan Receipt
                          </button>
                        </div>
                      </div>

                      {/* Transaction List Table */}
                      <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-[11px]">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500 font-extrabold uppercase tracking-wider bg-slate-50/60">
                              <th className="py-2.5 px-3">Time</th>
                              <th className="py-2.5 px-3 hidden sm:table-cell">ID / Ref</th>
                              <th className="py-3 px-3 hidden md:table-cell">Customer</th>
                              <th className="py-2.5 px-3">Method</th>
                              <th className="py-2.5 px-3 text-right">Total</th>
                              <th className="py-3 px-3 hidden lg:table-cell">Debt / Brokerage</th>
                              <th className="py-2.5 px-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {(() => {
                              const q = txQuery.toLowerCase();
                              const filteredTxs = historicalTxs.filter(t => {
                                if (!q) return true;
                                const refMatch = t.id.toLowerCase().includes(q);
                                const custMatch = t.customer?.name?.toLowerCase().includes(q) || t.customer?.phone?.includes(q);
                                const brokerMatch = t.brokerName?.toLowerCase().includes(q);
                                return refMatch || custMatch || brokerMatch;
                              });

                              if (filteredTxs.length === 0) {
                                return (
                                  <tr>
                                  <td colSpan={7} className="py-8 text-center text-slate-400 text-[11px]">
                                  {(() => {
                                    if (!q && historicalTxs.length === 0 && !txLoading) return 'No transactions found for this date';
                                    if (!q && historicalTxs.length === 0 && txLoading) return 'Loading transactions...';
                                    if (q) return 'No transactions match your search query';
                                    return 'No transactions found';
                                  })()}
                                    </td>
                                  </tr>
                                );
                              }

                              return filteredTxs.map(t => {
                                const remainingDebt = t.creditAmount ? (t.creditAmount - (t.creditPaidAmount || 0)) : 0;
                                return (
                                  <tr key={t.id} className="hover:bg-brand-primary-50/40 transition-colors group">
                                    <td className="py-3 px-3 text-slate-500 font-mono">
                                      {new Date(t.time).toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}
                                    </td>
                                    <td className="py-3 px-3 font-mono font-bold text-slate-700 hidden sm:table-cell">
                                      {t.id}
                                    </td>
                                    <td className="py-3 px-3 hidden md:table-cell">
                                      {t.customer ? (
                                        <div>
                                          <div className="font-bold text-slate-800">{t.customer.name}</div>
                                          <div className="text-[9px] text-slate-400 font-mono">{t.customer.phone || 'No Phone'}</div>
                                          {t.customer.customFieldValue && (
                                            <div className="text-[9px] text-brand-primary-700 font-mono mt-0.5">
                                              {customFieldLabel}: {t.customer.customFieldValue}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-slate-400">—</span>
                                      )}
                                    </td>
                                    <td className="py-3 px-3">
                                      <span className={`px-2 py-0.5 rounded text-[8px] font-bold uppercase border ${
                                        t.method === 'cash' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                                        t.method === 'card' ? 'bg-violet-50 text-violet-600 border-violet-200' :
                                        t.method === 'credit' ? 'bg-rose-50 text-rose-600 border-rose-200' :
                                        'bg-sky-50 text-sky-600 border-sky-200'
                                      }`}>{t.method}</span>
                                    </td>
                                    <td className="py-3 px-3 text-right font-bold text-slate-800 font-mono">
                                      {formatRs(t.total)}
                                    </td>
                                    <td className="py-3 px-3 space-y-1 hidden lg:table-cell">
                                      {remainingDebt > 0 ? (
                                        <span className="inline-block px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200 text-[8px] font-black font-mono">
                                          🚨 Debt: -{formatRs(remainingDebt)}
                                        </span>
                                      ) : t.creditAmount ? (
                                        <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-200 text-[8px] font-bold">
                                          ✓ Debt Settled
                                        </span>
                                      ) : null}

                                      {/* Broker badge */}
                                      {t.brokerName && (
                                        <div className="pt-0.5">
                                          <span className="block text-[8px] font-black text-slate-600">
                                            Broker: {t.brokerName}
                                          </span>
                                          <span className={`inline-block px-1.5 py-0.5 rounded text-[8px] font-black font-mono mt-0.5 ${
                                            t.brokerStatus === 'paid'
                                              ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                                              : 'bg-amber-100 text-amber-700 border border-amber-200'
                                          }`}>
                                            Commission: {formatRs(t.brokerCommission || 0)} ({t.brokerStatus === 'paid' ? 'Paid' : 'Unpaid'})
                                          </span>
                                        </div>
                                      )}
                                    </td>
                                    <td className="py-3 px-3 text-right">
                                      <div className="flex justify-end gap-1.5">
                                        {/* Pay Commission Button */}
                                        {t.brokerName && t.brokerStatus === 'unpaid' && (
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              const ok = await confirmAction({
                                                title: `Pay Broker Commission?`,
                                                message: `Are you ready to pay the commission of Rs. ${t.brokerCommission?.toLocaleString()} to ${t.brokerName}?`,
                                                confirmLabel: 'Pay Commission'
                                              });
                                              if (ok) {
                                                try {
                                                  const token = localStorage.getItem('kangavena:cashierToken') || '';
                                                  const res = await fetch(`/api/admin/broker-commission/${t.id}/pay`, {
                                                    method: 'PATCH',
                                                    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                                    body: JSON.stringify({ amount: t.brokerCommission, notes: 'Settle commission' })
                                                  });
                                                  const data = await res.json();
                                                  if (res.ok) {
                                                    showToast('Commission paid successfully!', 'success');
                                                    SoundManager.play('cha_ching');
                                                    fetchTodaySales();
                                                  } else {
                                                    showToast(data.error || 'Failed to pay commission', 'error');
                                                  }
                                                } catch (err: any) {
                                                  showToast(err.message, 'error');
                                                }
                                              }
                                            }}
                                            className="px-2 py-1 rounded-lg bg-brand-primary hover:bg-brand-secondary text-white font-black text-[9px] uppercase tracking-wider shadow-sm shadow-brand-primary/15 active:scale-95 transition-all"
                                          >
                                            Settle Broker
                                          </button>
                                        )}

                                        <button
                                          onClick={() => setForensicTx(t)}
                                          className="px-2 py-1 rounded-lg bg-white hover:bg-brand-primary-50 text-brand-primary font-bold text-[9px] uppercase border border-slate-200 hover:border-brand-primary/30 active:scale-95 transition-all"
                                        >
                                          View Details
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {historicalCount > 15 && (
                        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                          <p className="text-[10px] text-slate-500">
                            Showing {txPage * 15 + 1}–{Math.min((txPage + 1) * 15, historicalCount)} of {historicalCount}
                          </p>
                          <div className="flex gap-1.5">
                            <button
                              disabled={txPage === 0}
                              onClick={() => setTxPage(p => Math.max(0, p - 1))}
                              className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[10px] font-bold text-slate-700 disabled:opacity-30 transition-all"
                            >
                              ← Prev
                            </button>
                            <button
                              disabled={(txPage + 1) * 15 >= historicalCount}
                              onClick={() => setTxPage(p => p + 1)}
                              className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-[10px] font-bold text-slate-700 disabled:opacity-30 transition-all"
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      )}

                      {/* 🧾 Scanned Receipt Transaction Detail Card */}
                      {scannedTx && (
                        <div className="rounded-xl border-2 border-emerald-500/30 bg-emerald-50 p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                                <Receipt size={14} className="text-emerald-600" />
                              </div>
                              <div>
                                <p className="text-[12px] font-black text-emerald-800">Scanned Receipt — Full Details</p>
                                <p className="text-[9px] text-emerald-600 font-mono">{scannedTx.txRef} • {scannedTx.cashierName || 'Unknown cashier'}</p>
                              </div>
                            </div>
                            <button onClick={() => setScannedTx(null)} className="p-1 rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors">
                              <X size={14} />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                            <div className="bg-white rounded-lg p-2 border border-emerald-100">
                              <p className="text-slate-500 font-mono uppercase">Date/Time</p>
                              <p className="font-bold text-slate-800">{new Date(scannedTx.time).toLocaleString()}</p>
                            </div>
                            <div className="bg-white rounded-lg p-2 border border-emerald-100">
                              <p className="text-slate-500 font-mono uppercase">Total</p>
                              <p className="font-black text-emerald-700">Rs. {scannedTx.total?.toLocaleString()}</p>
                            </div>
                            <div className="bg-white rounded-lg p-2 border border-emerald-100">
                              <p className="text-slate-500 font-mono uppercase">Payment</p>
                              <p className="font-bold text-slate-800">{scannedTx.paymentMethod}</p>
                            </div>
                            <div className="bg-white rounded-lg p-2 border border-emerald-100">
                              <p className="text-slate-500 font-mono uppercase">Customer</p>
                              <p className="font-bold text-slate-800">{scannedTx.customer?.name || 'Walk-in'}</p>
                            </div>
                          </div>
                          {/* Items list */}
                          {scannedTx.items && scannedTx.items.length > 0 && (
                            <div className="bg-white rounded-lg border border-emerald-100 overflow-hidden">
                              <div className="grid grid-cols-4 gap-2 px-3 py-1.5 text-[9px] font-bold text-slate-500 uppercase bg-emerald-50 border-b border-emerald-100">
                                <span>Item</span><span className="text-right">Qty</span><span className="text-right">Unit Price</span><span className="text-right">Total</span>
                              </div>
                              {scannedTx.items.map((item: any, i: number) => (
                                <div key={i} className="grid grid-cols-4 gap-2 px-3 py-1.5 text-[10px] text-slate-700 border-b border-slate-50 last:border-0">
                                  <span className="font-medium truncate">{item.productName}</span>
                                  <span className="text-right">{item.qty}</span>
                                  <span className="text-right">Rs. {parseFloat(item.unitPrice).toLocaleString()}</span>
                                  <span className="text-right font-bold">Rs. {(parseFloat(item.unitPrice) * parseFloat(item.qty)).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Extra info: discount, tax, credit, broker, loyalty */}
                          <div className="flex flex-wrap gap-2 text-[9px]">
                            {scannedTx.discount > 0 && <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">Discount: {scannedTx.discountType === 'percent' ? scannedTx.discount + '%' : 'Rs. ' + scannedTx.discount}</span>}
                            {scannedTx.tax > 0 && <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-bold">Tax: Rs. {scannedTx.tax}</span>}
                            {scannedTx.creditAmount > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold">Credit: Rs. {scannedTx.creditAmount} (Paid: Rs. {scannedTx.creditPaidAmount})</span>}
                            {scannedTx.brokerName && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">Broker: {scannedTx.brokerName} — Rs. {scannedTx.brokerCommission}</span>}
                            {scannedTx.loyaltyPointsEarned > 0 && <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 font-bold">+{scannedTx.loyaltyPointsEarned} pts earned</span>}
                            {scannedTx.loyaltyPointsRedeemed > 0 && <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-bold">-{scannedTx.loyaltyPointsRedeemed} pts redeemed</span>}
                          </div>
                        </div>
                      )}
                      {scannedTxLoading && (
                        <div className="flex items-center justify-center gap-2 py-4 text-[11px] text-slate-500">
                          <Loader2 size={14} className="animate-spin" /> Looking up receipt…
                        </div>
                      )}
                      {scannedTxError && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[11px] text-red-700 font-medium flex items-center justify-between">
                          <span>{scannedTxError}</span>
                          <button onClick={() => setScannedTxError('')} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Receipt Scanner Camera Modal */}
                <AnimatePresence>
                  {receiptScanOpen && (
                    <CameraScanner
                      onDetected={handleReceiptScan}
                      onClose={() => setReceiptScanOpen(false)}
                    />
                  )}
                </AnimatePresence>

                {analyticsTab === 'analytics' && (
                  <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
                    {/* 📋 Estimates Summary */}
                    <EstimatesAnalyticsCard storeApiKey={storeApiKey} formatRs={formatRs} />

                    {/* 🎯 Loyalty & Rewards Settings */}
                    <div className="rounded-xl sm:rounded-2xl border-2 border-slate-500 bg-slate-800 p-4 sm:p-5 shadow-lg">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                          <Award size={16} className="text-amber-400" />
                        </div>
                        <div>
                          <h3 className="text-[13px] font-bold text-white">Loyalty & Rewards Settings</h3>
                          <p className="text-[10px] text-slate-400">Configure how customers earn and redeem points</p>
                        </div>
                      </div>

                      {/* Enable Toggle */}
                      <div
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all mb-4 ${
                          loyaltyForm.enabled
                            ? 'bg-emerald-500/10 border-emerald-500/30'
                            : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                        }`}
                        onClick={() => setLoyaltyForm(f => ({ ...f, enabled: !f.enabled }))}
                      >
                        <div className={`w-10 h-6 rounded-full relative transition-colors ${
                          loyaltyForm.enabled ? 'bg-emerald-500' : 'bg-slate-600'
                        }`}>
                          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                            loyaltyForm.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <p className="text-[11px] font-bold text-white">Enable Loyalty Program</p>
                          <p className="text-[9px] text-slate-400">{loyaltyForm.enabled ? 'Customers earn points on every purchase' : 'Loyalty program is currently disabled'}</p>
                        </div>
                      </div>

                      {/* Rates Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Points earned per 1 Rupee</label>
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            value={loyaltyForm.pointsPerRupee}
                            onChange={(e) => setLoyaltyForm(f => ({ ...f, pointsPerRupee: e.target.value }))}
                            className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500 bg-slate-900 text-white text-[12px] font-mono outline-none focus:border-amber-500 transition-colors placeholder:text-slate-600"
                            placeholder="0.01"
                          />
                          <p className="text-[9px] text-slate-500 mt-1">
                            e.g. 0.01 = 1 point per Rs. 100 spent
                          </p>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Rupee value per 1 Point</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={loyaltyForm.rupeePerPoint}
                            onChange={(e) => setLoyaltyForm(f => ({ ...f, rupeePerPoint: e.target.value }))}
                            className="w-full px-3 py-2.5 rounded-xl border-2 border-slate-500 bg-slate-900 text-white text-[12px] font-mono outline-none focus:border-amber-500 transition-colors placeholder:text-slate-600"
                            placeholder="0.50"
                          />
                          <p className="text-[9px] text-slate-500 mt-1">
                            e.g. 0.50 = 1 point is worth Rs. 0.50 on redemption
                          </p>
                        </div>
                      </div>

                      {/* Example Calculation */}
                      <div className="rounded-lg bg-slate-900/60 border border-slate-700 px-3 py-2 mb-4">
                        <p className="text-[10px] text-slate-400 font-medium">
                          📐 <span className="text-slate-300">Example:</span> A Rs. 1,000 purchase earns{' '}
                          <span className="text-amber-400 font-bold">
                            {((parseFloat(loyaltyForm.pointsPerRupee) || 0) * 1000).toFixed(1)} points
                          </span>{' '}
                          (worth Rs. {(((parseFloat(loyaltyForm.pointsPerRupee) || 0) * 1000) * (parseFloat(loyaltyForm.rupeePerPoint) || 0)).toFixed(2)} on redemption)
                        </p>
                      </div>

                      {/* Save Button */}
                      <button
                        onClick={saveLoyaltySettings}
                        disabled={loyaltySaving}
                        className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-bold uppercase tracking-wider shadow-md shadow-amber-500/20 disabled:opacity-50 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                      >
                        {loyaltySaving ? 'Saving...' : 'Save Settings'}
                      </button>
                    </div>

                    {/* Charts Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                      {/* Hourly Sales Trend - AreaChart */}
                      <div className="rounded-xl sm:rounded-2xl border border-sky-100 bg-white p-3 sm:p-5 shadow-sm">
                        <div className="flex items-center gap-2 mb-3 sm:mb-4">
                          <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                            <TrendingUp size={14} className="text-sky-600" />
                          </div>
                          <div>
                            <h3 className="text-[12px] sm:text-[13px] font-bold text-slate-800">Hourly Sales Trend</h3>
                            <p className="text-[9px] sm:text-[10px] text-slate-500">Sales volume throughout the day</p>
                          </div>
                        </div>
                        {analytics.hourBuckets.length > 0 ? (
                          <div className="h-40 sm:h-48">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={(() => {
  const pts = analytics.hourBuckets.map((h: any) => ({ hour: `${String(h.hour).padStart(2, '0')}:00`, sales: h.count, revenue: h.revenue }));
  if (pts.length < 2) pts.unshift({ hour: '00:00', sales: 0, revenue: 0 });
  return pts;
})()}>
                                <defs>
                                  <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                <XAxis 
                                  dataKey="hour" 
                                  tick={{ fontSize: 9, fill: '#64748b' }} 
                                  axisLine={false}
                                  tickLine={false}
                                  interval={2}
                                />
                                <YAxis 
                                  tick={{ fontSize: 9, fill: '#64748b' }} 
                                  axisLine={false}
                                  tickLine={false}
                                />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'white', 
                                    border: '1px solid #e2e8f0', 
                                    borderRadius: '8px',
                                    fontSize: '11px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                  }}
                                  formatter={(value: any, name: any) => [value, name === 'sales' ? 'Sales' : 'Revenue']}
                                />
                                <Area 
                                  type="monotone" 
                                  dataKey="sales" 
                                  stroke="#0ea5e9" 
                                  strokeWidth={2}
                                  fill="url(#salesGradient)" 
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div className="h-40 flex items-center justify-center text-slate-400 text-[11px]">No sales data yet</div>
                        )}
                      </div>

                      {/* Payment Breakdown - PieChart */}
                      <div className="rounded-xl sm:rounded-2xl border border-sky-100 bg-white p-3 sm:p-5 shadow-sm">
                        <div className="flex items-center gap-2 mb-3 sm:mb-4">
                          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                            <CreditCard size={14} className="text-violet-600" />
                          </div>
                          <div>
                            <h3 className="text-[12px] sm:text-[13px] font-bold text-slate-800">Payment Breakdown</h3>
                            <p className="text-[9px] sm:text-[10px] text-slate-500">Cash vs Card vs Mobile</p>
                          </div>
                        </div>
                        {(() => {
                          const paymentData = todaySales.reduce((acc, t) => {
                            const method = t.method === 'mobile' ? 'Mobile' : t.method === 'card' ? 'Card' : 'Cash';
                            acc[method] = (acc[method] || 0) + t.total;
                            return acc;
                          }, {} as Record<string, number>);
                          const data = Object.entries(paymentData).map(([name, value]) => ({ name, value }));
                          const COLORS = ['#0ea5e9', '#10b981', '#8b5cf6'];
                          return data.length > 0 ? (
                            <div className="h-40 sm:h-48 flex items-center">
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={data}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={42}
                                    outerRadius={68}
                                    paddingAngle={5}
                                    stroke="none"
                                    dataKey="value"
                                  >
                                    {data.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Legend 
                                    verticalAlign="bottom"
                                    align="center"
                                    layout="horizontal"
                                    iconType="circle"
                                    wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }}
                                    formatter={(value: any, entry: any) => (
                                      <span style={{ fontSize: '10px', color: '#475569' }}>{value}: {formatRs(entry.payload.value)}</span>
                                    )}
                                  />
                                  <Tooltip 
                                    formatter={(value: any) => formatRs(value)}
                                    contentStyle={{ 
                                      backgroundColor: 'white', 
                                      border: '1px solid #e2e8f0', 
                                      borderRadius: '8px',
                                      fontSize: '11px'
                                    }}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="h-40 flex items-center justify-center text-slate-400 text-[11px]">No payment data yet</div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Category Breakdown */}
                    <div className="rounded-xl sm:rounded-2xl border border-sky-100 bg-white p-3 sm:p-5 shadow-sm">
                      <div className="flex items-center gap-2 mb-3 sm:mb-4">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Target size={14} className="text-amber-600" />
                        </div>
                        <h3 className="text-[12px] sm:text-[13px] font-bold text-slate-800">Category Performance</h3>
                      </div>
                      {analytics.catRanking.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                          {analytics.catRanking.slice(0, 8).map((c, i) => (
                            <div key={c.name} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-slate-50 border border-slate-100">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white shadow-sm flex items-center justify-center text-lg">
                                {getCategoryEmoji(c.name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] sm:text-[11px] font-semibold text-slate-700 truncate">{c.name}</div>
                                <div className="text-[9px] sm:text-[10px] text-slate-500">{c.share.toFixed(1)}%</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[11px] text-slate-400 py-4">No category data yet</p>
                      )}
                    </div>

                    {/* Weekly Sales Trends — fetched lazily from /api/analytics/sales-trends */}
                    {weeklyTrendsData && weeklyTrendsData.bestDay && (() => {
                      const DAY_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
                      const DAY_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                      const chartData = DAY_FULL.map((full, i) => ({
                        day:     DAY_SHORT[i],
                        revenue: Math.round(weeklyTrendsData.dayTotals[full] || 0),
                        isBest:  full === weeklyTrendsData.bestDay,
                        isWorst: full === weeklyTrendsData.worstDay,
                      }));
                      return (
                        <div className="rounded-xl sm:rounded-2xl border border-sky-100 bg-white p-3 sm:p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-3 sm:mb-4">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                              <TrendingUp size={14} className="text-emerald-600" />
                            </div>
                            <div>
                              <h3 className="text-[12px] sm:text-[13px] font-bold text-slate-800">Weekly Sales Trends</h3>
                              <p className="text-[9px] sm:text-[10px] text-slate-500">
                                Best: <span className="text-emerald-600 font-semibold">{weeklyTrendsData.bestDay}</span>
                                {weeklyTrendsData.worstDay && <> &middot; Slow: <span className="text-red-500 font-semibold">{weeklyTrendsData.worstDay}</span></>}
                              </p>
                            </div>
                          </div>
                          <div className="h-40 sm:h-48">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={chartData} barSize={28} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 9, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `Rs.${(v/1000).toFixed(0)}k` : `Rs.${v}`} />
                                <Tooltip
                                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '11px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                  formatter={(value: number) => [`Rs. ${value.toLocaleString()}`, 'Lifetime Revenue']}
                                />
                                <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                                  {chartData.map((entry) => (
                                    <Cell
                                      key={entry.day}
                                      fill={entry.isBest ? '#10b981' : entry.isWorst ? '#ef4444' : '#0ea5e9'}
                                      opacity={entry.isBest ? 1 : entry.isWorst ? 0.7 : 0.85}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* LIFETIME ANALYTICS (all-time records) */}
                {analyticsTab === 'lifetime' && (
                  <LifetimeAnalytics storeApiKey={storeApiKey} />
                )}

                {/* ADVANCED ANALYTICS (period comparison, customers, products, staff) */}
                {analyticsTab === 'advanced' && (
                  <AdvancedAnalytics storeApiKey={storeApiKey} isReadOnly={false} />
                )}

                {/* INVENTORY HEALTH - Light Theme */}
                {analyticsTab === 'inventory' && (
                  <div className="p-3 sm:p-6">
                    <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="text-[10px] sm:text-[11px] font-medium text-slate-500">Velocity over {analytics.hoursElapsed.toFixed(1)}h session</div>
                        
                        {/* 📋 GENERATE REORDER SHEET BUTTON */}
                        <button
                          onClick={() => {
                            Haptics.fire('tap');
                            const lowStockItems = analytics.inventoryHealth.filter(row => row.priority === 'high' || row.priority === 'medium');
                            if (lowStockItems.length === 0) {
                              showToast('No low-stock items detected!', 'success');
                              return;
                            }
                            let sheet = `=== 📋 KANGAVENA REORDER SHEET ===\n`;
                            sheet += `Store: ${storeName || 'Your Store'}\n`;
                            sheet += `Date: ${new Date().toLocaleDateString('en-LK')} · ${new Date().toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}\n`;
                            sheet += `-------------------------------------------\n\n`;
                            sheet += `Please supply the following low-stock items:\n\n`;
                            lowStockItems.forEach((row, idx) => {
                              const suggestQty = row.priority === 'high' ? 50 : 20;
                              sheet += `${idx + 1}. ${row.product.name} (${row.product.category})\n`;
                              sheet += `   Current Stock: ${row.product.stockCount} ${row.product.unitType || 'Pcs'}\n`;
                              sheet += `   Suggested Reorder Qty: ${suggestQty} ${row.product.unitType || 'Pcs'}\n\n`;
                            });
                            sheet += `-------------------------------------------\n`;
                            sheet += `Generated via Luna POS.OS Brain 🚀`;
                            setReorderSheet(sheet);
                          }}
                          className="px-2 py-1 rounded bg-sky-500 hover:bg-sky-600 text-white font-black text-[9px] uppercase tracking-wider shadow-sm transition-colors"
                        >
                          📋 Reorder Sheet
                        </button>
                      </div>
                      <div className="flex items-center gap-3 text-[9px] sm:text-[10px] font-medium">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" /> High</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> Medium</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Low</span>
                      </div>
                    </div>

                    {/* 📋 REORDER SHEET MODAL */}
                    <AnimatePresence>
                      {reorderSheet && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-3xl p-5 border border-sky-100 max-w-md w-full shadow-2xl space-y-4"
                          >
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                              <div>
                                <h3 className="text-[13px] font-black text-slate-800 uppercase tracking-wider">Low-Stock Reorder Sheet</h3>
                                <p className="text-[10px] text-slate-400">Copy this text to send to your suppliers via WhatsApp/Telegram</p>
                              </div>
                              <button onClick={() => setReorderSheet(null)} className="text-slate-400 hover:text-slate-600">
                                <X size={16} />
                              </button>
                            </div>
                            <textarea
                              readOnly
                              value={reorderSheet}
                              className="w-full h-64 p-3 rounded-2xl bg-slate-50 border border-slate-200 text-[11px] font-mono text-slate-700 outline-none resize-none"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  Haptics.fire('success');
                                  await navigator.clipboard.writeText(reorderSheet);
                                  showToast('Copied to clipboard!', 'success');
                                  setReorderSheet(null);
                                }}
                                className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black uppercase tracking-widest rounded-2xl shadow-lg shadow-emerald-600/25 active:scale-95 transition-all"
                              >
                                📋 Copy Reorder Text
                              </button>
                              <button
                                onClick={() => setReorderSheet(null)}
                                className="px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 text-[11px] font-bold uppercase tracking-wider rounded-2xl active:scale-95 transition-all"
                              >
                                Close
                              </button>
                            </div>
                          </motion.div>
                        </div>
                      )}
                    </AnimatePresence>

                    <div className="rounded-xl sm:rounded-2xl border border-sky-100 bg-white overflow-hidden shadow-sm">
                      <div className="hidden sm:grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_0.8fr] gap-3 px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-sky-50/50 border-b border-sky-100">
                        <span>Product</span><span>Category</span><span className="text-right">Stock</span><span className="text-right">Sold</span><span className="text-right">Velocity/hr</span><span className="text-center">Priority</span>
                      </div>
                      <div className="divide-y divide-slate-100 max-h-[50vh] overflow-auto">
                        {analytics.inventoryHealth.length === 0 ? (
                          <div className="px-4 py-8 text-center text-slate-400 text-[11px]">No inventory data</div>
                        ) : (
                          analytics.inventoryHealth.map(row => {
                            const priorityStyles: Record<string, string> = {
                              high: 'bg-rose-100 text-rose-700 border-rose-200',
                              medium: 'bg-amber-100 text-amber-700 border-amber-200',
                              low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                            };
                            return (
                              <div key={row.product.id} className="sm:grid sm:grid-cols-[1.5fr_1fr_1fr_1fr_1fr_0.8fr] gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-[11px] sm:text-[12px] items-center hover:bg-slate-50 transition-colors flex flex-col sm:flex-row">
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                  <span className="text-base">{row.product.emoji}</span>
                                  <span className="font-medium text-slate-700 truncate">{row.product.name}</span>
                                </div>
                                <span className="text-slate-500 text-[10px] sm:text-[11px] w-full sm:w-auto">{row.product.category}</span>
                                <span className={`font-mono text-right w-full sm:w-auto ${row.product.stockCount === 0 ? 'text-rose-600 font-bold' : 'text-slate-600'}`}>
                                  {row.product.stockCount === 0 ? 'OUT OF STOCK' : row.product.stockCount}
                                </span>
                                <span className="font-mono text-right text-slate-500 w-full sm:w-auto">{row.sold}</span>
                                <span className="font-mono text-right text-sky-600 font-medium w-full sm:w-auto">{row.velocity.toFixed(2)}</span>
                                <div className="flex justify-start sm:justify-center w-full sm:w-auto">
                                  <span className={`px-2 py-0.5 rounded-md border text-[9px] sm:text-[10px] font-bold uppercase ${priorityStyles[row.priority]}`}>
                                    {row.priority}
                                  </span>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 💳 Phase 10 — PAYMENT ANALYTICS */}
                {analyticsTab === 'payments' && (
                  <div className="p-3 sm:p-6">
                    <div className="mb-3 sm:mb-4 flex items-center justify-between">
                      <h3 className="text-sm font-bold text-slate-800">Payment Methods</h3>
                      <button
                        onClick={() => { fetchPaymentRecon(); }}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-sky-50 text-sky-600 text-[10px] font-bold hover:bg-sky-100 transition-colors"
                      >
                        <RefreshCw size={11} /> Refresh
                      </button>
                    </div>
                    {paymentReconLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <RefreshCw size={20} className="animate-spin text-slate-400" />
                      </div>
                    ) : !paymentRecon ? (
                      <div className="text-center py-12 text-slate-400 text-[11px]">
                        <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
                        Loading payment data...
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Summary cards with gross/net/refund breakdown */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {Object.entries(paymentRecon.summary || {}).map(([method, detail]: [string, any]) => {
                            const net = typeof detail === 'object' ? (detail.net || 0) : parseFloat(detail) || 0;
                            const gross = typeof detail === 'object' ? (detail.gross || 0) : net;
                            const refunded = typeof detail === 'object' ? (detail.refunded || 0) : 0;
                            const count = typeof detail === 'object' ? (detail.count || 0) : 0;
                            return (
                              <div key={method} className="bg-white border border-slate-200 rounded-xl p-2.5">
                                <div className="text-[9px] text-slate-400 uppercase tracking-wider font-mono">{method.replace(/_/g, ' ')}</div>
                                <div className="text-sm font-black text-slate-800 font-mono mt-0.5">{formatRs(net)}</div>
                                <div className="flex items-center gap-2 mt-1">
                                  {count > 0 && <span className="text-[8px] text-slate-400">{count} txns</span>}
                                  {refunded > 0 && <span className="text-[8px] text-rose-500">-{formatRs(refunded)} refunded</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Totals row */}
                        {paymentRecon.totalGross !== undefined && (
                          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-[10px] font-mono">
                            <span className="text-slate-500">Gross: {formatRs(paymentRecon.totalGross)}</span>
                            {paymentRecon.totalRefunded > 0 && <span className="text-rose-500">Refunded: -{formatRs(paymentRecon.totalRefunded)}</span>}
                            <span className="text-emerald-600 font-bold">Net: {formatRs(paymentRecon.totalNet)}</span>
                          </div>
                        )}
                        {/* Transaction list */}
                        {paymentRecon.transactions && paymentRecon.transactions.length > 0 && (
                          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
                              <span>Recent Payments</span>
                              <span className="text-slate-400 font-normal normal-case">{paymentRecon.transactions.length} shown</span>
                            </div>
                            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
                              {(paymentRecon.transactions || []).map((tx: any) => (
                                <div key={tx.id} className="flex items-center justify-between px-3 py-2 text-[11px]">
                                  <div className="flex flex-col">
                                    <span className="font-bold text-slate-700">{tx.method?.replace(/_/g, ' ')}{tx.provider ? ` (${tx.provider})` : ''}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-[9px] text-slate-400 font-mono">{tx.reference || '—'}</span>
                                      {tx.status && tx.status !== 'completed' && (
                                        <span className="text-[8px] px-1 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200 font-bold uppercase">{tx.status}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <span className="font-mono font-semibold text-slate-800">{formatRs(tx.amount)}</span>
                                    {tx.refundedAmount > 0 && (
                                      <p className="text-[8px] text-rose-500 font-mono">-{formatRs(tx.refundedAmount)}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </MasterDashboard>
        )}

      {/* 💳 BILLING FAB — manager only */}
      {!isCashierMode && (
        <StoreSettingsFAB apiKey={storeApiKey} storeName={storeName} />
      )}

      {/* 🎯 LUNA MARKETING BRAIN — admin only */}
      {!isCashierMode && (
        <LunaMarketingBrain
          isOpen={marketingBrainOpen}
          onClose={() => setMarketingBrainOpen(false)}
          storeApiKey={storeApiKey}
          storeName={storeName}
          analytics={analytics}
        />
      )}

      {/* 🚀 MARKETING CENTER — dedicated hub with Vault */}
      <MarketingCenter
        isOpen={marketingCenterOpen}
        onClose={() => setMarketingCenterOpen(false)}
        storeApiKey={storeApiKey}
        storeName={storeName}
        onTriggerLunaConsult={() => {
          if (!lunaOpen) setLunaOpen(true);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('kangavena:luna-prompt', { detail: { text: 'ව්‍යාපාර දියුණු කරන්නේ කෙසේද?' } }));
          }, 600);
        }}
        onReplayPlan={(plan) => {
          setLunaOpen(true);
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('kangavena:replay-strategic-plan', { detail: plan }));
          }, 80);
        }}
      />

      {/* 🔍 FORENSIC TRANSACTION MODAL */}
      <AnimatePresence>
        {forensicTx && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9997] bg-black/80 backdrop-blur-xl flex items-center justify-center p-4"
            onClick={e => { if (e.target === e.currentTarget) setForensicTx(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.95, y: 10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-md bg-[#0a0a15] border border-violet-500/25 rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(139,92,246,0.3)] flex flex-col max-h-[88vh]"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Receipt size={16} className="text-violet-300" />
                  <div>
                    <div className="text-[13px] font-black text-white">Transaction Forensics</div>
                    <div className="text-[9px] font-mono text-slate-600 uppercase tracking-wider">{forensicTx.id}</div>
                  </div>
                </div>
                <button onClick={() => setForensicTx(null)} className="w-7 h-7 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-500 hover:text-white">
                  <X size={14} />
                </button>
              </div>

              <div className="px-5 py-3 grid grid-cols-2 gap-2 text-[10px] font-mono border-b border-white/[0.04]">
                <div><span className="text-slate-600">Date</span><div className="text-white">{new Date(forensicTx.time).toLocaleDateString('en-LK')}</div></div>
                <div><span className="text-slate-600">Time</span><div className="text-white">{new Date(forensicTx.time).toLocaleTimeString('en-LK')}</div></div>
                <div><span className="text-slate-600">Cashier</span><div className="text-white">{forensicTx.cashierName}</div></div>
                <div><span className="text-slate-600">Method</span><div className="text-white uppercase">{forensicTx.method}</div></div>
              </div>

              {forensicTx.method === 'split' && forensicTx.splitDetails && Array.isArray(forensicTx.splitDetails) && (
                <div className="px-5 py-2 text-[9px] font-mono border-b border-white/[0.04] bg-white/[0.02] space-y-1">
                  <span className="text-slate-500 uppercase tracking-wider">Split Payment Details:</span>
                  {forensicTx.splitDetails.map((leg: any, idx: number) => (
                    <div key={idx} className="flex justify-between text-slate-300">
                      <span className="capitalize">{leg.method === 'qr_code' ? 'QR Code' : leg.method} {leg.provider ? `(${leg.provider})` : ''}</span>
                      <span>{formatRs(leg.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex-1 overflow-auto px-5 py-3">
                <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-600 mb-2">Line items · {forensicTx.items}</div>
                <div className="space-y-1.5">
                  {forensicTx.cart.map((ci, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] font-mono">
                      <span className="text-slate-400 flex-1 truncate">{ci.product.emoji} {ci.product.name}</span>
                      <span className="text-slate-600 w-12 text-right">×{ci.qty}</span>
                      <span className="text-slate-500 w-16 text-right">{formatRs(ci.product.price)}</span>
                      <span className="text-white w-20 text-right">{formatRs(ci.product.price * ci.qty)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-5 py-3 border-t border-white/[0.06] space-y-1">
                <div className="flex justify-between text-[10px] font-mono text-slate-500"><span>Subtotal</span><span>{formatRs(forensicTx.subtotal)}</span></div>
                {forensicTx.discountPct > 0 && (
                  <div className="flex justify-between text-[10px] font-mono text-violet-300"><span>Discount ({forensicTx.discountPct}%)</span><span>− {formatRs(forensicTx.discountAmt)}</span></div>
                )}
                {forensicTx.upsellAccepted && forensicTx.upsellItem && (
                  <div className="flex items-center gap-1.5 text-[9px] font-mono text-fuchsia-300 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-lg px-2 py-1 my-1.5">
                    <Sparkles size={10} /> Luna upsell accepted: {forensicTx.upsellItem}
                  </div>
                )}
                <div className="flex justify-between text-[15px] font-black text-white pt-1.5 border-t border-white/[0.04]">
                  <span>Grand Total</span>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">{formatRs(forensicTx.total)}</span>
                </div>
              </div>

              {/* 💳 CREDIT/DEBT SUMMARY (Real-world Retail Credit & Laidaway) */}
              {forensicTx.creditAmount !== undefined && forensicTx.creditAmount > 0 && (
                <div className="mx-5 my-2 p-3.5 rounded-2xl bg-red-500/10 border border-red-500/25 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black text-red-400 uppercase tracking-wider flex items-center gap-1">
                      <CircleDollarSign size={13} /> Credit / Debt Summary
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[8px] font-bold uppercase border ${
                      forensicTx.creditStatus === 'paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                      forensicTx.creditStatus === 'items_held' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                      'bg-red-500/10 text-red-400 border-red-500/20'
                    }`}>
                      {forensicTx.creditStatus === 'paid' ? 'Fully Paid' : forensicTx.creditStatus === 'items_held' ? 'Laidaway (Held)' : 'Active Debt'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[9px] font-mono text-slate-400">
                    <div>
                      <span>Owed Amount:</span>
                      <p className="text-white font-bold">{formatRs(forensicTx.creditAmount)}</p>
                    </div>
                    <div>
                      <span>Paid Back:</span>
                      <p className="text-emerald-400 font-bold">{formatRs(forensicTx.creditPaidAmount || 0)}</p>
                    </div>
                    {forensicTx.creditStatus !== 'paid' && (
                      <div className="col-span-2 border-t border-white/[0.04] pt-1.5 flex justify-between items-center">
                        <span className="text-red-400 font-bold uppercase">Pending Due:</span>
                        <span className="text-red-400 font-black text-xs">{formatRs(forensicTx.creditAmount - (forensicTx.creditPaidAmount || 0))}</span>
                      </div>
                    )}
                    {forensicTx.creditPromiseDate && (
                      <div className="col-span-2 flex justify-between items-center text-slate-400">
                        <span>Promise Date:</span>
                        <span className="text-white font-bold">{new Date(forensicTx.creditPromiseDate).toLocaleDateString('en-LK')}</span>
                      </div>
                    )}
                    {forensicTx.customer && (
                      <div className="col-span-2 border-t border-white/[0.04] pt-1.5 flex justify-between items-center">
                        <span>Customer:</span>
                        <span className="text-blue-300 font-bold">{forensicTx.customer.name} ({forensicTx.customer.phone || 'No phone'})</span>
                      </div>
                    )}
                  </div>

                  {/* Action buttons: Pay Back Credit or Release items */}
                  {forensicTx.creditStatus !== 'paid' && (
                    <div className="flex gap-2 border-t border-white/[0.04] pt-2">
                      <button
                        onClick={async () => {
                          const unpaidAmt = forensicTx.creditAmount! - (forensicTx.creditPaidAmount || 0);
                          const val = await openNumpad({ title: `Pay Debt — ${forensicTx.customer?.name || 'Customer'}`, initial: String(unpaidAmt), suffix: ' Rs.', max: unpaidAmt, allowDecimal: false });
                          if (val !== null) {
                            const amt = parseFloat(val) || 0;
                            if (amt <= 0) return;
                            
                            try {
                              const token = localStorage.getItem('kangavena:cashierToken') || '';
                              // Find the server-side auto-incremented ID of the transaction
                              const txId = todaySales.find(s => s.id === forensicTx.id)?.id || forensicTx.id;
                              const res = await fetch(`/api/admin/credit-transactions/${txId}/pay`, {
                                method: 'PATCH',
                                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                body: JSON.stringify({ amount: amt, notes: 'Paid against credit' })
                              });
                              const data = await res.json();
                              if (res.ok) {
                                showToast('Debt payment recorded successfully!', 'success');
                                SoundManager.play('cha_ching');
                                
                                // Live update the list and the modal!
                                const updatedTx = {
                                  ...forensicTx,
                                  creditPaidAmount: parseFloat(data.transaction.creditPaidAmount),
                                  creditStatus: data.transaction.creditStatus,
                                  creditPaymentHistory: data.transaction.creditPaymentHistory
                                };
                                setForensicTx(updatedTx);
                                setTodaySales(prev => prev.map(s => s.id === forensicTx.id ? updatedTx : s));
                                fetchTodaySales();
                              } else {
                                showToast(data.error || 'Payment failed', 'error');
                              }
                            } catch (err: any) {
                              showToast(err.message, 'error');
                            }
                          }
                        }}
                        className="flex-1 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1"
                      >
                        <CircleDollarSign size={11} /> Pay Debt
                      </button>

                      {forensicTx.creditStatus === 'items_held' && (
                        <button
                          onClick={async () => {
                            const ok = await confirmAction({
                              title: 'Release held items?',
                              message: 'Has the customer paid enough and are you ready to hand over the items?',
                              confirmLabel: 'Release Items'
                            });
                            if (ok) {
                              try {
                                const token = localStorage.getItem('kangavena:cashierToken') || '';
                                const txId = todaySales.find(s => s.id === forensicTx.id)?.id || forensicTx.id;
                                const res = await fetch(`/api/admin/credit-transactions/${txId}/pay`, {
                                  method: 'PATCH',
                                  headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                                  body: JSON.stringify({ amount: 0, notes: 'RELEASE_ITEMS' })
                                });
                                const data = await res.json();
                                if (res.ok) {
                                  showToast('Items released successfully!', 'success');
                                  SoundManager.play('cha_ching');
                                  
                                  const updatedTx = {
                                    ...forensicTx,
                                    creditStatus: 'active'
                                  };
                                  setForensicTx(updatedTx);
                                  setTodaySales(prev => prev.map(s => s.id === forensicTx.id ? updatedTx : s));
                                  fetchTodaySales();
                                } else {
                                  showToast(data.error || 'Failed to release items', 'error');
                                }
                              } catch (err: any) {
                                showToast(err.message, 'error');
                              }
                            }
                          }}
                          className="flex-1 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1"
                        >
                          <CheckCircle2 size={11} /> Hand Over Items
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 🛡️ Phase 1.3 — Void / Refund action (Manager only) */}
              {!isCashierMode && (
                <div className="px-5 py-3 border-t border-white/[0.06]">
                  {voidedTxRefs.has(forensicTx.id) ? (
                    <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px] font-bold uppercase tracking-wider">
                      <RotateCcw size={12} /> Voided
                    </div>
                  ) : (
                    <Can
                      action="transaction:void"
                      session={cashier}
                      fallback={
                        <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-slate-500/10 border border-slate-500/20 text-slate-400 text-[11px] font-bold uppercase tracking-wider">
                          <Shield size={12} /> Manager sign-in required to void
                        </div>
                      }
                    >
                      <button
                        onClick={() => setVoidTarget({ txRef: forensicTx.id, total: forensicTx.total })}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 hover:text-rose-200 text-[11px] font-bold uppercase tracking-wider transition-all active:scale-[0.98]"
                      >
                        <RotateCcw size={12} /> Void / Refund
                      </button>
                    </Can>
                  )}
                </div>
              )}

              {/* 🔄 Return items action */}
              {forensicTx.cart && forensicTx.cart.length > 0 && (
                <div className="px-5 py-3 border-t border-white/[0.06]">
                  {returnedTxRefs.has(forensicTx.id) ? (
                    <div className="flex items-center justify-center gap-2 py-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] font-bold uppercase tracking-wider">
                      <RotateCcw size={12} /> Return Processed
                    </div>
                  ) : (
                    <button
                      onClick={() =>
                        setReturnTarget({
                          txRef: forensicTx.id,
                          items: forensicTx.cart.map((ci: any) => ({
                            product: {
                              id: ci.product.id,
                              name: ci.product.name,
                              price: ci.product.price,
                              emoji: ci.product.emoji,
                            },
                            qty: ci.qty,
                          })),
                          customerId: forensicTx.customer?.id || forensicTx.customerId || null,
                        })
                      }
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 hover:text-amber-200 text-[11px] font-bold uppercase tracking-wider transition-all active:scale-[0.98]"
                    >
                      <RotateCcw size={12} /> Return Items
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 60, scale: 0.9 }}
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-3 rounded-2xl text-[12px] font-bold shadow-2xl border backdrop-blur-xl z-[9999]
              ${toast.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-[0_0_28px_rgba(16,185,129,0.15)]'
                : toast.type === 'undo'
                ? 'bg-white border-sky-200 text-slate-700 shadow-[0_0_28px_rgba(14,165,233,0.15)]'
                : 'bg-rose-50 border-rose-200 text-rose-700 shadow-[0_0_28px_rgba(239,68,68,0.15)]'
              }`}
          >
            {toast.type === 'success' ? <CheckCircle2 size={14} className="text-emerald-500" /> 
              : toast.type === 'undo' ? <RotateCcw size={14} className="text-sky-500" />
              : <AlertTriangle size={14} className="text-rose-500" />}
            <span>{toast.msg}</span>
            {toast.type === 'undo' && toast.onUndo && (
              <button
                onClick={() => {
                  toast.onUndo?.();
                  setToast(null);
                  if (undoTimeoutRef.current) {
                    clearTimeout(undoTimeoutRef.current);
                    undoTimeoutRef.current = null;
                  }
                }}
                className="ml-2 px-3 py-1.5 rounded-lg bg-sky-500 text-white text-[11px] font-black uppercase tracking-wider hover:bg-sky-600 active:scale-95 transition-all"
              >
                UNDO
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ══ FLOATING LUNA (Above Dock, Left) ═══════════════════ */}
      {!isCashierMode && !lunaOpen && !showBill && (
        <div className="fixed bottom-[72px] left-4 md:bottom-[14px] md:left-8 z-50 w-12 h-12">
          {/* Live rings — pointer-events-none so they never intercept taps */}
          <motion.div
            animate={{ scale: [1, 1.18, 1], opacity: [0.25, 0.08, 0.25] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-0 rounded-full border border-sky-400/50 scale-[1.45] pointer-events-none"
            style={{ zIndex: -1 }}
          />
          <motion.div
            animate={{ scale: [1, 1.28, 1], opacity: [0.15, 0.04, 0.15] }}
            transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            className="absolute inset-0 rounded-full border border-sky-300/40 scale-[1.75] pointer-events-none"
            style={{ zIndex: -1 }}
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setLunaOpen(true)}
            title="Open Luna AI"
            className="relative w-12 h-12 rounded-full bg-white/90 backdrop-blur-md shadow-[0_8px_20px_rgba(14,165,233,0.15)] flex items-center justify-center border border-sky-200 hover:scale-105 active:scale-95 transition-all cursor-pointer overflow-hidden"
          >
            <LunaFace sentiment="neutral" isListening={false} className="w-10 h-10 text-sky-500" />
          </motion.button>
        </div>
      )}

      {/* ══ FLOATING PING (Above Dock, Right) ══════════════════ */}
      {!isCashierMode && !lunaOpen && !showBill && (
        <div className="fixed bottom-[72px] right-4 md:bottom-[14px] md:right-[19rem] lg:right-[21rem] xl:right-[25rem] z-50 flex flex-col items-center justify-center gap-0.5 w-12 h-12 rounded-full bg-white/90 backdrop-blur-md border border-slate-200 shadow-md cursor-pointer">
          <NetworkMonitor />
        </div>
      )}

      {/* ══ BOTTOM DOCK (Buttons Only) ═════════════════════════ */}
      {/* Manager: Luna-mediated Fast Stock + Express + More Actions */}
      {/* Cashier: direct-open Fast Stock + Express + Discount toggle */}
      {!lunaOpen && (
        <div style={(showBill && window.innerWidth < 768) ? { display: 'none' } : undefined} className={`print:hidden fixed z-30 ${forceDesktop ? '' : ''} bottom-[72px] left-0 right-0 w-full flex justify-center md:bottom-0 md:left-0 md:w-full md:h-[76px] bg-transparent md:bg-white/80 backdrop-blur-none md:backdrop-blur-xl border-0 md:border-t md:border-sky-100/60 md:border-l-0 md:border-r-0 md:border-b-0 rounded-none md:rounded-none px-2.5 py-1.5 md:px-4 md:py-0 md:flex md:items-center md:justify-center shadow-none md:shadow-[0_-10px_30px_rgba(14,165,233,0.04)] md:w-[calc(100%-18rem)] lg:w-[calc(100%-20rem)] xl:w-[calc(100%-24rem)]`}>
          <div className="flex flex-row gap-1.5 md:max-w-[400px] md:w-full md:gap-3">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (isCashierMode) { setBulkInitialProducts([]); setBulkInventoryOpen(true); }
                else window.dispatchEvent(new CustomEvent('luna-express-inventory-start'));
              }}
              title="Fast Stock Voice Entry"
              className="h-9 md:h-12 rounded-xl bg-sky-50 text-sky-700 font-bold text-[10px] md:text-[13px] border border-sky-200 hover:bg-sky-100 shadow-sm transition-all flex items-center justify-center gap-1 md:gap-1.5 px-3 md:px-0 md:flex-1 active:scale-95"
            >
              <PackagePlus size={15} className="text-sky-600" />
              <span>Fast Stock</span>
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                if (isCashierMode) setExpressCheckoutOpen(true);
                else window.dispatchEvent(new CustomEvent('luna-express-checkout-start'));
              }}
              title="Express Voice Billing"
              className="h-9 md:h-12 rounded-xl bg-sky-500 text-white font-black text-[10px] md:text-[13px] border border-sky-400 hover:bg-sky-400 shadow-[0_4px_12px_rgba(14,165,233,0.3)] transition-all flex items-center justify-center gap-1 md:gap-1.5 px-3 md:px-0 md:flex-1 active:scale-95"
            >
              <ShoppingCart size={15} className="text-white" />
              <span>Express</span>
            </motion.button>
          </div>
        </div>
      )}

      {/* ══ MOBILE BOTTOM ACTION SHEET (Phase 8) ═══════════════════ */}
      {/* Positioned at bottom-[72px] to clear Luna (90px) + Ping (95px) + safe margin */}
      <AnimatePresence>
        {mobileActionSheetOpen && (
          <>
            {/* Backdrop — covers content but NOT dock/Luna/Ping at lower z */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: mobileActionSheetReducedMotion ? 0 : 0.15 }}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[54] md:hidden"
              onClick={() => setMobileActionSheetOpen(false)}
              aria-hidden="true"
            />
            {/* Sheet Panel */}
            <motion.div
              id="mobile-action-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="Quick actions"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{
                type: mobileActionSheetReducedMotion ? 'tween' : 'spring',
                damping: 25,
                stiffness: 300,
                duration: mobileActionSheetReducedMotion ? 0 : 0.35
              }}
              className="fixed bottom-[72px] left-2 right-2 max-h-[60vh] bg-white rounded-2xl shadow-2xl border border-slate-200 z-[55] md:hidden overflow-hidden flex flex-col"
              style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
            >
              {/* Handle bar for swipe affordance */}
              <div className="w-full flex justify-center pt-2 pb-1 pointer-events-none">
                <div className="w-10 h-1 rounded-full bg-slate-300/70" />
              </div>
              {/* Close button header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                <span className="text-[13px] font-black uppercase tracking-wider text-slate-800">Quick Actions</span>
                <button
                  onClick={() => setMobileActionSheetOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors"
                  aria-label="Close actions"
                >
                  <X size={16} className="text-slate-500" />
                </button>
              </div>
              {/* Scrollable action grid — min 44px touch targets per WCAG */}
              <div className="flex-1 overflow-y-auto p-3">
                <div className="grid grid-cols-4 gap-2">
                  {/* Search */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); document.getElementById('product-search')?.focus(); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="Focus search"
                  >
                    <Search size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Search</span>
                  </button>
                  {/* Scan */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); setCameraOpen(true); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="Open camera scanner"
                  >
                    <Camera size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Scan</span>
                  </button>
                  {/* Voice */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); setLunaOpen(true); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="Open voice assistant"
                  >
                    <Mic size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Voice</span>
                  </button>
                  {/* Discount */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); setShowDiscount(true); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="Apply discount"
                  >
                    <Percent size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Discount</span>
                  </button>
                  {/* Hold Bill */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); holdBill(); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="Hold current bill"
                  >
                    <PauseCircle size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Hold</span>
                  </button>
                  {/* Bills */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); setHeldBillsOpen(true); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="View held bills"
                  >
                    <BookMarked size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Bills</span>
                  </button>
                  {/* Dashboard */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); navigateTo('analytics'); setAnalyticsTab('pulse'); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="Open dashboard"
                  >
                    <BarChart3 size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Dashboard</span>
                  </button>
                  {/* Settings */}
                  <button
                    onClick={() => { setMobileActionSheetOpen(false); navigateTo('settings'); setProfileError(''); }}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-slate-50 hover:bg-sky-50 active:bg-sky-100 border border-slate-100 transition-all min-h-[72px] justify-center"
                    aria-label="Open settings"
                  >
                    <Settings size={20} className="text-sky-600" />
                    <span className="text-[10px] font-bold text-slate-600">Settings</span>
                  </button>
                  {isAutoParts && (
                    <>
                      <button
                        onClick={() => { setMobileActionSheetOpen(false); navigateTo('garage_jobs'); }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl bg-orange-50 hover:bg-orange-100 active:bg-orange-200 border border-orange-200 transition-all min-h-[72px] justify-center"
                        aria-label="Open job cards"
                      >
                        <ClipboardList size={20} className="text-orange-600" />
                        <span className="text-[10px] font-bold text-orange-700">Jobs</span>
                      </button>
                      <button
                        onClick={() => { setMobileActionSheetOpen(false); navigateTo('vin_lookup'); }}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl bg-orange-50 hover:bg-orange-100 active:bg-orange-200 border border-orange-200 transition-all min-h-[72px] justify-center"
                        aria-label="VIN lookup"
                      >
                        <Car size={20} className="text-orange-600" />
                        <span className="text-[10px] font-bold text-orange-700">VIN</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ══ FLOATING CHECKOUT PILL (Mobile only, centered above dock) ══════ */}
      {!isCashierMode && !lunaOpen && !showBill && cart.length > 0 && (
        <div
          onClick={() => openBill()}
          className="fixed bottom-[90px] left-1/2 -translate-x-1/2 h-12 w-[220px] bg-sky-600/95 backdrop-blur-md text-white rounded-full shadow-lg flex items-center justify-between px-5 z-[40] md:hidden cursor-pointer active:scale-[0.97] transition-all"
        >
          <span className="font-black text-[13px] tracking-tight truncate">
            {formatRs(cart.reduce((s, i) => s + i.product.price * i.qty, 0) * (1 - discount / 100))}
          </span>
          <span className="flex items-center gap-1 text-[11px] font-bold text-sky-100 shrink-0">
            {cart.reduce((s, i) => s + (isWeightVolume(i.product) ? 1 : i.qty), 0)} <ChevronRight size={13} className="text-white" />
          </span>
        </div>
      )}

      {/* NetworkMonitor fallback for cashier mode */}
      {isCashierMode && <NetworkMonitor />}

      {/* 🛡️ Phase 1 — Day-End / Z-Report fullscreen view (manager only — cashier hash bypass blocked) */}
      {currentView === 'dayend' && !isCashierMode && (
        <DayEndReport
          storeApiKey={storeApiKey}
          cashierToken={localStorage.getItem('kangavena:cashierToken') || ''}
          onBack={() => navigateTo('pos')}
          customFieldLabel={customFieldLabel}
        />
      )}

      {/* 🛡️ Phase 1 — Void / Refund modal */}
      <VoidModal
        open={!!voidTarget}
        txRef={voidTarget?.txRef || null}
        txTotal={voidTarget?.total || 0}
        storeApiKey={storeApiKey}
        onClose={() => setVoidTarget(null)}
        onVoided={(txRef) => {
          setVoidedTxRefs(prev => { const next = new Set(prev); next.add(txRef); return next; });
          setVoidTarget(null);
          setForensicTx(null);
          showToast(`Transaction ${txRef.slice(-6)} voided & stock restored`, 'success');
          // Optimistically remove from today's local feed
          setTodaySales(prev => prev.filter(s => s.id !== txRef));
        }}
      />

      {/* 🔄 Return items modal */}
      <ReturnModal
        open={!!returnTarget}
        txRef={returnTarget?.txRef || null}
        txItems={returnTarget?.items || []}
        txCustomerId={returnTarget?.customerId || null}
        storeApiKey={storeApiKey}
        onClose={() => setReturnTarget(null)}
        onReturned={(txRef) => {
          setReturnedTxRefs(prev => { const next = new Set(prev); next.add(txRef); return next; });
          setReturnTarget(null);
          setForensicTx(null);
          showToast(`Return processed for ${txRef.slice(-6)}`, 'success');
          fetchTodaySales().then(() => {
            fetch('/api/reports/daily-summary', {
              headers: { 'x-store-api-key': storeApiKey },
            })
              .then(r => r.ok ? r.json() : null)
              .then(setDailySummary)
              .catch(() => {});
          });
        }}
      />

      {/* 🛡️ Phase 1 — Branded confirm modal host (replaces window.confirm) */}
      <ConfirmModalHost />

      {/* ⚡ Phase 2 — Speed & Power-User UX hosts */}
      <ScanBurstHost />
      <NumpadHost />
      <ShortcutsModal />
      <SplitPaymentModal
        open={splitModalOpen}
        grandTotal={grandTotal}
        onClose={() => setSplitModalOpen(false)}
        onConfirm={({ splitDetails }) => {
          setSplitModalOpen(false);
          processCheckout('split', {
            primaryMethod: splitDetails.primaryMethod || 'cash',
            splitDetails,
          });
        }}
      />

      {/* 🎯 Phase 3 — CRM / Loyalty hosts */}
      <CustomerPicker
        open={customerPickerOpen}
        storeApiKey={storeApiKey}
        onClose={() => setCustomerPickerOpen(false)}
        onPick={(c) => {
          setCustomer(c);
          setPointsToRedeem(0);
          setCustomerPickerOpen(false);
          Haptics.fire('success');
          showToast(`${c.name} attached · ${Number(c.points).toFixed(0)} pts available`, 'success');
        }}
        customFieldLabel={customFieldLabel}
        customFieldPlaceholder={customFieldPlaceholder}
        customFieldRequired={customFieldRequired}
      />
      {currentView === 'customers' && (
        <CustomerDirectory storeApiKey={storeApiKey} onBack={() => navigateTo('pos')} />
      )}

      {/* ⏸️ Hold Dashboard Manager View */}
      {currentView === 'hold_manager' && (
        <HoldManager
          heldBills={heldBills}
          onBack={() => navigateTo('pos')}
          onRestore={(id) => {
            restoreBill(id);
            navigateTo('pos');
          }}
          onDelete={(id) => setHeldBills(prev => prev.filter(b => b.id !== id))}
          onUpdateLabel={(id, newLabel) => setHeldBills(prev => prev.map(b => b.id === id ? { ...b, label: newLabel } : b))}
          formatRs={formatRs}
        />
      )}

      {/* 📋 Estimates View */}
      {currentView === 'estimates' && (
        <EstimatesView
          storeApiKey={storeApiKey}
          formatRs={formatRs}
          onPrintEstimate={(est) => {
            // Set estimate data for standalone printing
            setEstimatePrintData({
              id: est.id,
              customerName: est.customerName || undefined,
              customerPhone: est.customerPhone || undefined,
              vehicleMake: est.vehicleMake || undefined,
              vehicleModel: est.vehicleModel || undefined,
              vehicleYear: est.vehicleYear || undefined,
              licensePlate: est.licensePlate || undefined,
              cart: (est.cartSnapshot || []) as any,
              subtotal: Number(est.subtotal) || 0,
              discountPct: Number(est.discountPct) || 0,
              grandTotal: Number(est.grandTotal) || 0,
              notes: est.notes || undefined,
              validUntil: est.validUntil,
              createdAt: est.createdAt,
            });
            setShouldPrintEstimate(true);
          }}
          onConvertToSale={async (restoredCart, discountPct, customerPhone, customerName) => {
            console.log('[Estimates] Restoring cart:', restoredCart, 'discount:', discountPct, 'phone:', customerPhone, 'name:', customerName);
            // Map cartSnapshot items to proper CartItem format with defaults for missing Product fields
            const mappedCart = (restoredCart as any[]).map((item: any) => ({
              product: {
                id: item.product.id,
                name: item.product.name,
                price: Number(item.product.price) || 0,
                emoji: item.product.emoji || '📦',
                coreCharge: Number(item.product.coreCharge) || 0,
                category: item.product.category || 'General',
                stockCount: item.product.stockCount ?? 999,
                costPrice: item.product.costPrice ? Number(item.product.costPrice) : undefined,
                sellingPrice: Number(item.product.sellingPrice || item.product.price) || 0,
              },
              qty: Number(item.qty) || 1,
            }));
            console.log('[Estimates] Mapped cart:', mappedCart.length, 'items');
            // Set cart + discount FIRST, then navigate to POS on next tick to avoid React batching issues
            setCart(mappedCart);
            setDiscount(Number(discountPct) || 0);
            setFlatDiscount(0);
            setShowDiscount(Number(discountPct) > 0);

            if (customerPhone) {
              try {
                const token = localStorage.getItem('kangavena:cashierToken') || '';
                const headers = {
                  'x-store-api-key': storeApiKey,
                  'Content-Type': 'application/json',
                  ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                };
                const res = await fetch(`/api/customers?q=${encodeURIComponent(customerPhone)}&limit=1`, { headers });
                if (res.ok) {
                  const data = await res.json();
                  if (data.customers && data.customers.length > 0) {
                    setCustomer(data.customers[0]);
                  } else {
                    setCustomer({ name: customerName || 'Walk-in Customer', phone: customerPhone, points: '0', totalSpent: '0', totalVisits: '0' } as any);
                  }
                } else {
                  setCustomer({ name: customerName || 'Walk-in Customer', phone: customerPhone, points: '0', totalSpent: '0', totalVisits: '0' } as any);
                }
              } catch (e) {
                console.error('[Estimates] Failed to auto-attach customer:', e);
                setCustomer({ name: customerName || 'Walk-in Customer', phone: customerPhone, points: '0', totalSpent: '0', totalVisits: '0' } as any);
              }
            } else if (customerName) {
              setCustomer({ name: customerName, phone: '', points: '0', totalSpent: '0', totalVisits: '0' } as any);
            } else {
              setCustomer(null);
            }

            // Navigate after a micro-delay so React commits the cart state first
            setTimeout(() => navigateTo('pos'), 50);
          }}
          navigateTo={navigateTo}
        />
      )}

      {/* 📋 Save Estimate Modal */}
      <SaveEstimateModal
        isOpen={estimateSaveModalOpen}
        onClose={() => setEstimateSaveModalOpen(false)}
        cart={cart}
        subtotal={subtotal}
        discountPct={discount}
        grandTotal={grandTotal}
        storeApiKey={storeApiKey}
        formatRs={formatRs}
        preselectedCustomer={customer}
        shopType={shopType}
        isAutoParts={isAutoParts}
        onSaved={(savedEstimate, andPrint) => {
          // Store estimate data for standalone printing
          setEstimatePrintData({
            id: savedEstimate.id,
            customerName: savedEstimate.customerName || customer?.name || undefined,
            customerPhone: savedEstimate.customerPhone || customer?.phone || undefined,
            vehicleMake: savedEstimate.vehicleMake || undefined,
            vehicleModel: savedEstimate.vehicleModel || undefined,
            vehicleYear: savedEstimate.vehicleYear || undefined,
            licensePlate: savedEstimate.licensePlate || undefined,
            serviceType: savedEstimate.serviceType || undefined,
            orderType: savedEstimate.orderType || undefined,
            deliveryDate: savedEstimate.deliveryDate || undefined,
            cart: (savedEstimate.cart || cart) as any,
            subtotal: Number(savedEstimate.subtotal) || subtotal,
            discountPct: Number(savedEstimate.discountPct) || discount,
            grandTotal: Number(savedEstimate.grandTotal) || grandTotal,
            notes: savedEstimate.notes || undefined,
            validUntil: savedEstimate.validUntil || new Date(Date.now() + 7 * 86400000).toISOString(),
            createdAt: savedEstimate.createdAt || new Date().toISOString(),
          });
          if (andPrint) {
            setShouldPrintEstimate(true);
          }
        }}
      />

      
      {/* ══ STANDALONE EXECUTIVE ESTIMATE PRINT LAYER ══
           Renders ONLY on print (@media print). Hidden on screen via .kangavena-printable.
           Shows high-definition commercial quotation when estimatePrintData is set.
      ══════════════════════════════════════════════════════ */}
      {estimatePrintData && (
        <div
          id="kangavena-estimate-print-root"
          className="kangavena-printable"
          style={{
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            color: '#0f172a',
            background: '#ffffff',
            width: '100%',
            maxWidth: '820px',
            margin: '0 auto',
            padding: '32px 36px',
            boxSizing: 'border-box',
            lineHeight: 1.45,
          }}
        >
          {/* ── 1. CORPORATE QUOTATION HEADER ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #0f172a', paddingBottom: '16px', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-0.02em', color: '#0f172a' }}>
                {storeName || profileForm.name || 'Store'}
              </div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '2px' }}>
                {isAutoParts ? 'Automotive Spare Parts & Engineering Services' : 'Commercial Wholesale & Retail Quotation'}
              </div>
              {profileForm.address && (
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  {profileForm.address}
                </div>
              )}
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                {profileForm.phone && <span>Tel: <strong style={{ color: '#0f172a' }}>{profileForm.phone}</strong></span>}
                {profileForm.managerWhatsapp && <span>WhatsApp: <strong style={{ color: '#0f172a' }}>+{profileForm.managerWhatsapp}</strong></span>}
                {profileForm.email && <span>Email: {profileForm.email}</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right', minWidth: '220px' }}>
              <div style={{ display: 'inline-block', background: '#0f172a', color: '#ffffff', fontSize: '10px', fontWeight: 900, letterSpacing: '0.15em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '4px', marginBottom: '6px' }}>
                COMMERCIAL ESTIMATE
              </div>
              <div style={{ fontSize: '18px', fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: '#0f172a' }}>
                EST-{String(estimatePrintData.id).padStart(5, '0')}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>
                Date: <strong style={{ color: '#0f172a' }}>{new Date(estimatePrintData.createdAt || Date.now()).toLocaleDateString('en-LK', { day: '2-digit', month: 'short', year: 'numeric' })}</strong>
              </div>
              <div style={{ fontSize: '11px', color: '#059669', fontWeight: 700, marginTop: '2px' }}>
                Valid Until: {new Date(estimatePrintData.validUntil).toLocaleDateString('en-LK', { day: '2-digit', month: 'short', year: 'numeric' })}
              </div>
            </div>
          </div>

          {/* ── 2. CLIENT & SPECIFICATIONS CARD ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
            <div>
              <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', marginBottom: '4px' }}>
                PREPARED FOR (CLIENT)
              </div>
              <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>
                {estimatePrintData.customerName || 'Walk-in Customer'}
              </div>
              {estimatePrintData.customerPhone && (
                <div style={{ fontSize: '12px', fontFamily: "'JetBrains Mono', monospace", color: '#334155', marginTop: '2px' }}>
                  Tel: {estimatePrintData.customerPhone}
                </div>
              )}
            </div>

            <div>
              <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', marginBottom: '4px' }}>
                {isAutoParts || estimatePrintData.vehicleMake || estimatePrintData.licensePlate ? 'VEHICLE / SERVICE SPECIFICATIONS' : 'ORDER DETAILS'}
              </div>
              {isAutoParts || estimatePrintData.vehicleMake || estimatePrintData.licensePlate ? (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                    {[estimatePrintData.vehicleMake, estimatePrintData.vehicleModel, estimatePrintData.vehicleYear].filter(Boolean).join(' ') || 'General Automotive Parts'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                    {estimatePrintData.licensePlate && <span>Plate #: <strong style={{ color: '#0f172a' }}>{estimatePrintData.licensePlate}</strong> • </span>}
                    <span>Type: {estimatePrintData.serviceType || 'Parts Quotation'}</span>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                    {estimatePrintData.orderType === 'delivery' ? 'Delivery Order' : 'Store Pickup'}
                  </div>
                  {estimatePrintData.deliveryDate && (
                    <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>
                      Delivery Target: {new Date(estimatePrintData.deliveryDate).toLocaleDateString('en-LK')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── 3. ITEMIZED QUOTATION TABLE ── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <thead>
              <tr style={{ background: '#0f172a', color: '#ffffff', fontSize: '11px', fontWeight: 800, letterSpacing: '0.05em' }}>
                <th style={{ padding: '8px 10px', textAlign: 'center', width: '36px' }}>#</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>ITEM & DESCRIPTION</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', width: '60px' }}>QTY</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', width: '110px' }}>UNIT PRICE</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', width: '130px' }}>TOTAL (RS.)</th>
              </tr>
            </thead>
            <tbody>
              {estimatePrintData.cart.map((item: any, idx: number) => {
                const name = item.product?.name || item.name || 'Item';
                const qty = Number(item.qty) || 1;
                const price = Number(item.product?.price ?? item.price ?? 0);
                const total = Number(item.adjustedPrice != null ? item.adjustedPrice * qty : price * qty);
                const warranty = item.warrantyPeriod || item.product?.warrantyPeriod;
                const returnPol = item.returnPolicy || item.product?.returnPolicy;
                const isEven = idx % 2 === 1;

                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', background: isEven ? '#f8fafc' : '#ffffff', fontSize: '12px' }}>
                    <td style={{ padding: '10px 8px', textAlign: 'center', color: '#64748b', fontFamily: "'JetBrains Mono', monospace" }}>{idx + 1}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{name}</div>
                      {(warranty || returnPol) && (
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                          {warranty && <span>Warranty: {warranty} </span>}
                          {returnPol && <span>• Return Policy: {returnPol}</span>}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: '#0f172a' }}>
                      {qty}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", color: '#334155' }}>
                      {formatRs(price)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, color: '#0f172a' }}>
                      {formatRs(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── 4. FINANCIAL SUMMARY & TERMS ── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px', borderTop: '2px solid #0f172a', paddingTop: '16px', marginBottom: '24px' }}>
            {/* Notes & Commercial Terms */}
            <div style={{ flex: 1 }}>
              {estimatePrintData.notes && (
                <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px', padding: '8px 12px', marginBottom: '10px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', color: '#b45309', display: 'block', marginBottom: '2px' }}>Special Instructions:</span>
                  <span style={{ fontSize: '11px', color: '#78350f', fontStyle: 'italic' }}>"{estimatePrintData.notes}"</span>
                </div>
              )}
              <div style={{ fontSize: '10px', color: '#64748b', lineHeight: 1.5 }}>
                <div style={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' }}>Terms & Conditions:</div>
                <div>1. Quoted prices are in Sri Lankan Rupees (LKR) and strictly valid until <strong>{new Date(estimatePrintData.validUntil).toLocaleDateString('en-LK')}</strong>.</div>
                <div>2. This estimate is for pricing information only; it is not a tax invoice and does not reserve stock.</div>
                <div>3. Final parts availability and installation schedules will be confirmed upon acceptance of this estimate.</div>
              </div>
            </div>

            {/* Totals Box */}
            <div style={{ width: '280px', flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#475569', marginBottom: '6px' }}>
                <span>Subtotal</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: '#0f172a' }}>{formatRs(estimatePrintData.subtotal)}</span>
              </div>
              {estimatePrintData.discountPct > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#dc2626', marginBottom: '6px' }}>
                  <span>Discount ({estimatePrintData.discountPct}%)</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>- {formatRs(estimatePrintData.subtotal * estimatePrintData.discountPct / 100)}</span>
                </div>
              )}
              <div style={{ background: '#0f172a', color: '#ffffff', borderRadius: '8px', padding: '10px 14px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '9px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8' }}>TOTAL ESTIMATE</div>
                  <div style={{ fontSize: '9px', color: '#64748b' }}>Includes applicable items</div>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 900, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.02em' }}>
                  {formatRs(estimatePrintData.grandTotal)}
                </div>
              </div>
            </div>
          </div>

          {/* ── 5. SIGNATURE & STAMP BLOCK ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', borderTop: '1px dashed #cbd5e1', paddingTop: '24px', marginTop: '20px', textAlign: 'center' }}>
            <div>
              <div style={{ height: '40px', borderBottom: '1px solid #94a3b8', margin: '0 auto 8px', maxWidth: '240px' }} />
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#0f172a' }}>Customer Acceptance</div>
              <div style={{ fontSize: '9px', color: '#64748b' }}>Signature & Date</div>
            </div>
            <div>
              <div style={{ height: '40px', borderBottom: '1px solid #94a3b8', margin: '0 auto 8px', maxWidth: '240px' }} />
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#0f172a' }}>Authorized Signatory</div>
              <div style={{ fontSize: '9px', color: '#64748b' }}>{storeName || profileForm.name || 'Store'} Seal & Signature</div>
            </div>
          </div>

          {/* ── 6. DOCUMENT FOOTER ── */}
          <div style={{ textAlign: 'center', borderTop: '1px solid #f1f5f9', paddingTop: '16px', marginTop: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Thank you for choosing {storeName || profileForm.name || 'our store'}!</p>
            <p style={{ fontSize: '9px', color: '#94a3b8', marginTop: '4px', letterSpacing: '0.05em' }}>
              System Ref: EST-{String(estimatePrintData.id).padStart(5, '0')} • Prepared by {cashierName || 'Cashier'} • Powered by Kangavena Cloud Intelligence
            </p>
          </div>
        </div>
      )}

      {/* 🛡️ Phase 4 — Cashier Management view (manager only — hash bypass blocked) */}
      {currentView === 'cashiers' && !isCashierMode && (
        <CashierManager
          storeApiKey={storeApiKey}
          currentCashier={cashier}
          onBack={() => navigateTo('pos')}
        />
      )}

      {/* 👥 Phase 2 — Employee Management view (manager only) */}
      {currentView === 'employees' && !isCashierMode && (
        <EmployeeManagement
          storeApiKey={storeApiKey}
          onBack={() => navigateTo('pos')}
        />
      )}

      {/* 🏭 Phase 7 — Supplier Management view */}
      {currentView === 'suppliers' && (
        <SupplierManager
          storeApiKey={storeApiKey}
          onBack={() => navigateTo('pos')}
        />
      )}

      {/* 🕐 Phase 7 — Shift Management view */}
      {currentView === 'shifts' && (
        <ShiftManager
          storeApiKey={storeApiKey}
          currentCashier={cashier}
          onBack={() => navigateTo('pos')}
        />
      )}

      {/* ── GARAGE: Job Cards View ── */}
      {currentView === 'garage_jobs' && isAutoParts && (
        <GarageJobsView
          storeApiKey={storeApiKey}
          onBack={() => navigateTo('pos')}
        />
      )}

      {/* ── GARAGE: VIN / Vehicle Lookup View ── */}
      {currentView === 'vin_lookup' && isAutoParts && (
        <VinLookupView
          storeApiKey={storeApiKey}
          onBack={() => navigateTo('pos')}
        />
      )}

      {/* 🔐 Phase 6 — Manager Login Approval Panel */}
      <AnimatePresence>
        {showApprovalPanel && (
          <motion.div
            key="approval-panel-bg"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10003] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center pt-16 px-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowApprovalPanel(false); }}
          >
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -12, scale: 0.97 }}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-amber-500/10 flex items-center justify-center">
                    <Bell size={14} className="text-amber-500" />
                  </div>
                  <div>
                    <p className="text-[13px] font-black text-slate-800 uppercase tracking-widest">Login Approvals</p>
                    <p className="text-[9px] text-slate-400 font-mono">{pendingLogins.length} pending</p>
                  </div>
                </div>
                <button onClick={() => setShowApprovalPanel(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                  <X size={14} />
                </button>
              </div>
              <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
                {pendingLogins.length === 0 ? (
                  <p className="text-center text-[11px] text-slate-400 py-6">No pending approvals</p>
                ) : pendingLogins.map((req: any) => (
                  <div key={req.id} className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="min-w-0">
                      <p className="text-[12px] font-bold text-slate-800 truncate">{req.cashierName || req.username}</p>
                      <p className="text-[9px] text-slate-400 font-mono capitalize">{req.role} · {new Date(req.requestedAt).toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => approveLogin(req.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-600 transition-colors"
                      >
                        <CheckCircle2 size={10} /> Allow
                      </button>
                      <button
                        onClick={() => rejectLogin(req.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-500 text-[10px] font-bold uppercase tracking-widest hover:bg-rose-100 transition-colors"
                      >
                        <X size={10} /> Deny
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🛡️ Phase 4 — Cashier Auth modal */}
      <CashierAuth
        open={cashierAuthOpen}
        storeApiKey={storeApiKey}
        onClose={() => setCashierAuthOpen(false)}
        onAuth={(session, token) => {
          setCashier(session);
          setLastActivity(Date.now());
          localStorage.setItem('kangavena:cashierToken', token);
          setCashierAuthOpen(false);
          Haptics.fire('success');
          showToast(`Signed in as ${session.name} (${session.roleLabel})`, 'success');
        }}
      />

      {/* 🛡️ Phase 4 — Session expiry warning */}
      {sessionExpiring && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10003] bg-rose-600 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3">
          <AlertTriangle size={16} />
          <div className="text-[12px] font-bold">Session expiring — 30s until logout</div>
          <button
            onClick={() => { setLastActivity(Date.now()); setSessionExpiring(false); }}
            className="ml-2 px-3 py-1.5 rounded-lg bg-white text-rose-600 text-[11px] font-black uppercase"
          >
            Stay Signed In
          </button>
        </div>
      )}


      {/* 📋 Print Estimate Modal */}
      {estimateModalOpen && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Estimate Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-[18px] font-black text-slate-800">ESTIMATE / PRO-FORMA</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Not an official invoice — for customer reference only</p>
              </div>
              <button
                onClick={() => setEstimateModalOpen(false)}
                className="w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Estimate Content */}
            <div className="p-6 space-y-4">
              {/* Store Info */}
              <div className="text-center pb-4 border-b border-slate-200">
                <h3 className="text-[16px] font-black text-slate-800">{storeName || profileForm.name || 'Your Store'}</h3>
                <p className="text-[10px] text-slate-500 mt-1">{profileForm.location || 'Location'}</p>
                <p className="text-[9px] text-slate-400 mt-2">{now.toLocaleDateString('en-LK')} · {now.toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' })}</p>
              </div>

              {/* Items List */}
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Items</p>
                {cart.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-[11px] pb-2 border-b border-slate-100">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">{item.qty}× {item.product.name}</p>
                      <p className="text-[9px] text-slate-500">{formatRs(item.product.price)}{getUnitLabel(item.product) ? `/${item.product.unitType}` : ''}</p>
                    </div>
                    <p className="font-mono font-semibold text-slate-800 text-right">{formatRs(item.product.price * item.qty)}</p>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t-2 border-dashed border-slate-300 my-3"></div>

              {/* Totals */}
              <div className="space-y-1.5">
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-mono font-semibold text-slate-800">{formatRs(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-[11px] text-violet-600">
                    <span>Discount ({discount}%)</span>
                    <span className="font-mono">−{formatRs(discountAmt)}</span>
                  </div>
                )}
                {coreChargesTotal > 0 && (
                  <div className="flex justify-between text-[11px] text-orange-600">
                    <span>Core Charges</span>
                    <span className="font-mono">+{formatRs(coreChargesTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-[13px] font-black pt-2 border-t-2 border-slate-300">
                  <span className="text-slate-800">Grand Total</span>
                  <span className="font-mono text-brand-primary">{formatRs(grandTotal)}</span>
                </div>
              </div>

              {/* Footer Note */}
              <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                <p className="text-[9px] text-slate-500 italic">This is an estimate. No transaction has been recorded.</p>
                {showLogoOnBill && (
                  <img src="/kangavena-logo.png" alt="Kangavena" className="h-4 mx-auto mt-1.5 mb-0.5 object-contain opacity-60" style={{ display: 'block' }} />
                )}
                {!hidePoweredByOnBill && (
                  <p className="text-[8px] text-slate-400 mt-0.5">Powered by Kangavena Cloud</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 p-4 flex gap-2">
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => printThermalReceipt()}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-black text-[11px] uppercase tracking-wider shadow-md hover:shadow-lg transition-all"
              >
                <Receipt size={14} /> Print Estimate
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => setEstimateModalOpen(false)}
                className="flex-1 py-3 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-[11px] uppercase tracking-wider transition-colors"
              >
                Close
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}
    </div>

      {/* ══ MOBILE BOTTOM NAVIGATION BAR ═════════════════════════ */}
      <div className={`fixed bottom-0 left-0 w-full h-16 bg-white/95 backdrop-blur-xl border-t border-slate-200 z-50 flex items-stretch ${forceDesktop ? 'hidden' : 'md:hidden'} print:hidden`}>
        {/* Home */}
        <button
          onClick={() => { Haptics.fire('tap'); navigateTo('pos'); }}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[#1e1b4b] active:bg-[#1e1b4b]/5 transition-colors"
        >
          <Home size={20} strokeWidth={2} />
          <span className="text-[9px] font-bold tracking-wide">Home</span>
        </button>

        {/* Add Item */}
        <button
          onClick={() => setAddProductOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[#1e1b4b] active:bg-[#1e1b4b]/5 transition-colors"
        >
          <PlusCircle size={20} strokeWidth={2} />
          <span className="text-[9px] font-bold tracking-wide">Add Item</span>
        </button>

        {/* Scan */}
        <button
          onClick={() => setCameraOpen(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[#1e1b4b] active:bg-[#1e1b4b]/5 transition-colors"
        >
          <Camera size={20} strokeWidth={2} />
          <span className="text-[9px] font-bold tracking-wide">Scan</span>
        </button>

        {/* Cart */}
        <button
          onClick={() => openBill()}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[#1e1b4b] active:bg-[#1e1b4b]/5 transition-colors relative"
        >
          <ShoppingCart size={20} strokeWidth={2} className={cart.length === 0 ? 'text-slate-400' : ''} />
          {cart.length > 0 && (
            <span className="absolute top-1 right-1/2 translate-x-3 -translate-y-1 w-4 h-4 rounded-full bg-emerald-500 text-white text-[8px] font-black flex items-center justify-center shadow-sm">
              {cart.length}
            </span>
          )}
          <span className={`text-[9px] font-bold tracking-wide ${cart.length === 0 ? 'text-slate-400' : ''}`}>Cart</span>
        </button>

        {/* More */}
        <button
          onClick={() => setShowMoreActions(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[#1e1b4b] active:bg-[#1e1b4b]/5 transition-colors"
        >
          <Grid3X3 size={20} strokeWidth={2} />
          <span className="text-[9px] font-bold tracking-wide">More</span>
        </button>
      </div>

      {/* ══ MORE ACTIONS BOTTOM SHEET ═══════════════════════════ */}
      <AnimatePresence>
        {showMoreActions && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] md:hidden"
              onClick={() => setShowMoreActions(false)}
              aria-hidden="true"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 w-full max-h-[80vh] bg-white rounded-t-2xl z-[61] shadow-2xl flex flex-col overflow-hidden md:hidden"
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 rounded-full bg-slate-300" />
              </div>

              {/* Title */}
              <div className="px-5 pb-3 flex items-center justify-between">
                <h3 className="text-sm font-black text-slate-800">Quick Actions</h3>
                <button
                  onClick={() => setShowMoreActions(false)}
                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Actions grid */}
              <div className="overflow-y-auto px-4 pb-6">
                <div className="grid grid-cols-3 gap-2.5">
                  {/* Menu — from old bottom nav */}
                  <button
                    onClick={() => { setShowMoreActions(false); Haptics.fire('tap'); setShowMobileMenu(prev => !prev); }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                  >
                    <Menu size={20} className="text-slate-700" />
                    <span className="text-[10px] font-bold text-slate-700">Menu</span>
                  </button>

                  {/* Dashboard */}
                  <button
                    onClick={() => { setShowMoreActions(false); navigateTo('analytics'); setAnalyticsTab('pulse'); }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                  >
                    <BarChart3 size={20} className="text-brand-primary" />
                    <span className="text-[10px] font-bold text-slate-700">Dashboard</span>
                  </button>

                  {/* Day-End */}
                  <Can action="dayend:run" session={cashier}>
                    <button
                      onClick={() => { setShowMoreActions(false); navigateTo('dayend'); }}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                    >
                      <Receipt size={20} className="text-slate-500" />
                      <span className="text-[10px] font-bold text-slate-700">Day-End</span>
                    </button>
                  </Can>

                  {/* Customers */}
                  <button
                    onClick={() => { setShowMoreActions(false); navigateTo('customers'); }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                  >
                    <UserCircle2 size={20} className="text-slate-500" />
                    <span className="text-[10px] font-bold text-slate-700">Customers</span>
                  </button>

                  {/* Cashiers */}
                  <Can action="cashier:view" session={cashier}>
                    <button
                      onClick={() => { setShowMoreActions(false); navigateTo('cashiers'); fetchCashiers(); }}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                    >
                      <Users size={20} className="text-slate-500" />
                      <span className="text-[10px] font-bold text-slate-700">Cashiers</span>
                    </button>
                  </Can>

                  {/* Hold / Held */}
                  <button
                    onClick={() => {
                      setShowMoreActions(false);
                      if (cart.length === 0) { navigateTo('hold_manager'); } else { holdBill(); }
                    }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm relative"
                  >
                    <PauseCircle size={20} className="text-slate-500" />
                    <span className="text-[10px] font-bold text-slate-700">
                      {cart.length > 0 ? 'Hold' : 'Held'}
                    </span>
                    {heldBills.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-white text-[8px] font-black flex items-center justify-center shadow-sm">
                        {heldBills.length}
                      </span>
                    )}
                  </button>

                  {/* Discount */}
                  <button
                    onClick={() => { setShowMoreActions(false); setShowDiscount(v => !v); }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                  >
                    <Percent size={20} className="text-emerald-600" />
                    <span className="text-[10px] font-bold text-slate-700">Discount</span>
                  </button>

                  {/* Estimates */}
                  <button
                    onClick={() => { setShowMoreActions(false); navigateTo('estimates'); }}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                  >
                    <FileText size={20} className="text-slate-500" />
                    <span className="text-[10px] font-bold text-slate-700">Estimates</span>
                  </button>

                  {/* Bulk Add */}
                  <Can action="inventory:bulk" session={cashier}>
                    <button
                      onClick={() => { setShowMoreActions(false); setBulkInitialProducts([]); setBulkInventoryOpen(true); }}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                    >
                      <Package size={20} className="text-slate-500" />
                      <span className="text-[10px] font-bold text-slate-700">Bulk Add</span>
                    </button>
                  </Can>

                  {/* Scan Inventory Sheet */}
                  <Can action="inventory:bulk" session={cashier}>
                    <button
                      onClick={() => { setShowMoreActions(false); setInventorySheetScannerOpen(true); }}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-cyan-50 border border-cyan-200 hover:bg-cyan-100 active:scale-95 transition-all shadow-sm group"
                    >
                      <Camera size={20} className="text-cyan-600 group-hover:scale-110 transition-transform" />
                      <span className="text-[10px] font-bold text-cyan-800">Scan Sheet</span>
                    </button>
                  </Can>

                  {/* Settings */}
                  <Can action="store:settings" session={cashier}>
                    <button
                      onClick={() => { setShowMoreActions(false); navigateTo('settings'); setProfileError(''); }}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-white border border-slate-100 hover:bg-slate-50 hover:border-slate-200 active:scale-95 transition-all shadow-sm"
                    >
                      <Settings size={20} className="text-slate-500" />
                      <span className="text-[10px] font-bold text-slate-700">Settings</span>
                    </button>
                  </Can>

                  {isAutoParts && (
                    <>
                      <button
                        onClick={() => { setShowMoreActions(false); navigateTo('garage_jobs'); }}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-orange-50 border border-orange-200 hover:bg-orange-100 active:scale-95 transition-all shadow-sm"
                      >
                        <ClipboardList size={20} className="text-orange-600" />
                        <span className="text-[10px] font-bold text-orange-700">Jobs</span>
                      </button>
                      <button
                        onClick={() => { setShowMoreActions(false); navigateTo('vin_lookup'); }}
                        className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-orange-50 border border-orange-200 hover:bg-orange-100 active:scale-95 transition-all shadow-sm"
                      >
                        <Car size={20} className="text-orange-600" />
                        <span className="text-[10px] font-bold text-orange-700">VIN</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 🖨️ Printer Setup Modal */}
      <PrinterSetupModal
        isOpen={printerSetupOpen}
        onClose={() => setPrinterSetupOpen(false)}
        storeApiKey={storeApiKey}
      />

    </>
    </BillingLockGate>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   GARAGE JOBS VIEW — Mobile-Responsive Job Cards Management
═══════════════════════════════════════════════════════════════════════════ */
type GarageJobStatus = 'OPEN' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
interface GarageJob {
  id: number; jobRef: string;
  vehicleMake: string; vehicleModel: string; vehicleYear: string; licensePlate: string;
  customerName: string; customerPhone: string; description: string;
  status: GarageJobStatus; laborCost: number; partsCost: number; total: number;
  Parts?: any[];
  createdAt: string; updatedAt: string;
}
const GARAGE_STATUS_META: Record<GarageJobStatus, { label: string; color: string; bg: string; border: string }> = {
  OPEN:        { label: 'Open',        color: 'text-sky-700',     bg: 'bg-sky-50',     border: 'border-sky-200' },
  IN_PROGRESS: { label: 'In Progress', color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200' },
  COMPLETED:   { label: 'Completed',   color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  CANCELLED:   { label: 'Cancelled',   color: 'text-slate-500',   bg: 'bg-slate-50',   border: 'border-slate-200' },
};

const GarageJobsView: React.FC<{ storeApiKey: string; onBack: () => void }> = ({ storeApiKey, onBack }) => {
  const [jobs, setJobs] = useState<GarageJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<GarageJobStatus | 'ALL'>('ALL');
  const [showCreate, setShowCreate] = useState(false);
  const [completeTarget, setCompleteTarget] = useState<GarageJob | null>(null);
  const [completing, setCompleting] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const headers = { 'x-store-api-key': storeApiKey, 'Content-Type': 'application/json' };
  const fmt = (n: number) => `Rs. ${Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try { const r = await fetch('/api/garage/jobs', { headers }); if (r.ok) setJobs(await r.json()); } catch {}
    setLoading(false);
  }, [storeApiKey]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const updateStatus = async (id: number, status: GarageJobStatus) => {
    try {
      await fetch(`/api/garage/jobs/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ status }) });
      setJobs(prev => prev.map(j => j.id === id ? { ...j, status } : j));
      setToast({ msg: `Status -> ${GARAGE_STATUS_META[status].label}`, ok: true });
      setTimeout(() => setToast(null), 2500);
    } catch { setToast({ msg: 'Failed to update', ok: false }); setTimeout(() => setToast(null), 2500); }
  };

  const completeJob = async (job: GarageJob) => {
    setCompleting(true);
    try {
      const r = await fetch(`/api/garage/jobs/${job.id}/complete`, { method: 'PATCH', headers });
      const d = await r.json();
      if (!r.ok) { setToast({ msg: d.error || 'Failed', ok: false }); setCompleting(false); setCompleteTarget(null); setTimeout(() => setToast(null), 2500); return; }
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, ...(d.job || {}) } : j));
      setToast({ msg: `Job ${job.jobRef} completed!`, ok: true });
      setCompleteTarget(null);
    } catch { setToast({ msg: 'Cannot reach server', ok: false }); }
    setCompleting(false);
    setTimeout(() => setToast(null), 2500);
  };

  const filtered = filter === 'ALL' ? jobs : jobs.filter(j => j.status === filter);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onBack} className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors">
          <ChevronLeft size={18} className="text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[15px] font-black text-slate-800 truncate">Job Cards</h1>
          <p className="text-[10px] text-slate-400 font-mono">{jobs.length} total</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-black shadow-sm active:scale-95 transition-all shrink-0">
          <Plus size={13} /> <span className="hidden sm:inline">New Job</span><span className="sm:hidden">New</span>
        </button>
      </div>

      {/* Status Filter */}
      <div className="px-4 py-2 bg-white border-b border-slate-100 shrink-0 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max">
          {(['ALL', 'OPEN', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const).map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-bold whitespace-nowrap transition-all
                ${filter === s ? 'bg-orange-500 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-orange-300'}`}>
              {s === 'ALL' ? 'All' : GARAGE_STATUS_META[s].label}
              <span className="ml-1 opacity-60">{s === 'ALL' ? jobs.length : jobs.filter(j => j.status === s).length}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Job List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-orange-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList size={36} className="mx-auto mb-3 text-slate-300" />
            <p className="text-[13px] text-slate-400 font-bold">No jobs found</p>
          </div>
        ) : filtered.map(job => {
          const sm = GARAGE_STATUS_META[job.status];
          return (
            <div key={job.id} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm active:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0">
                    <Car size={18} className="text-orange-500" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-[13px] font-black text-slate-800">{job.vehicleMake} {job.vehicleModel} {job.vehicleYear}</p>
                      <span className="font-mono text-[10px] text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">{job.licensePlate}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">{job.customerName}{job.customerPhone ? ` · ${job.customerPhone}` : ''}</p>
                    {job.description && <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{job.description}</p>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border ${sm.bg} ${sm.color} ${sm.border}`}>
                    {sm.label}
                  </span>
                  <p className="text-[13px] font-black text-slate-800 mt-2">{fmt(job.total)}</p>
                  <p className="text-[10px] text-slate-400 font-mono">{job.jobRef}</p>
                </div>
              </div>

              <div className="flex gap-4 mt-3 text-[10px] text-slate-400 font-mono">
                <span>Labor: {fmt(job.laborCost)}</span>
                <span>Parts: {fmt(job.partsCost)}</span>
              </div>

              {job.Parts && job.Parts.length > 0 && (
                <div className="mt-2 bg-slate-50 rounded-xl p-2.5 space-y-1">
                  {job.Parts.map((p: any, i: number) => (
                    <div key={i} className="flex justify-between text-[10px]">
                      <span className="text-slate-600">{p.productName || p.name || `Part #${p.productId}`} x{p.qty}</span>
                      <span className="font-bold text-slate-700">{fmt(p.lineTotal || (p.unitPrice || 0) * (p.qty || 0))}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 mt-3 pt-3 border-t border-slate-50">
                {job.status !== 'COMPLETED' && job.status !== 'CANCELLED' && (
                  <>
                    {job.status === 'OPEN' && (
                      <button onClick={() => updateStatus(job.id, 'IN_PROGRESS')}
                        className="flex-1 py-2.5 rounded-xl text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 active:scale-95 transition-all">
                        Start Work
                      </button>
                    )}
                    {job.status === 'IN_PROGRESS' && (
                      <button onClick={() => updateStatus(job.id, 'OPEN')}
                        className="flex-1 py-2.5 rounded-xl text-[11px] font-bold bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 active:scale-95 transition-all">
                        Reopen
                      </button>
                    )}
                    <button onClick={() => setCompleteTarget(job)}
                      className="flex-1 py-2.5 rounded-xl text-[11px] font-bold bg-emerald-500 hover:bg-emerald-600 text-white active:scale-95 transition-all">
                      Complete
                    </button>
                    <button onClick={() => updateStatus(job.id, 'CANCELLED')}
                      className="py-2.5 px-3 rounded-xl text-[11px] font-bold bg-slate-50 text-slate-400 border border-slate-200 hover:bg-rose-50 hover:text-rose-500 hover:border-rose-200 active:scale-95 transition-all">
                      <X size={13} />
                    </button>
                  </>
                )}
                {(job.status === 'COMPLETED' || job.status === 'CANCELLED') && (
                  <div className={`flex-1 py-2.5 rounded-xl text-[11px] font-bold text-center border ${sm.bg} ${sm.color} ${sm.border}`}>
                    {sm.label}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {showCreate && (
          <GarageCreateJobModal apiKey={storeApiKey} onClose={() => setShowCreate(false)}
            onCreated={(job) => { setJobs(prev => [job, ...prev]); setShowCreate(false); setToast({ msg: 'Job card created!', ok: true }); setTimeout(() => setToast(null), 2500); }} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {completeTarget && (
          <GarageCompleteConfirm job={completeTarget} loading={completing}
            onConfirm={() => completeJob(completeTarget)} onClose={() => setCompleteTarget(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            className={`fixed bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-3 rounded-2xl text-[12px] font-bold shadow-2xl border backdrop-blur-xl z-[9999]
              ${toast.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
            {toast.ok ? <CheckCircle2 size={14} className="text-emerald-500" /> : <AlertCircle size={14} className="text-rose-500" />}
            <span>{toast.msg}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Create Job Modal (Mobile-First) ───────────────────────────────────── */
const GarageCreateJobModal: React.FC<{
  apiKey: string; onClose: () => void; onCreated: (job: GarageJob) => void;
}> = ({ apiKey, onClose, onCreated }) => {
  const [form, setForm] = useState({ vehicleMake: '', vehicleModel: '', vehicleYear: '', licensePlate: '', customerName: '', customerPhone: '', description: '', laborCost: '', partsCost: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const f = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const total = (parseFloat(form.laborCost) || 0) + (parseFloat(form.partsCost) || 0);
      const r = await fetch('/api/garage/jobs', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'x-store-api-key': apiKey },
        body: JSON.stringify({ ...form, laborCost: parseFloat(form.laborCost) || 0, partsCost: parseFloat(form.partsCost) || 0, total, status: 'OPEN' }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed'); setLoading(false); return; }
      onCreated(d);
    } catch { setError('Cannot reach server'); }
    setLoading(false);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ y: 40, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-[15px] font-black text-slate-800 flex items-center gap-2"><ClipboardList size={15} className="text-orange-500" /> New Job Card</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-400"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Vehicle</p>
          <div className="grid grid-cols-3 gap-2.5">
            {(['vehicleMake', 'vehicleModel', 'vehicleYear'] as const).map(k => (
              <input key={k} value={form[k]} onChange={e => f(k, e.target.value)}
                placeholder={k === 'vehicleMake' ? 'Make' : k === 'vehicleModel' ? 'Model' : 'Year'}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
            ))}
          </div>
          <input value={form.licensePlate} onChange={e => f('licensePlate', e.target.value.toUpperCase())}
            placeholder="License Plate (e.g. WP-CAA-1234)" required
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20 font-mono uppercase" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <input value={form.customerName} onChange={e => f('customerName', e.target.value)} placeholder="Customer Name"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
            <input value={form.customerPhone} onChange={e => f('customerPhone', e.target.value)} placeholder="Phone"
              className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
          </div>
          <textarea value={form.description} onChange={e => f('description', e.target.value)} placeholder="Work description..." rows={3} required
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20 resize-none" />
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Labour Cost</label>
              <input type="number" value={form.laborCost} onChange={e => f('laborCost', e.target.value)} placeholder="0.00" min="0" step="0.01"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-orange-400 font-mono" />
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Parts Cost</label>
              <input type="number" value={form.partsCost} onChange={e => f('partsCost', e.target.value)} placeholder="0.00" min="0" step="0.01"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-[13px] outline-none focus:border-orange-400 font-mono" />
            </div>
          </div>
          {(parseFloat(form.laborCost) || parseFloat(form.partsCost)) > 0 && (
            <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5">
              <span className="text-[11px] font-bold text-orange-600">Total</span>
              <span className="text-[14px] font-black text-orange-700">
                Rs. {((parseFloat(form.laborCost) || 0) + (parseFloat(form.partsCost) || 0)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
          {error && <p className="text-[11px] text-rose-600 font-mono bg-rose-50 px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex gap-3 pt-1 pb-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-600 hover:bg-slate-50 active:scale-95 transition-all">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-[13px] font-black flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95 transition-all">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {loading ? 'Creating...' : 'Create Job'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

/* ── Complete Job Confirm (Mobile-First) ───────────────────────────────── */
const GarageCompleteConfirm: React.FC<{
  job: GarageJob; loading: boolean; onConfirm: () => void; onClose: () => void;
}> = ({ job, loading, onConfirm, onClose }) => {
  const fmt = (n: number) => `Rs. ${Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9995] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={e => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <motion.div initial={{ y: 40, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center"><CheckCircle2 size={15} className="text-emerald-500" /></div>
            <div><p className="text-[13px] font-black text-slate-800">Complete Job</p><p className="text-[10px] text-slate-400 font-mono">{job.jobRef}</p></div>
          </div>
          <button disabled={loading} onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-400 disabled:opacity-40"><X size={15} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 rounded-2xl p-4 space-y-2">
            <div className="flex justify-between text-[12px]"><span className="text-slate-500">Vehicle</span><span className="font-bold text-slate-800">{job.vehicleMake} {job.vehicleModel} — {job.licensePlate}</span></div>
            <div className="flex justify-between text-[12px]"><span className="text-slate-500">Customer</span><span className="font-bold text-slate-800">{job.customerName || '—'}</span></div>
            <div className="flex justify-between text-[12px] border-t border-slate-200 pt-2 mt-1"><span className="text-slate-500">Labour</span><span className="font-bold text-slate-700">{fmt(job.laborCost)}</span></div>
            <div className="flex justify-between text-[12px]"><span className="text-slate-500">Parts</span><span className="font-bold text-slate-700">{fmt(job.partsCost)}</span></div>
            <div className="flex justify-between text-[13px] border-t border-slate-200 pt-2 mt-1"><span className="font-black text-slate-800">Total</span><span className="font-black text-emerald-600">{fmt(job.total)}</span></div>
          </div>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
            <AlertCircle size={13} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700 font-medium">Staged parts will be deducted from inventory. This action cannot be undone.</p>
          </div>
          <div className="flex gap-3 pb-2">
            <button disabled={loading} onClick={onClose} className="flex-1 py-3 rounded-xl border border-slate-200 text-[13px] font-bold text-slate-500 hover:bg-slate-50 active:scale-95 transition-all disabled:opacity-40">Cancel</button>
            <button disabled={loading} onClick={onConfirm}
              className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-black shadow active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-1.5">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {loading ? 'Completing...' : 'Confirm & Complete'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   VIN / VEHICLE LOOKUP VIEW — Mobile-Responsive
═══════════════════════════════════════════════════════════════════════════ */
const VinLookupView: React.FC<{ storeApiKey: string; onBack: () => void }> = ({ storeApiKey, onBack }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const fmt = (n: number) => `Rs. ${Number(n || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const doSearch = async () => {
    if (!query.trim()) return;
    setSearching(true); setSearched(true);
    try {
      const r = await fetch(`/api/vehicles/search?q=${encodeURIComponent(query)}&apiKey=${storeApiKey}`);
      if (r.ok) setResults(await r.json()); else setResults([]);
    } catch { setResults([]); }
    setSearching(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onBack} className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors">
          <ChevronLeft size={18} className="text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-[15px] font-black text-slate-800">VIN / Vehicle Search</h1>
          <p className="text-[10px] text-slate-400">Find compatible parts by vehicle</p>
        </div>
      </div>

      <div className="px-4 py-3 bg-white border-b border-slate-100 shrink-0">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Car size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()}
              placeholder="VIN, plate, make/model/year..."
              className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-[13px] text-slate-800 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20" />
          </div>
          <button onClick={doSearch} disabled={searching}
            className="px-5 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-[12px] flex items-center gap-2 disabled:opacity-50 active:scale-95 transition-all shrink-0">
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            <span className="hidden sm:inline">Search</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">
        {searching && (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-orange-400" /></div>
        )}

        {!searching && searched && results.length === 0 && (
          <div className="text-center py-16">
            <Car size={36} className="mx-auto mb-3 text-slate-300" />
            <p className="text-[13px] text-slate-400 font-bold">No vehicles found</p>
            <p className="text-[11px] text-slate-300 mt-1">for "{query}"</p>
          </div>
        )}

        {!searched && (
          <div className="text-center py-16">
            <Car size={40} className="mx-auto mb-4 text-slate-200" />
            <p className="text-[14px] font-bold text-slate-500 mb-1">Search Vehicle Database</p>
            <p className="text-[11px] text-slate-400 max-w-xs mx-auto">Enter a VIN number, license plate, or make/model to find compatible parts and service history.</p>
          </div>
        )}

        {results.map((v: any, i: number) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center shrink-0">
                <Car size={18} className="text-orange-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-black text-slate-800 truncate">{v.vehicleMake} {v.vehicleModel} {v.vehicleYear}</p>
                <p className="text-[11px] font-mono text-slate-400 truncate">{v.vin || v.licensePlate}</p>
              </div>
            </div>
            {v.compatibleParts?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Compatible Parts ({v.compatibleParts.length})</p>
                {v.compatibleParts.map((p: any, pi: number) => (
                  <div key={pi} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-xl">
                    <span className="text-[12px] font-medium text-slate-700 truncate flex-1 mr-2">{p.name}</span>
                    <span className="text-[12px] font-black text-slate-800 shrink-0">{fmt(p.price)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
